import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  gstAppliesToDocumentFor,
  gstDay,
  isPreGstRegistrationFor,
} from "../../../../shared/utils/gstApplicability";
import { GstPreRegistrationNotice } from "../../../../shared/presentation/components/GstPreRegistrationNotice";
import { LocationPicker } from "../../../../shared/presentation/components/LocationPicker";
import { useActiveLocations } from "../../../../shared/hooks/useActiveLocations";
import { useLocation, useNavigate } from "react-router-dom";

// UI Components
import { Badge } from "../../../../ui/components/Badge";
import { Breadcrumbs } from "../../../../ui/components/Breadcrumbs";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import { Dialog } from "../../../../ui/components/Dialog";
import { Tooltip } from "../../../../ui/components/Tooltip";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";

// Icons
import {
  FeatherAlertOctagon,
  FeatherAlertTriangle,
  FeatherCheck,
  FeatherChevronDown,
  FeatherCopy,
  FeatherDownload,
  FeatherEdit2,
  FeatherFile,
  FeatherInfo,
  FeatherLoader,
  FeatherLock,
  FeatherMessageCircle,
  FeatherPlus,
  FeatherSparkles,
  FeatherUserPen,
  FeatherX,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";

// Feature Components
import SearchPartyModal from "../components/SearchPartyModal";
import VendorRerunConfirmModal from "../components/VendorRerunConfirmModal";
import AIDetectionOverviewBills from "../components/AIDetectionOverviewBills";
import EditLineItemForBillsComponent from "../components/EditLineItemForBillsComponent";
import { getBillListStatus } from "../utils/billListStatus";

// Shared Components
import QueryRaiseModal from "../../../../shared/queries/presentation/components/QueryRaiseModal";
import QueryDetailModal from "../../../../shared/queries/presentation/components/QueryDetailModal";
import QueriesListForDocument from "../../../../shared/queries/presentation/components/QueriesListForDocument";
import DocumentViewer from "../../../../components/DocumentViewer";
import ActivityLogComponent from "../../../../components/ActivityLogComponent";
import {
  BasicInfoEditableField,
  DatesEditableField,
  TaxInfoEditableField,
  DocumentMetadataEditableField,
  AmountSummaryEditableField,
  ReviewPendingInlineValue,
} from "../../../../shared/presentation/components/document-review";
import {
  ErrorBanner,
  useToast,
  GLClassificationModal,
  GLClassificationLineItem,
  ReviewStatusBanner,
  isReviewStatusBannerVisibleForUser,
  ApplyGLAsDefaultModal,
  ApplyGLAsDefaultLineItem,
} from "../../../../shared/presentation/components";
import UpdateGLDialog from "../../../../shared/presentation/components/UpdateGLDialog";
import { useErrorHandling } from "../../../../shared/presentation/hooks";

// Domain Entities
import {
  BillDataEntity,
  LineItemEntity,
} from "../../domain/entities/BillEntity";
import { TaxEntity } from "../../../../shared/domain/entities/TaxEntity";

// Shared utilities
import { getCompany } from "../../../../shared/data/storage/authStorage";
import { getSignedUrlForDocument } from "../../../../services/api";
import { formatDetectedLineItemGstDisplay } from "../../../../shared/utils/lineItemGstDisplay";

// Tax Repository for direct calls when needed
import { taxRepository } from "../../../../shared/data/repositories/TaxRepositoryImpl";

// Clean Architecture: Queries hook
import { useBillQueries } from "../hooks/useBillQueries";

// Shared Hooks
import { useTaxes } from "../../../../shared/presentation/hooks/useTaxes";
import { useGSTDetermination } from "../../../../shared/presentation/hooks/useGSTDetermination";
import { useProposedDecisions } from "../../../../shared/presentation/hooks/useProposedDecisions";
import { useTcsRecommendation } from "../../../../shared/presentation/hooks/useTcsRecommendation";
import { useEntityReviewStatus } from "../../../../shared/presentation/hooks/useEntityReviewStatus";

// Bills Feature Hooks
import { useBillOperations } from "../hooks/useBillOperations";
import { useBillProcessing } from "../hooks/useBillProcessing";
import { useVendorDetails } from "../hooks/useVendorDetails";
import {
  formatReverseChargeAmountWithDocument,
  resolveReverseChargeAmount,
  shouldShowReverseChargeMessage,
} from "../../utils/reverseCharge";
import {
  applyIgst18ToLineItems,
  findIgst18Tax,
  shouldApplyIgst18ForReverseCharge,
} from "../../utils/applyIgst18ForReverseCharge";
import { withVendorGstInReviewRawJson } from "../../../../shared/utils/review/withVendorGstInReviewRawJson";

// Types (Query type no longer needed - using QueryEntity from clean architecture)

// Utils
import {
  getStateAbbreviation,
  getAccountNameLeaf,
  getCountryAbbreviation,
} from "../../../../utils";

// Permissions
import { useDocumentReviewData } from "../../../../shared/presentation/hooks";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";
import { InvoiceBillReviewChangesModal } from "../../../../shared/presentation/components/InvoiceBillReviewChangesModal";
import {
  ClosedMonthBanner,
  ClosedMonthConfirmModal,
} from "../../../../shared/presentation/components";
import {
  ChangeDetails,
  ACTION_TYPE_MONTH_CLOSE,
  ApprovalAuditPayload,
} from "../../../../shared/utils/reviewWorkflow";
import { dateToMonthYear } from "../../../../shared/utils/date";
import {
  applyDocumentDateTdsToLineItems,
  lineItemsTdsChanged,
} from "../../../../shared/utils/tds";
import {
  useIsMonthClosed,
  useIsAdminOrCFO,
} from "../../../../shared/presentation/hooks";
import {
  billsAPI,
  SemanticOverrideRequest,
  createSemanticOverride,
  fetchTDSRates,
  TDSRate,
} from "../../../../services/api";
import {
  pushManualBillDraft,
  APIError,
  rerunLocationChange,
  rerunVendorChange,
  getRecomputeStatus,
} from "../../data/api/billsApi";
import { buildManualBillJson } from "../../../../shared/utils/buildManualDocumentJson";
import { CORE_API_URL } from "../../../../config/api";
import { useAuth } from "../../../../features/auth/presentation/hooks/useAuth";
import {
  BILL_HEADER_TRACK_KEY_SET,
  BILL_LINE_FIELDS,
  BILL_LINE_TRACK_KEY_SET,
  buildBillPendingReviewDisplayMapsFromChangeDetails,
  isBillTrackedFieldChangeRowAllowed,
  shortenTrackedPartyChangeNewValue,
} from "../../../../shared/utils/review";
import {
  getDeletedFromBackendSuccessMessage,
  getPushPermissionDeniedMessage,
  getReviewRequestPushReason,
  getReviewRequestUpdateAfterPostReason,
  getStoredAccountingBackend,
  getUpdateDocumentAfterPushPermissionDeniedMessage,
} from "../../../../config/accounting";
import {
  applyExchangeRateForAccountingPayload,
  formatExchangeRateForBasicInfoField,
  isExchangeRateMissingForTallyPost,
  needsZohoExchangeRateFetch,
} from "../../../../shared/utils/documentExchangeRate";
import { buildValidationSnapshotForReview } from "../../../../shared/utils/review/validationSnapshot";
import { useZohoDocumentExchangeRate } from "../../../../shared/hooks/useZohoDocumentExchangeRate";

const getSupplyLocationCode = (
  state: string | null | undefined,
  country: string | null | undefined,
): string => {
  const stateValue = String(state ?? "").trim();
  return stateValue
    ? getStateAbbreviation(stateValue)
    : getCountryAbbreviation(country);
};

// Rollback flag for the 2026-06-26 line-item column reorder.
// The OLD line-item table is kept for rollback: the OLD HEADER is literally
// commented out, but the OLD ROW MAP can't be literally commented because it
// contains nested JSX comments, so it is gated by this flag instead (code left
// fully intact). false = render the NEW reordered layout. Set true (and
// uncomment the OLD HEADER) to fall back to the original layout.
const SHOW_OLD_LINE_ITEM_LAYOUT = false;

// Ranked AI GL candidate shape (mirrors the modal's GLSuggestion; kept local so
// BillsReviewPage doesn't depend on the modal's internal type).
type InlineGlSuggestion = {
  rank?: number;
  gl_id?: string;
  gl_name: string;
  account_id: string;
  source_layer?: string;
  confidence?: number | null;
  reason?: string;
};

// Inline GL picker for the Debit A/c cell (2026-06-26). For LLM-classified lines
// the AI's auto-pick is hidden and the reviewer sees an empty "Select account"
// box; clicking it opens the ranked "AI Suggested" list (same look as the edit
// modal). Rendered in a portal (SubframeCore.Popover) so it escapes the cell's
// overflow-hidden. Picking does NOT auto-close (Popover, unlike DropdownMenu),
// so onSelect closes it explicitly via the parent's open state.
function InlineGlAccountPicker({
  open,
  onOpenChange,
  suggestions,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestions: InlineGlSuggestion[];
  onSelect: (s: InlineGlSuggestion) => void;
}) {
  return (
    <SubframeCore.Popover.Root open={open} onOpenChange={onOpenChange}>
      <SubframeCore.Popover.Trigger asChild={true}>
        {/* Empty AI picker box with an animated blue "comet" ring that orbits the
            border (.gl-ai-ring in index.css) to signal an AI suggestion awaiting
            the reviewer's pick. text-warning-700 = blue in this theme (the brand
            ramp is grayscale; the warning ramp is sky-blue). */}
        <button
          type="button"
          className="gl-ai-ring flex w-full items-center gap-1 px-2 py-1 text-left"
        >
          <span className="truncate text-caption-bold font-caption-bold text-warning-700">
            Select account
          </span>
          <FeatherChevronDown className="ml-auto flex-none text-caption font-caption text-warning-700" />
        </button>
      </SubframeCore.Popover.Trigger>
      <SubframeCore.Popover.Portal>
        <SubframeCore.Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className="z-50"
        >
          <div className="flex w-80 flex-col overflow-hidden rounded-md border border-solid border-neutral-200 bg-white shadow-lg">
            {/* Branded "AI Suggested" header strip (blue = this theme's AI accent,
                mirrors the AI SUGGESTED TDS card). */}
            <div className="flex items-center gap-1.5 bg-warning-50 px-3 py-2">
              <FeatherSparkles className="text-caption font-caption text-warning-700" />
              <span className="text-caption-bold font-caption-bold uppercase tracking-wide text-warning-700">
                AI Suggested GL
              </span>
            </div>
            <div className="flex flex-col gap-1 px-2 py-2">
              {suggestions.map((suggestion, idx) => {
                const pct =
                  suggestion.confidence != null
                    ? Math.round(suggestion.confidence * 100)
                    : null;
                return (
                  <button
                    type="button"
                    key={`${suggestion.account_id}-${idx}`}
                    onClick={() => onSelect(suggestion)}
                    className="flex w-full flex-col gap-0.5 rounded-md border border-solid border-transparent px-2.5 py-1.5 text-left transition-colors hover:bg-neutral-100"
                  >
                    <div className="flex w-full items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-body-bold font-body-bold text-default-font">
                        {getAccountNameLeaf(suggestion.gl_name)}
                      </span>
                      {pct != null && (
                        <span
                          className={`flex-none rounded-full px-1.5 py-0.5 text-caption font-caption ${
                            idx === 0
                              ? "bg-warning-100 text-warning-700"
                              : "bg-neutral-100 text-subtext-color"
                          }`}
                        >
                          {idx === 0 ? `${pct}% Match` : `${pct}%`}
                        </span>
                      )}
                    </div>
                    {suggestion.reason && (
                      <span className="text-caption font-caption italic text-subtext-color leading-snug">
                        {suggestion.reason}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </SubframeCore.Popover.Content>
      </SubframeCore.Popover.Portal>
    </SubframeCore.Popover.Root>
  );
}

// ── Inline ITC eligibility picker (#612) ────────────────────────────────────
// Mirrors InlineGlAccountPicker: a badge trigger opening a small "AI Suggested"
// popover, but with the 3 fixed ITC tokens (Zoho line_items[].itc_eligibility).
// The recommended token is flagged "AI pick"; a needs-review line shows the
// amber comet ring + "Review". Writing a token onto the line item is what feeds
// the Zoho write-back and the classification capture.
const ITC_STATUS_META: Record<string, { label: string; cls: string }> = {
  eligible: { label: "Eligible", cls: "bg-success-100 text-success-800" },
  ineligible_section17: {
    label: "Blocked · 17(5)",
    cls: "bg-error-100 text-error-800",
  },
  ineligible_others: {
    label: "Ineligible · Other",
    cls: "bg-neutral-200 text-subtext-color",
  },
};
const ITC_OPTION_ORDER = [
  "eligible",
  "ineligible_section17",
  "ineligible_others",
];

// Friendly provenance labels for the ITC recommendation. rule = deterministic
// Sec 17(5) checklist, llm = the model, knn = learned from confirmed history.
// Shared by the in-dropdown "AI pick" pill and the source hint under the picker
// so they never drift. #612
const ITC_SOURCE_LABELS: Record<string, string> = {
  rule: "Rule (17(5))",
  llm: "LLM",
  knn: "kNN",
  reconcile: "LLM + history",
  human: "Manual",
};

function ItcStatusBadge({
  status,
  needsReview,
}: {
  status: string;
  needsReview?: boolean;
}) {
  const meta = ITC_STATUS_META[status] ?? ITC_STATUS_META.eligible;
  return (
    <span
      className={`truncate rounded-full px-1.5 py-0.5 text-caption font-caption-bold ${
        needsReview ? "bg-warning-100 text-warning-800" : meta.cls
      }`}
    >
      {needsReview ? "Review" : meta.label}
    </span>
  );
}

function InlineItcPicker({
  open,
  onOpenChange,
  current,
  recommended,
  source,
  needsReview,
  needsPick,
  reason,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  current: string;
  recommended: string;
  source?: string | null;
  needsReview: boolean;
  // LLM-sourced line the reviewer hasn't confirmed yet: show a blank "Select"
  // box instead of a pre-filled badge (mirrors the Debit A/c GL picker). kNN /
  // rule / reconcile lines are pre-selected, so this stays false for them.
  needsPick: boolean;
  reason?: string;
  onSelect: (status: string) => void;
}) {
  // The recommended option is tagged with its provenance (LLM / kNN / Rule …)
  // rather than "AI pick" or a "% Match", so every line reads the same way.
  // Falls back to nothing when there is no classification (no misleading pill). #612
  const sourceLabel = source ? (ITC_SOURCE_LABELS[source] ?? source) : null;
  return (
    <SubframeCore.Popover.Root open={open} onOpenChange={onOpenChange}>
      <SubframeCore.Popover.Trigger asChild={true}>
        {needsPick ? (
          // Empty AI-suggestion box with the blue comet ring — same affordance
          // as InlineGlAccountPicker's "Select account". The AI's pick + source
          // live inside the popover; nothing is committed until the reviewer clicks.
          <button
            type="button"
            className="gl-ai-ring flex w-full items-center gap-1 px-2 py-1 text-left"
          >
            <span className="truncate text-caption-bold font-caption-bold text-warning-700">
              Select
            </span>
            <FeatherChevronDown className="ml-auto flex-none text-caption font-caption text-warning-700" />
          </button>
        ) : (
          <button
            type="button"
            className={`flex w-full items-center gap-1 px-1 py-0.5 text-left ${
              needsReview ? "gl-ai-ring" : ""
            }`}
          >
            <ItcStatusBadge status={current} needsReview={needsReview} />
            <FeatherChevronDown className="ml-auto flex-none text-caption font-caption text-subtext-color" />
          </button>
        )}
      </SubframeCore.Popover.Trigger>
      <SubframeCore.Popover.Portal>
        <SubframeCore.Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className="z-50"
        >
          <div className="flex w-64 flex-col overflow-hidden rounded-md border border-solid border-neutral-200 bg-white shadow-lg">
            {/* No header strip (dropped per review feedback) — the source pill on
                the recommended option already says where the pick came from. */}
            <div className="flex flex-col gap-1 px-2 py-2">
              {ITC_OPTION_ORDER.map((status) => {
                const m = ITC_STATUS_META[status];
                const isRec = status === recommended;
                // Nothing is committed while the line still needs a pick, so no
                // option is the "current" one — show the AI-pick pill, not a
                // selected-looking highlight. #612
                const isCurrent = !needsPick && status === current;
                return (
                  <button
                    type="button"
                    key={status}
                    onClick={() => onSelect(status)}
                    className={`flex w-full flex-col gap-0.5 rounded-md border border-solid px-2.5 py-1.5 text-left transition-colors hover:bg-neutral-100 ${
                      isCurrent
                        ? "border-neutral-200 bg-neutral-50"
                        : "border-transparent"
                    }`}
                  >
                    <div className="flex w-full items-center gap-2">
                      {/* Bold status text left, a source pill (LLM / kNN / Rule)
                          right on the recommended option (others carry no pill). */}
                      <span className="min-w-0 flex-1 truncate text-body-bold font-body-bold text-default-font">
                        {m.label}
                      </span>
                      {isRec && sourceLabel && (
                        <span className="flex-none rounded-full px-1.5 py-0.5 text-caption font-caption bg-warning-100 text-warning-700">
                          {sourceLabel}
                        </span>
                      )}
                    </div>
                    {isRec && reason && (
                      <span className="text-caption font-caption italic text-subtext-color leading-snug">
                        {reason}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </SubframeCore.Popover.Content>
      </SubframeCore.Popover.Portal>
    </SubframeCore.Popover.Root>
  );
}

interface BillsReviewPageProps {}

const BillsReviewPage: React.FC<BillsReviewPageProps> = () => {
  const { user } = useAuth();
  const reviewBannerCurrentUserId = user?.id ? parseInt(user.id, 10) : null;
  const navigate = useNavigate();
  const location = useLocation();
  const isPartyMatched = location.state?.isPartyMatched;
  // const isEditMode = location.state?.isEdit || false;
  const [isSearchPartyModalOpen, setIsSearchPartyModalOpen] = useState(false);
  const [isQueryRaiseModalOpen, setIsQueryRaiseModalOpen] = useState(false);
  const [partyMatched, setPartyMatched] = useState(isPartyMatched);

  // Extract data from navigation state
  // If location.state is lost (e.g., after navigate(-1)), try to restore from sessionStorage or query params
  const getDocumentIdFromStateOrStorage = (): string | null => {
    if (location.state?.documentId) {
      // Store in sessionStorage for recovery
      sessionStorage.setItem("billReviewDocumentId", location.state.documentId);
      return location.state.documentId;
    }
    // Try to get from query parameters (for new tab opens)
    const searchParams = new URLSearchParams(location.search);
    const queryDocumentId = searchParams.get("documentId");
    if (queryDocumentId) {
      sessionStorage.setItem("billReviewDocumentId", queryDocumentId);
      return queryDocumentId;
    }
    // Try to recover from sessionStorage
    const stored = sessionStorage.getItem("billReviewDocumentId");
    return stored || null;
  };

  const documentId = getDocumentIdFromStateOrStorage();
  const pendingReviewIdFromUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("reviewId");
  }, [location.search]);
  const jobId =
    location.state?.jobId ||
    (() => {
      const stored = sessionStorage.getItem("billReviewJobId");
      return stored || null;
    })();
  // const initialBillJson: BillDataEntity | null =
  //   location.state?.billJson || null;
  const initialBillJson: BillDataEntity | null = null;
  const initialBillDocument = location.state?.billDocument || null;
  const initialSignedUrl: string | null = location.state?.signedUrl || null;
  const initialFileName = location.state?.fileName || null;

  // Store jobId in sessionStorage if available
  useEffect(() => {
    if (location.state?.jobId) {
      sessionStorage.setItem("billReviewJobId", location.state.jobId);
    }
  }, [location.state?.jobId]);

  // Fetch proposed decisions using custom hook
  const { proposedDecisions } = useProposedDecisions(jobId);

  // Fetch TCS recommendation once when user lands on review screen (by job_id)
  const { tcsRecommendation } = useTcsRecommendation(jobId);

  // Track if user has changed TCS from dropdown so we hide the AI recommendation info icon
  const [userHasChangedTcs, setUserHasChangedTcs] = useState(false);

  // ============================================
  // NEW: Use unified hook to fetch all data (permissions + document + review statuses)
  // ============================================
  // Memoize functions to prevent infinite loops
  const fetchBillDocument = useCallback(async (id: string) => {
    return await billsAPI.getBill(id);
  }, []);

  const checkBillPostedToZoho = useCallback((doc: any) => {
    return getBillListStatus(doc) === "posted";
  }, []);

  const reviewData = useDocumentReviewData({
    moduleCode: MODULE_CODES.BILL,
    entityType: "bill",
    documentId,
    fetchDocument: fetchBillDocument,
    isPostedToZoho: checkBillPostedToZoho,
    initialDocument: initialBillDocument,
  });

  // Extract values from reviewData (includes canCreate, needsReviewForCreate for manual-draft create flow)
  const {
    isDataLoading,
    document: billDocument,
    isPostedToZoho,
    canEdit,
    canUpdateAfterPush,
    canPushToZoho,
    canCreate,
    canReject,
    canMarkAsDuplicate,
    canDelete,
    needsReviewForEdit,
    needsReviewForUpdateAfterPush,
    needsReviewForPushToZoho,
    needsReviewForCreate,
    needsReviewForOverrideGLPermanent,
    needsReviewForReject,
    needsReviewForDuplicate,
    needsReviewForDelete,
    hasApprovedUpdateAfterPushReview,
    hasApprovedReview,

    canSaveAndGoBack,
    canApprove,
    error: reviewDataError,
  } = reviewData;

  // Get bill document details (for signedUrl, fileName, etc.)
  const billDoc = billDocument as any;
  const signedUrl = billDoc?.signed_url || initialSignedUrl;
  const fileName = billDoc?.original_filename || initialFileName;
  const fetchedBillJson = billDoc?.bill_json ?? null;
  const isManualSource = billDoc?.source === "manual";
  const isTallySource = billDoc?.source === "tally";
  const attachmentUrls =
    (billDoc?.attachment_urls as Array<{
      signed_url: string;
      filename: string;
      file_size?: number | null;
    }>) || [];

  // Local state for billJson (can be modified by user)
  const [billJson, setBillJson] = useState<BillDataEntity | null>(
    initialBillJson,
  );

  // Ref to track latest billJson for review requests (to avoid stale state)
  const billJsonRef = useRef<BillDataEntity | null>(initialBillJson);
  useEffect(() => {
    billJsonRef.current = billJson;
  }, [billJson]);

  // ============================================
  // CHANGE TRACKING: Track all changes made by user
  // ============================================
  // Store the original document when first loaded (for comparison)
  const [originalBillJson, setOriginalBillJson] =
    useState<BillDataEntity | null>(initialBillJson);

  /**
   * Baseline snapshot for system-driven updates applied during validation.
   * Used to avoid tracking tax/TDS/GL fields that the system auto-populates in `handleValidationComplete`.
   * This should only be updated from validation flows, not from user edits.
   */
  const validationBaselineLineItemsRef = useRef<any[] | null>(null);

  /**
   * Lock the baseline after the first validation completes.
   * Subsequent validations (triggered after edits) must NOT overwrite the baseline,
   * otherwise previously-tracked user edits can disappear from the changes summary.
   */
  const validationBaselineLockedRef = useRef(false);

  /**
   * After the first `handleValidationComplete` run, allow line-item field tracking.
   * Until then, `updateLineItems` still updates JSON/totals but does not mutate `trackedChanges`.
   */
  const lineItemTrackingAfterValidationRef = useRef(false);

  // Track all changes made by the user (accumulated as they edit)
  const [trackedChanges, setTrackedChanges] = useState<
    Array<{
      field: string;
      old: string;
      new: string;
    }>
  >([]);

  // Helper function to format values for change tracking
  // Handles empty strings, null, undefined, and numbers (including 0)
  // For tax_name: empty → "No Tax", otherwise uppercase
  const formatValueForTracking = (value: any, fieldKey?: string): string => {
    const isEmpty =
      value === null ||
      value === undefined ||
      value === "" ||
      value === "empty";
    if (fieldKey === "tax_name") {
      if (isEmpty) return "No Tax";
      return String(value).toUpperCase();
    }
    if (isEmpty) return "empty";
    // Handle objects (arrays, objects) - stringify them
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    // Handle numbers (including 0) - convert to string
    if (typeof value === "number") {
      return String(value);
    }
    return String(value);
  };

  // Helper function to format field names for display
  // Removes underscores, capitalizes first letter of each word
  const formatFieldName = (fieldName: string): string => {
    const formatted = fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    // Replace "Gst No" with "Vendor GST" for bills
    if (
      formatted.toLowerCase() === "gst no" ||
      formatted.toLowerCase() === "gstno"
    ) {
      return "Vendor GST";
    }

    // Replace "Vendor Gstin" with "Vendor GSTIN" for bills
    if (
      formatted.toLowerCase() === "vendor gstin" ||
      formatted.toLowerCase() === "vendor_gstin"
    ) {
      return "Vendor GSTIN";
    }

    return formatted;
  };

  const seedTrackedUserLineItemChangesAfterValidation = useCallback(
    (items: any[]) => {
      if (!originalBillJson?.line_items || !Array.isArray(items)) return;
      const userEditableLineKeys = new Set([
        "description",
        "hsn_or_sac",
        "quantity",
        "rate",
        "account_name",
      ]);

      setTrackedChanges((prevTrackedChanges) => {
        const updatedChanges = [...prevTrackedChanges];
        const changesToTrack: Array<{
          field: string;
          old: string;
          new: string;
        }> = [];

        items.forEach((item, index) => {
          const originalItem = originalBillJson.line_items?.[index];
          if (!originalItem) return;

          userEditableLineKeys.forEach((lineFieldKey) => {
            const formattedFieldName = formatFieldName(lineFieldKey);
            const field = `Line Item ${index + 1}: ${formattedFieldName}`;
            const oldFieldValue = (originalItem as any)[lineFieldKey];
            const newFieldValue = (item as any)[lineFieldKey];

            if (oldFieldValue == null && newFieldValue == null) return;

            const existingIndex = updatedChanges.findIndex(
              (change) => change.field === field,
            );

            if (
              JSON.stringify(oldFieldValue) === JSON.stringify(newFieldValue)
            ) {
              if (existingIndex >= 0) updatedChanges.splice(existingIndex, 1);
              return;
            }

            const change = {
              field,
              old: formatValueForTracking(oldFieldValue, lineFieldKey),
              new: formatValueForTracking(newFieldValue, lineFieldKey),
            };

            if (existingIndex >= 0) {
              updatedChanges[existingIndex] = change;
            } else {
              changesToTrack.push(change);
            }
          });
        });

        return [...updatedChanges, ...changesToTrack].filter((change) =>
          isBillTrackedFieldChangeRowAllowed(change.field),
        );
      });
    },
    [originalBillJson],
  );

  // Initialize billJson from fetched data (when API fetch happens)
  useEffect(() => {
    // if (fetchedBillJson && !initialBillJson) {
    //   setBillJson(fetchedBillJson);
    // }
    if (fetchedBillJson) {
      setBillJson((prevBillJson) => {
        // If this is the same bill (same bill_number), check if data actually changed
        if (
          prevBillJson &&
          prevBillJson.bill_number === fetchedBillJson.bill_number
        ) {
          // Compare key vendor-related fields to detect actual changes
          // This prevents infinite loops when navigating back from edit pages
          const vendorFieldsChanged =
            prevBillJson.vendor_id !== fetchedBillJson.vendor_id ||
            prevBillJson.vendor_name !== fetchedBillJson.vendor_name ||
            prevBillJson.vendor_gstin !== fetchedBillJson.vendor_gstin ||
            prevBillJson.vendor_address !== fetchedBillJson.vendor_address ||
            prevBillJson.gst_treatment !== fetchedBillJson.gst_treatment ||
            prevBillJson.gst_category !== fetchedBillJson.gst_category ||
            prevBillJson.payment_terms !== fetchedBillJson.payment_terms ||
            prevBillJson.source_of_supply !== fetchedBillJson.source_of_supply;

          // If no vendor fields changed, preserve existing billJson to prevent loops
          // Only update if vendor-related data actually changed
          if (!vendorFieldsChanged) {
            // Still preserve line_items if fetched data doesn't have them
            if (
              prevBillJson.line_items &&
              prevBillJson.line_items.length > 0 &&
              (!fetchedBillJson.line_items ||
                fetchedBillJson.line_items.length === 0)
            ) {
              // No changes needed, return existing state
              return prevBillJson;
            }
            // If line items exist in fetched data, check if they're different
            const lineItemsChanged =
              JSON.stringify(prevBillJson.line_items) !==
              JSON.stringify(fetchedBillJson.line_items);
            if (!lineItemsChanged) {
              // No changes at all, return existing state to prevent loop
              return prevBillJson;
            }
          }

          // Vendor fields changed: user selected/changed vendor (prev has new vendor, fetched is stale).
          // Preserve prevBillJson so we don't overwrite with fetchedBillJson after handleValidationComplete.
          if (vendorFieldsChanged) {
            return prevBillJson;
          }
        }
        // Otherwise, use fetched data as-is
        return fetchedBillJson;
      });

      // Store original document for change tracking (only if not already set or document changed)
      if (
        !originalBillJson ||
        originalBillJson.bill_number !== fetchedBillJson.bill_number
      ) {
        setOriginalBillJson(fetchedBillJson);
        // Reset tracked changes when new document is loaded
        setTrackedChanges([]);
        validationBaselineLineItemsRef.current = null;
        validationBaselineLockedRef.current = false;

        lineItemTrackingAfterValidationRef.current = false;
      }
    }
  }, [fetchedBillJson, originalBillJson]);

  // Clear tax_id from line items when gst_treatment is overseas
  // useEffect(() => {
  //   if (
  //     billJson?.gst_treatment?.toLowerCase() === "overseas" &&
  //     billJson?.line_items
  //   ) {
  //     const hasTaxInLineItems = billJson.line_items.some(
  //       (item) => item.tax_id && item.tax_id !== ""
  //     );

  //     if (hasTaxInLineItems) {
  //       const updatedLineItems = billJson.line_items.map((item) => ({
  //         ...item,
  //         tax_id: "",
  //         tax_name: "",
  //         tax_percentage: 0,
  //         tax_amount: 0,
  //         item_total_inclusive_of_tax:
  //           item.item_total || item.rate * item.quantity,
  //       }));

  //       // Update both billJson and lineItems state to keep them in sync
  //       setBillJson((prev) => {
  //         if (!prev) return prev;
  //         return {
  //           ...prev,
  //           line_items: updatedLineItems,
  //         };
  //       });
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [billJson?.gst_treatment]);

  // Check if vendor is already matched based on vendor_id in billJson
  useEffect(() => {
    if (billJson?.vendor_id && billJson.vendor_id !== "") {
      setPartyMatched(true);
    }
  }, [billJson?.vendor_id, billJson?.vendor_name]);

  const { vendor: fetchedVendor, fetchVendor } = useVendorDetails();
  const vendorHydratedRef = useRef<string | null>(null);

  useEffect(() => {
    const vendorId = billJson?.vendor_id?.trim();
    if (!vendorId || isDataLoading) {
      return;
    }
    if (vendorHydratedRef.current === vendorId) {
      return;
    }
    vendorHydratedRef.current = vendorId;
    fetchVendor(vendorId).catch(() => {
      vendorHydratedRef.current = null;
    });
  }, [billJson?.vendor_id, isDataLoading, fetchVendor]);

  // Auto-save state
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedDataRef = useRef<string | null>(null);
  const retryCountRef = useRef<number>(0);
  const maxRetries = 3;

  // Activity log refresh key - increment this to trigger activity log refresh
  const [activityLogRefreshKey, setActivityLogRefreshKey] = useState(0);

  // Error handling with new hook
  const {
    errors,
    setFieldError,
    setFieldErrors,
    setApiErrors,
    clearErrors,
    clearFieldError,
  } = useErrorHandling();

  // Toast notifications
  const { showSuccess, showError, showWarning } = useToast();

  // Closed month confirm modal (when non-Admin/CFO tries to save or approve in closed month)
  const [showClosedMonthConfirmModal, setShowClosedMonthConfirmModal] =
    useState(false);
  const [pendingClosedMonthSource, setPendingClosedMonthSource] = useState<
    "save" | "approve" | null
  >(null);

  // Review request modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingReviewAction, setPendingReviewAction] = useState<{
    action: () => void;
    changeDetails?: ChangeDetails;
    disableSubmit?: boolean;
    actionCode?: string;
    requiresReview?: boolean;
  } | null>(null);

  // Track pending GL overrides that need review (array to support multiple line items)
  const [pendingGLOverrideReview, setPendingGLOverrideReview] = useState<
    Array<{
      lineItemIndex: number;
      glData: {
        old_account_id: string;
        old_account_name: string;
        new_account_id: string;
        new_account_name: string;
      };
      changeScope: "always" | "once";
      reason: string;
      overrideData: SemanticOverrideRequest;
    }>
  >([]);

  // Review status (for banner display)
  const { review } = useEntityReviewStatus(
    documentId ? "bill" : null,
    documentId ? parseInt(documentId) : null,
  );
  const pendingReviewDisplayChangeDetails =
    pendingReviewIdFromUrl &&
    review?.status === "pending" &&
    String(review.id) === pendingReviewIdFromUrl
      ? review.change_details
      : null;

  // Check if review is pending (blocks all actions)
  const isReviewPending = review?.status === "pending";
  const isReviewRejected = review?.status === "rejected";

  useEffect(() => {
    const reviewRawJson = pendingReviewDisplayChangeDetails?.raw_json;
    if (!reviewRawJson || typeof reviewRawJson !== "object") return;

    setBillJson(reviewRawJson);
    if (Array.isArray(reviewRawJson.line_items)) {
      setLineItems([...reviewRawJson.line_items]);
    }
  }, [pendingReviewDisplayChangeDetails]);

  // Check if user has edit permission (edit or update_after_push based on document status)
  const hasEditPermission = isPostedToZoho ? canUpdateAfterPush : canEdit;

  // Closed month: derive month from bill date and check if closed
  const billDate = billJson?.date ?? fetchedBillJson?.date ?? "";
  const monthForClosedCheck = billDate ? dateToMonthYear(billDate) : "";
  const { isClosed: isMonthClosed } = useIsMonthClosed(
    monthForClosedCheck || undefined,
  );
  const isAdminOrCFO = useIsAdminOrCFO();

  // Disable all editable fields if review is pending OR user doesn't have update permissions
  const shouldDisableEditableFields = isReviewPending || !hasEditPermission;

  // State for showing approved banner temporarily (auto-dismisses after 40 seconds)
  const [showApprovedBanner, setShowApprovedBanner] = useState(false);
  const approvedBannerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // AI detection loading: gate Approve/Reject/Duplicate until AIDetectionOverview finishes
  const [aiDetectionLoading, setAiDetectionLoading] = useState(false);

  // Use review status from reviewData hook
  const isCheckingReview = reviewData.isReviewDataLoading;

  // Show approved banner when review is approved (on mount or when review changes to approved)
  useEffect(() => {
    if (review?.status === "approved") {
      setShowApprovedBanner(true);

      // Clear any existing timeout
      if (approvedBannerTimeoutRef.current) {
        clearTimeout(approvedBannerTimeoutRef.current);
      }

      // Auto-dismiss after 40 seconds
      // approvedBannerTimeoutRef.current = setTimeout(() => {
      //   setShowApprovedBanner(false);
      // }, 40000);
    } else {
      setShowApprovedBanner(false);
    }

    // Cleanup timeout on unmount
    // return () => {
    //   if (approvedBannerTimeoutRef.current) {
    //     clearTimeout(approvedBannerTimeoutRef.current);
    //   }
    // };
  }, [review?.status, review?.id]);

  // Permission flags are now computed in the hook
  // canActuallyEdit, canSaveAndGoBack, canApprove, canEditLineItem are already computed

  // Bill operations hooks
  const {
    createZohoBill,
    updateZohoBill,
    updateBillJson: updateBillJsonHook,
    rejectBill,
    markAsDuplicate,
    deletePostedBill,
    error: billOperationsError,
  } = useBillOperations();

  const { processBillJson, error: billProcessingError } = useBillProcessing();

  // Display errors from hooks in ErrorBanner
  useEffect(() => {
    if (billOperationsError) {
      setApiErrors(new Error(billOperationsError));
    }
  }, [billOperationsError, setApiErrors]);

  useEffect(() => {
    if (billProcessingError) {
      setApiErrors(new Error(billProcessingError));
    }
  }, [billProcessingError, setApiErrors]);

  // Debounced save function with retry logic
  const debouncedSave = useCallback(
    async (billData: BillDataEntity, isRetry = false) => {
      if (!documentId) {
        return;
      }

      // CRITICAL: Don't save if data is still loading
      if (isDataLoading) {
        return;
      }

      // Closed month: non-Admin/CFO cannot save directly; show confirm modal to submit for review
      if (isMonthClosed && !isAdminOrCFO) {
        setPendingClosedMonthSource("save");
        setShowClosedMonthConfirmModal(true);
        return;
      }

      // CRITICAL: Check permissions before saving
      // For documents NOT pushed to Zoho: Check can_update permission
      // For documents pushed to Zoho: Check can_update_after_push permission AND review status
      if (isPostedToZoho) {
        // Document is pushed to Zoho - need update_after_push permission
        if (!canUpdateAfterPush) {
          return;
        }
        // Also check if review is required
        if (needsReviewForUpdateAfterPush || hasApprovedUpdateAfterPushReview) {
          return;
        }
      } else {
        // Document is NOT pushed to Zoho - need edit permission
        if (!canEdit) {
          return;
        }
      }

      // Check if data has actually changed to prevent unnecessary API calls
      const currentDataString = JSON.stringify(billData);
      if (lastSavedDataRef.current === currentDataString && !isRetry) {
        return;
      }

      try {
        setIsAutoSaving(true);
        setSaveError(null);

        await updateBillJsonHook(documentId, billData);
        setLastSaved(new Date());
        lastSavedDataRef.current = currentDataString;
        retryCountRef.current = 0; // Reset retry count on success

        // Refresh activity log after successful save
        setActivityLogRefreshKey((prev) => prev + 1);
      } catch (error) {
        console.error("Failed to save bill JSON:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to save changes";
        setSaveError(errorMessage);

        // Retry logic for network errors
        if (retryCountRef.current < maxRetries && !isRetry) {
          retryCountRef.current++;

          setTimeout(() => {
            debouncedSave(billData, true);
          }, 2000 * retryCountRef.current); // Exponential backoff
        }
      } finally {
        setIsAutoSaving(false);
      }
    },
    [
      documentId,
      maxRetries,
      updateBillJsonHook,
      isDataLoading,
      isMonthClosed,
      isAdminOrCFO,
      isPostedToZoho,
      canUpdateAfterPush,
      canEdit,
      needsReviewForUpdateAfterPush,
      hasApprovedUpdateAfterPushReview,
    ],
  );

  // Function to trigger debounced save
  const triggerAutoSave = useCallback(
    (billData: BillDataEntity) => {
      debouncedSave(billData);
    },
    [debouncedSave],
  );

  // Function to update billJson data
  // ZOUR-65: which of our GST registrations this bill is filed against.
  // Hidden entirely unless the company has more than one.
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
  } = useActiveLocations();

  const updateBillJson = useCallback(
    (updates: Partial<BillDataEntity>) => {
      // CRITICAL: Don't allow updates if data is still loading
      if (isDataLoading) {
        return;
      }

      // Check if user has permission to edit (edit or update_after_push)
      const hasEditPermission = isPostedToZoho ? canUpdateAfterPush : canEdit;
      if (!hasEditPermission) {
        return;
      }

      setBillJson((prev) => {
        if (!prev) return null;
        const updated = { ...prev, ...updates };

        // Track changes by comparing with original document
        // Use functional update to always work with latest trackedChanges state
        if (originalBillJson) {
          setTrackedChanges((prevTrackedChanges) => {
            const changesToTrack: Array<{
              field: string;
              old: string;
              new: string;
            }> = [];
            const updatedChanges = [...prevTrackedChanges];

            // Vendor-related fields that should be removed when vendor_id changes
            // These are fields that get updated automatically when vendor changes
            const vendorAutoUpdateFields = [
              "vendor_id",
              "vendor_name",
              "vendor_gstin",
              "vendor_address",
              "gst_no",
              "gst_category",
              "source_of_supply",
              "place_of_supply",
              "destination_of_supply",
            ];

            // Check if vendor_id is being changed
            const isVendorIdChanging =
              updates.vendor_id !== undefined &&
              originalBillJson.vendor_id !== updates.vendor_id;

            // If vendor_id is changing, remove all vendor-related tracked changes
            if (isVendorIdChanging) {
              // Remove all vendor auto-update fields from tracked changes
              vendorAutoUpdateFields.forEach((field) => {
                const index = updatedChanges.findIndex(
                  (change) => change.field === field,
                );
                if (index >= 0) {
                  updatedChanges.splice(index, 1);
                }
              });

              // Remove existing "Vendor" change if it exists (prevent duplicates)
              const existingVendorIndex = updatedChanges.findIndex(
                (change) => change.field === "Vendor",
              );
              if (existingVendorIndex >= 0) {
                updatedChanges.splice(existingVendorIndex, 1);
              }

              // Track vendor change (for display)
              const oldVendorId = originalBillJson.vendor_id;
              const oldVendorName = originalBillJson.vendor_name || "Unknown";
              // Get new vendor name from updates or from the merged updated object
              const newVendorName =
                (updates.vendor_name as string) ||
                updated.vendor_name ||
                "Unknown";

              // Check if vendor is being created (old is empty/null)
              if (
                !oldVendorId ||
                oldVendorId === "" ||
                oldVendorId === "empty"
              ) {
                changesToTrack.push({
                  field: "Vendor",
                  old: "empty",
                  new: `${newVendorName}`,
                });
              } else {
                changesToTrack.push({
                  field: "Vendor",
                  old: oldVendorName,
                  new: newVendorName,
                });
              }
            }

            // Do not track Customer GSTIN / Vendor GSTIN changes (excluded from field_changes for review)
            const gstinKeysToIgnore = [
              "customer_gstin",
              "vendor_gstin",
              "gst_no",
            ];

            // Process all updates
            Object.keys(updates).forEach((key) => {
              // Skip line_items - handled separately in updateLineItems
              if (key === "line_items") {
                return;
              }

              // Skip GSTIN fields - do not show in Changes Requested
              if (gstinKeysToIgnore.includes(key)) {
                return;
              }

              if (!BILL_HEADER_TRACK_KEY_SET.has(key)) {
                return;
              }

              // Skip vendor_id if we already handled it above
              if (isVendorIdChanging && key === "vendor_id") {
                return;
              }

              // Skip vendor auto-update fields if vendor_id is changing
              // (they get updated automatically when vendor changes)
              if (isVendorIdChanging && vendorAutoUpdateFields.includes(key)) {
                return;
              }

              const oldValue = (originalBillJson as any)[key];
              const newValue = (updates as any)[key];

              // Only track if value actually changed from original
              if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
                // Format field name for display
                const formattedFieldName = formatFieldName(key);

                // Check if this field is already tracked
                const existingChangeIndex = updatedChanges.findIndex(
                  (change) => change.field === formattedFieldName,
                );

                if (existingChangeIndex >= 0) {
                  // Update existing tracked change in place
                  updatedChanges[existingChangeIndex] = {
                    field: formattedFieldName,
                    old: formatValueForTracking(oldValue),
                    new: formatValueForTracking(newValue),
                  };
                } else {
                  // Add new tracked change
                  changesToTrack.push({
                    field: formattedFieldName,
                    old: formatValueForTracking(oldValue),
                    new: formatValueForTracking(newValue),
                  });
                }
              } else {
                // If value matches original, remove it from tracked changes (user reverted)
                const formattedFieldName = formatFieldName(key);
                const existingChangeIndex = updatedChanges.findIndex(
                  (change) => change.field === formattedFieldName,
                );
                if (existingChangeIndex >= 0) {
                  updatedChanges.splice(existingChangeIndex, 1);
                }
              }
            });

            // Exclude any GSTIN changes that might still be in updatedChanges (from previous state)
            const combined = [...updatedChanges, ...changesToTrack];
            const gstinDisplayNames = ["Customer GSTIN", "Vendor GSTIN"];
            const filtered = combined.filter(
              (c) => !gstinDisplayNames.includes(c.field),
            );

            return filtered.filter((c) =>
              isBillTrackedFieldChangeRowAllowed(c.field),
            );
          });
        }

        // CRITICAL: Never trigger auto-save for posted documents
        // Only trigger auto-save if we have permission (for non-posted documents)
        // AND if there's no pending GL override review (stop auto-save when GL override needs review)
        if (!isPostedToZoho && canEdit) {
          // triggerAutoSave(updated);
        }
        return updated;
      });
    },
    [
      // triggerAutoSave,
      isPostedToZoho,
      canEdit,
      canUpdateAfterPush,
      isDataLoading,
      originalBillJson,
      // Note: trackedChanges removed from deps - using functional updates instead
    ],
  );

  // Function to update line items
  const updateLineItems = (updatedLineItems: any[]) => {
    // CRITICAL: Don't allow updates if data is still loading
    if (isDataLoading) {
      return;
    }

    // Check if user has permission to edit (edit or update_after_push)
    const hasEditPermission = isPostedToZoho ? canUpdateAfterPush : canEdit;
    if (!hasEditPermission) {
      return;
    }

    setBillJson((prev) => {
      if (!prev) return null;

      // Recalculate totals using rate * quantity
      const newSubTotal = updatedLineItems.reduce(
        (sum, item) => sum + (item.rate || 0) * (item.quantity || 0),
        0,
      );
      const newTaxTotal = updatedLineItems.reduce((sum, item) => {
        const taxPercentage = item.tax_percentage || 0;
        const taxAmount =
          (item.rate || 0) * (item.quantity || 0) * (taxPercentage / 100);
        return sum + taxAmount;
      }, 0);
      const newTotal = newSubTotal + discount + newTaxTotal;

      const updated = {
        ...prev,
        line_items: updatedLineItems,
        sub_total: newSubTotal,
        tax_total: newTaxTotal,
        total: newTotal,
        discount: discount,
      };

      // Track line items changes using functional update to avoid duplicates
      if (
        originalBillJson &&
        (lineItemTrackingAfterValidationRef.current || isManualSource)
      ) {
        const originalLineItems = originalBillJson.line_items || [];
        const baselineLineItems = validationBaselineLineItemsRef.current;
        const systemManagedLineKeys = new Set<string>([
          "tax_id",
          "tax_percentage",
          "tax_name",
          "tax_amount",
          "account_id",
          "account_name",
          "tds_tax_id",
          "tds_tax_name",
          "tds_tax_percentage",
          "item_total",
          "item_total_inclusive_of_tax",
        ]);

        setTrackedChanges((prevTrackedChanges) => {
          const changesToTrack: Array<{
            field: string;
            old: string;
            new: string;
          }> = [];
          // Remove any existing "Line Items" or line_items[${index}] entries (we don't want raw JSON)
          const updatedChanges = prevTrackedChanges.filter(
            (change) =>
              !change.field.startsWith("line_items[") &&
              change.field !== "Line Items" &&
              !change.field.endsWith("]"),
          );

          // Track changes for each line item
          updatedLineItems.forEach((item, index) => {
            const originalItem = originalLineItems[index];
            const fieldKey = `line_items[${index}]`;

            if (!originalItem) {
              const lineNum = index + 1;
              const lineItemPrefix = `Line Item ${lineNum}`;
              for (let i = updatedChanges.length - 1; i >= 0; i--) {
                if (
                  updatedChanges[i].field === lineItemPrefix ||
                  updatedChanges[i].field.startsWith(lineItemPrefix + ":")
                ) {
                  updatedChanges.splice(i, 1);
                }
              }
              BILL_LINE_FIELDS.forEach(({ key: lineFieldKey }) => {
                const value = (item as any)[lineFieldKey];
                if (value === undefined && lineFieldKey !== "tax_name") return;
                const formattedFieldName = formatFieldName(lineFieldKey);
                const displayValue =
                  lineFieldKey === "tax_name" &&
                  (!value || String(value).trim() === "")
                    ? "No Tax"
                    : formatValueForTracking(value, lineFieldKey);
                changesToTrack.push({
                  field: `${lineItemPrefix}: ${formattedFieldName}`,
                  old: "empty",
                  new: displayValue,
                });
              });
            } else if (JSON.stringify(originalItem) !== JSON.stringify(item)) {
              const changedFields: Array<{
                field: string;
                old: string;
                new: string;
              }> = [];

              BILL_LINE_TRACK_KEY_SET.forEach((lineFieldKey) => {
                const baselineItem =
                  Array.isArray(baselineLineItems) &&
                  baselineLineItems[index] != null
                    ? baselineLineItems[index]
                    : null;
                const compareFrom =
                  baselineItem && systemManagedLineKeys.has(lineFieldKey)
                    ? baselineItem
                    : originalItem;

                const oldHasKey = Object.prototype.hasOwnProperty.call(
                  compareFrom,
                  lineFieldKey,
                );
                const newHasKey = Object.prototype.hasOwnProperty.call(
                  item as any,
                  lineFieldKey,
                );
                if (!oldHasKey && !newHasKey) return;

                const oldFieldValue = (compareFrom as any)[lineFieldKey];
                const newFieldValue = (item as any)[lineFieldKey];
                if (oldFieldValue == null && newFieldValue == null) return;

                if (
                  JSON.stringify(oldFieldValue) !==
                  JSON.stringify(newFieldValue)
                ) {
                  const formattedFieldName = formatFieldName(lineFieldKey);
                  changedFields.push({
                    field: `Line Item ${index + 1}: ${formattedFieldName}`,
                    old: formatValueForTracking(oldFieldValue, lineFieldKey),
                    new: formatValueForTracking(newFieldValue, lineFieldKey),
                  });
                }
              });

              // If there are changed fields, track them
              if (changedFields.length > 0) {
                // Field-level merge:
                // - Preserve other line-item fields previously tracked.
                // - For the SAME field (e.g. Account Name), always overwrite with the latest value.
                // - Remove only fields that are no longer changed (user reverted).
                const changedFieldNames = new Set(
                  changedFields.map((c) => c.field),
                );

                // Prune stale entries for this line item that are no longer changed
                BILL_LINE_TRACK_KEY_SET.forEach((lineFieldKey) => {
                  const formattedFieldName = formatFieldName(lineFieldKey);
                  const label = `Line Item ${index + 1}: ${formattedFieldName}`;
                  if (changedFieldNames.has(label)) return;
                  const existingIndex = updatedChanges.findIndex(
                    (c) => c.field === label,
                  );
                  if (existingIndex >= 0) {
                    updatedChanges.splice(existingIndex, 1);
                  }
                });

                // Upsert changed fields (latest wins)
                changedFields.forEach((change) => {
                  const fieldChangeIndex = updatedChanges.findIndex(
                    (c) => c.field === change.field,
                  );
                  if (fieldChangeIndex >= 0) {
                    updatedChanges[fieldChangeIndex] = change;
                  } else {
                    changesToTrack.push(change);
                  }
                });
              } else {
                // No changes for this line item anymore: remove any previously tracked allowlisted fields.
                BILL_LINE_TRACK_KEY_SET.forEach((lineFieldKey) => {
                  const formattedFieldName = formatFieldName(lineFieldKey);
                  const label = `Line Item ${index + 1}: ${formattedFieldName}`;
                  const existingIndex = updatedChanges.findIndex(
                    (c) => c.field === label,
                  );
                  if (existingIndex >= 0) {
                    updatedChanges.splice(existingIndex, 1);
                  }
                });
              }
            } else {
              // Line item matches original - remove from tracked changes if exists
              // Remove both raw format (line_items[index]) and formatted format (Line Items)
              const existingRawIndex = updatedChanges.findIndex(
                (change) => change.field === fieldKey,
              );
              if (existingRawIndex >= 0) {
                updatedChanges.splice(existingRawIndex, 1);
              }
              // Also remove formatted "Line Items" field if it exists
              const existingFormattedIndex = updatedChanges.findIndex(
                (change) => change.field === "Line Items",
              );
              if (existingFormattedIndex >= 0) {
                updatedChanges.splice(existingFormattedIndex, 1);
              }
            }
          });

          // Track removed line items
          if (originalLineItems.length > updatedLineItems.length) {
            for (
              let i = updatedLineItems.length;
              i < originalLineItems.length;
              i++
            ) {
              const itemName =
                originalLineItems[i].name ||
                originalLineItems[i].description ||
                "Item";
              // Track as readable format only
              changesToTrack.push({
                field: `Line Item ${i + 1}`,
                old: itemName,
                new: "Removed",
              });
              // DO NOT track raw JSON - we don't need it in change_details
            }
          }

          return [...updatedChanges, ...changesToTrack].filter((c) =>
            isBillTrackedFieldChangeRowAllowed(c.field),
          );
        });
      }

      // CRITICAL: Never trigger auto-save for posted documents
      // Only trigger auto-save if not Posted to Zoho (for non-posted documents)
      // if (!isPostedToZoho && canUpdate) {
      //   triggerAutoSave(updated);
      // }
      return updated;
    });
  };

  // Handle navigation back
  const handleGoBack = useCallback(() => {
    // Clear sessionStorage before navigating away
    sessionStorage.removeItem("autoSelectVendor");
    navigate("/dashboard/bills");
  }, [navigate]);

  // Helper function to get field value with fallback
  const getFieldValue = (
    value: any,
    fallback: string = "Not provided",
  ): string => {
    if (value === null || value === undefined || value === "") {
      return fallback;
    }
    return String(value);
  };

  // Helper function to format amount with proper rounding
  const formatAmount = (value: any, fallback: string = "0"): string => {
    if (value === null || value === undefined || value === "") {
      return fallback;
    }
    const numValue = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(numValue)) {
      return fallback;
    }
    return Math.round(numValue).toString();
  };

  const parseTaxRateFromName = (taxName: string): number | null => {
    const match = taxName?.match(/([\d.]+)/);
    return match ? parseFloat(match[1]) : null;
  };

  const formatRate = (rate: number): string => {
    const fixed = Number(rate.toFixed(2));
    return Number.isInteger(fixed) ? fixed.toString() : fixed.toString();
  };

  const calculateTaxBreakdown = (
    items: any[],
    billData?: BillDataEntity | null,
  ): {
    rows: { label: string; amount: number }[];
    totalTax: number;
  } => {
    const taxGroups: Record<
      string,
      { type: "IGST" | "GST"; rate: number; amount: number }
    > = {};

    // Determine if it's inter-state or intra-state based on place_of_supply and destination_of_supply
    // Inter-state: place_of_supply !== destination_of_supply → IGST
    // Intra-state: place_of_supply === destination_of_supply → CGST+SGST
    const normalizeState = (state: string | null | undefined): string => {
      if (!state) return "";
      // Convert to uppercase and get abbreviation if needed
      const normalized = state.toUpperCase().trim();
      // If it's already a 2-letter code, return it
      if (normalized.length === 2) return normalized;
      // Otherwise, try to get abbreviation (this handles full state names)
      try {
        return getStateAbbreviation(state) || normalized;
      } catch {
        return normalized;
      }
    };

    const placeOfSupply = normalizeState(billData?.place_of_supply);
    const destinationOfSupply = normalizeState(billData?.destination_of_supply);
    const hasSupplyInfo = placeOfSupply && destinationOfSupply;
    const isInterState = hasSupplyInfo && placeOfSupply !== destinationOfSupply;

    items.forEach((item) => {
      const taxName = (item.tax_name || "").toString();
      const taxPercentageRaw = item.tax_percentage;
      const taxRateFromPercentage =
        taxPercentageRaw !== undefined &&
        taxPercentageRaw !== null &&
        !isNaN(Number(taxPercentageRaw))
          ? parseFloat(taxPercentageRaw)
          : null;
      const parsedRateFromName = parseTaxRateFromName(taxName);
      const taxRate =
        taxRateFromPercentage !== null
          ? taxRateFromPercentage
          : parsedRateFromName !== null && !isNaN(parsedRateFromName)
            ? parsedRateFromName
            : 0;

      if (!taxRate) return;

      // Determine tax type:
      // 1. First check tax_name as it reflects what tax was actually applied
      // 2. If tax_name doesn't indicate IGST, then check place_of_supply and destination_of_supply
      // 3. Fallback to GST if neither provides clear indication
      let taxType: "IGST" | "GST";
      const taxNameUpper = taxName.toUpperCase();

      if (taxNameUpper.includes("IGST")) {
        // Tax name explicitly indicates IGST, use it
        taxType = "IGST";
      } else if (taxNameUpper.includes("GST")) {
        taxType = "GST";
      } else if (hasSupplyInfo) {
        // We have supply information, use it to determine tax type
        taxType = isInterState ? "IGST" : "GST";
      } else {
        // Fallback: if no clear indication, default to GST
        taxType = "GST";
      }

      const taxableAmount = (item.rate || 0) * (item.quantity || 0);
      const taxAmount = (taxableAmount * taxRate) / 100;

      const key = `${taxType}-${taxRate}`;
      if (!taxGroups[key]) {
        taxGroups[key] = { type: taxType, rate: taxRate, amount: 0 };
      }
      taxGroups[key].amount += taxAmount;
    });

    const rows: { label: string; amount: number }[] = [];

    Object.values(taxGroups).forEach((group) => {
      if (group.type === "IGST") {
        rows.push({
          label: `IGST ${formatRate(group.rate)}%`,
          amount: group.amount,
        });
      } else {
        const halfRate = group.rate / 2;
        const halfAmount = group.amount / 2;
        rows.push(
          {
            label: `SGST ${formatRate(halfRate)}%`,
            amount: halfAmount,
          },
          {
            label: `CGST ${formatRate(halfRate)}%`,
            amount: halfAmount,
          },
        );
      }
    });

    const totalTax = Object.values(taxGroups).reduce(
      (sum, group) => sum + group.amount,
      0,
    );

    return { rows, totalTax };
  };

  // Helper function to format date as "March 15, 2024"
  const formatDate = (
    dateString: string | null | undefined,
    fallback: string = "Not provided",
  ): string => {
    if (!dateString || dateString === "Not provided") {
      return fallback;
    }

    try {
      // ZOUR-19 (review 15): normalise first. A pipeline document's JSON date
      // is the extractor's DD/MM/YYYY, and `new Date("15/05/2026")` is Invalid
      // Date — so the Dates card read "Not provided" on a document the system
      // had already judged pre- or post-registration, which is the worst
      // possible thing for it to say next to the GST notice.
      const date = new Date(gstDay(dateString) ?? dateString);
      if (isNaN(date.getTime())) {
        return fallback;
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return fallback;
    }
  };

  // Clean Architecture: Use queries hook
  const {
    queries: queriesEntities,
    loading: queriesLoading,
    loadingMore: queriesLoadingMore,
    refetch: refetchQueries,
    hasMore: queriesHasMore,
    loadMore: loadMoreQueries,
  } = useBillQueries(documentId);

  const [isQueryDetailModalOpen, setIsQueryDetailModalOpen] = useState(false);
  const [selectedQueryId, setSelectedQueryId] = useState<number | undefined>();

  // Check if there are any unresolved queries
  const hasUnresolvedQueries = queriesEntities.some(
    (q) => q.status !== "resolved",
  );
  const unresolvedCount = queriesEntities.filter(
    (q) => q.status !== "resolved",
  ).length;

  // Check if document is rejected or duplicate (read-only mode)
  const isDocumentReadOnly =
    billDocument?.status === "3" || billDocument?.status === "4";

  // Reset AI detection loading when overview is not rendered (e.g. party not matched)
  const isAiOverviewRendered =
    !isDocumentReadOnly &&
    !isReviewPending &&
    !!billJson &&
    !!billDocument &&
    partyMatched;
  useEffect(() => {
    if (!isAiOverviewRendered) {
      setAiDetectionLoading(false);
    }
  }, [isAiOverviewRendered]);

  const documentStatusLabel =
    billDocument?.status === "3"
      ? "Duplicate"
      : billDocument?.status === "4"
        ? "Rejected"
        : null;

  // Function to save and navigate back (defined after state to avoid TypeScript errors)
  const saveAndGoBack = useCallback(async () => {
    if (!billJson || !documentId) {
      console.error("No billJson data or documentId to save");
      return;
    }

    // Check if GL override needs review (for non-posted docs)
    if (
      !isPostedToZoho &&
      pendingGLOverrideReview.length > 0 &&
      needsReviewForOverrideGLPermanent
    ) {
      // Collect all GL overrides that need review
      const glOverridesNeedingReview = pendingGLOverrideReview.filter(
        (gl) => gl.changeScope === "always",
      );

      if (glOverridesNeedingReview.length > 0) {
        // Build field_changes for all GL overrides
        const glFieldChanges = glOverridesNeedingReview.map((gl) => ({
          field: `Line Item ${gl.lineItemIndex + 1}: Account Name`,
          old: getAccountNameLeaf(gl.glData.old_account_name),
          new: getAccountNameLeaf(gl.glData.new_account_name),
        }));

        // Store all GL override data in array format
        const lineItemGLDataArray = glOverridesNeedingReview.map((gl) => ({
          line_item_index: gl.lineItemIndex,
          old_account_id: gl.glData.old_account_id,
          old_account_name: gl.glData.old_account_name,
          new_account_id: gl.glData.new_account_id,
          new_account_name: gl.glData.new_account_name,
          change_scope: gl.changeScope,
          reason: gl.reason,
          override_data: gl.overrideData,
        }));

        // Show review modal for GL override
        const glChanges: ChangeDetails = {
          reason: glOverridesNeedingReview[0].reason, // Use first reason as primary
          old_value: glOverridesNeedingReview[0].glData.old_account_name,
          new_value: glOverridesNeedingReview[0].glData.new_account_name,
          field_changes: glFieldChanges,
          line_item_index: glOverridesNeedingReview[0].lineItemIndex, // Keep for backward compatibility
          line_item_gl_data: lineItemGLDataArray as any, // Store as array
          change_scope: glOverridesNeedingReview[0].changeScope,
          raw_json: withVendorGstInReviewRawJson(
            billJsonRef.current || billJson,
            fetchedVendor,
          ),
          original_json: originalBillJson ?? undefined,
          validation_snapshot: buildValidationSnapshotForReview(
            validationBaselineLineItemsRef.current,
            billJsonRef.current || billJson,
          ),
          action_types: ["override_gl_permanent"],
          // Include the override data for semantic override API call (use first one for backward compatibility)
          override_data: glOverridesNeedingReview[0].overrideData,
        };

        setPendingReviewAction({
          action: async () => {
            // After review is submitted, navigate back
            handleGoBack();
          },
          changeDetails: glChanges,
          disableSubmit: false,
        });
        setIsReviewModalOpen(true);
        return;
      }
    }

    // Check permission before allowing save
    // For non-posted documents, just check edit permission
    // For posted documents, check update_after_push permission
    const hasEditPermission = isPostedToZoho ? canUpdateAfterPush : canEdit;
    if (!hasEditPermission) {
      return;
    }

    try {
      // Clear any pending debounced save
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Save immediately
      await debouncedSave(billJson);

      // Clear sessionStorage before navigating away
      sessionStorage.removeItem("autoSelectVendor");

      // Navigate back to bills section
      handleGoBack();
    } catch (error) {
      console.error("Failed to save before navigation:", error);
      // Still navigate back even if save fails
      handleGoBack();
    }
  }, [
    billJson,
    documentId,
    debouncedSave,
    handleGoBack,
    isPostedToZoho,
    canEdit,
    canUpdateAfterPush,
    pendingGLOverrideReview,
    needsReviewForOverrideGLPermanent,
  ]);

  // Line item editing state
  const [isEditLineItemModalOpen, setIsEditLineItemModalOpen] = useState(false);
  const [editingLineItemIndex, setEditingLineItemIndex] = useState<
    number | null
  >(null);
  const [editingLineItemData, setEditingLineItemData] =
    useState<LineItemEntity | null>();
  const [originalAccountName, setOriginalAccountName] = useState<string>("");
  const [originalAccountId, setOriginalAccountId] = useState<string>("");
  const [lineItems, setLineItems] = useState(billJson?.line_items || []);

  // Inline GL picker (2026-06-26). For LLM-classified lines, the Debit A/c cell
  // starts EMPTY and the reviewer must pick from the ranked AI list. UI-only:
  // the line's account_id/account_name are never cleared, so save, capture
  // (gl_selection_events) and Zoho posting (account_id) are unaffected. A line
  // is "confirmed" once picked inline OR saved via the edit modal.
  const [glConfirmedLines, setGlConfirmedLines] = useState<
    Record<number, boolean>
  >({});
  const [openInlineGlIndex, setOpenInlineGlIndex] = useState<number | null>(
    null,
  );

  // Re-arm the inline GL picker whenever a different bill is opened.
  useEffect(() => {
    setGlConfirmedLines({});
    setOpenInlineGlIndex(null);
  }, [documentId]);
  // Memoize tax breakdown calculation to prevent infinite loops

  const taxBreakdown = useMemo(() => {
    return calculateTaxBreakdown(lineItems, billJson);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, billJson?.place_of_supply, billJson?.destination_of_supply]);

  // TCS and TDS are mutually exclusive: if any line item has TDS, TCS dropdown is disabled
  const hasAnyTds = useMemo(
    () =>
      lineItems.some(
        (item) =>
          item.tds_tax_id &&
          item.tds_tax_id !== "" &&
          (item.tds_tax_percentage ?? 0) > 0,
      ),
    [lineItems],
  );

  const resolvedGstTreatment =
    fetchedVendor?.gst_treatment ?? billJson?.gst_treatment;
  const resolvedGstCategory =
    fetchedVendor?.gst_category ?? billJson?.gst_category;

  const showReverseChargeMessage = useMemo(
    () =>
      shouldShowReverseChargeMessage(
        resolvedGstTreatment,
        resolvedGstCategory,
        lineItems,
      ),
    [resolvedGstTreatment, resolvedGstCategory, lineItems],
  );

  // Update GL Dialog state
  const [isUpdateGLDialogOpen, setIsUpdateGLDialogOpen] = useState(false);
  const [pendingGLUpdate, setPendingGLUpdate] = useState<{
    changedLineItemIndex: number;
    oldAccountName: string;
    newAccountName: string;
    newAccountId: string;
  } | null>(null);

  // Initialize shared hooks
  const { taxes, fetchTaxSpecification: loadTaxSpecification } = useTaxes();

  const { determineGSTType } = useGSTDetermination();

  // Inter-state taxes for RCM/Overseas (cached; also used by edit-line modal)
  const [interStateTaxes, setInterStateTaxes] = useState<TaxEntity[]>([]);

  // Validation state
  const [validationData, setValidationData] = useState<any>(null);
  const [isValidationProgressModalOpen, setIsValidationProgressModalOpen] =
    useState(false);
  const [validationPassed, setValidationPassed] = useState(false);
  const [failedValidationMessages, setFailedValidationMessages] = useState<
    string[]
  >([]);

  // GL Classification Modal state
  const [isGLClassificationModalOpen, setIsGLClassificationModalOpen] =
    useState(false);
  const [glClassificationLineItems, setGLClassificationLineItems] = useState<
    GLClassificationLineItem[]
  >([]);

  // "Apply this GL automatically next time?" modal state.
  // Shown on Approve when one or more line items still carry the AI-suggested
  // GL (i.e. the user has not edited the account name). Picking
  // "Save as default suggestion" calls createSemanticOverride directly for the
  // listed items without going through any review flow.
  const [isApplyGLAsDefaultOpen, setIsApplyGLAsDefaultOpen] = useState(false);
  const [applyGLAsDefaultItems, setApplyGLAsDefaultItems] = useState<
    ApplyGLAsDefaultLineItem[]
  >([]);
  const [isApplyingGLDefaults, setIsApplyingGLDefaults] = useState(false);
  // Per-line-item gl_source_layer from the validation API. Populated in
  // handleValidationComplete so buildApplyGLAsDefaultItems can gate on "llm".
  const [glSourceLayerMap, setGlSourceLayerMap] = useState<
    Record<number, string>
  >({});

  // Approval loading state
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);

  // TDS state
  // setIsTDSApplicable(true) still runs when validation finds applicable TDS (so
  // we auto-populate rates). Visibility no longer depends on this flag — see
  // effectiveTdsApplicable below.
  const [, setIsTDSApplicable] = useState(false);
  const [tdsRates, setTdsRates] = useState<TDSRate[]>([]);
  // Always show the TDS column + edit-line dropdown for every vendor, even when
  // the vendor has TDS=No or bill validation returns tds_applicable="no".
  // Default display is "NO TDS"; validation may later fill a concrete rate.
  const effectiveTdsApplicable = true;

  // Per-line TDS classification result (populated after full_validation)
  const tdsClassification =
    validationData?.validation_results?.tds_classification;

  // Per-line GST classification result (OCR -> kNN engine; populated after full_validation).
  // Read-only hint: shows the recommended rate/class + source (learned vs document) next to
  // the GST column, and flags lines the engine wants a human to review.
  const gstClassification =
    validationData?.validation_results?.gst_classification;

  // Per-line ITC classification result (rule + LLM + kNN; populated after
  // full_validation). Drives the inline ITC eligibility picker column.
  const itcClassification =
    validationData?.validation_results?.itc_classification;

  // Accountant's per-line ITC overrides (index -> token). Empty until they pick;
  // the effective status falls back to the engine recommendation.
  const [itcSelections, setItcSelections] = useState<Record<number, string>>(
    {},
  );
  const [openInlineItcIndex, setOpenInlineItcIndex] = useState<number | null>(
    null,
  );

  // Always load TDS rates so the edit-line dropdown is ready for any vendor.
  useEffect(() => {
    let cancelled = false;
    fetchTDSRates()
      .then((response) => {
        if (cancelled) {
          return;
        }
        if (response.success && response.data?.tds_rates) {
          setTdsRates(response.data.tds_rates);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch TDS rates:", err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // GST enabled state
  const [isGstEnabled, setIsGstEnabled] = useState(true);

  // Discount state (not from API, user-editable)
  const [discount, setDiscount] = useState<number>(0);

  // Handler for discount changes - updates both state and billJson
  const handleDiscountChange = useCallback(
    (newDiscount: number) => {
      setDiscount(newDiscount);
      updateBillJson({ discount: newDiscount });
    },
    [updateBillJson],
  );

  // Update lineItems when billJson changes
  useEffect(() => {
    if (billJson?.line_items) {
      // Only update if lineItems doesn't already match billJson.line_items
      // This prevents unnecessary re-renders and race conditions
      setLineItems((prevLineItems) => {
        // Quick reference check first
        if (prevLineItems === billJson.line_items) {
          return prevLineItems; // Same reference, no update needed
        }

        // Deep comparison of tax-related fields to detect actual changes
        const hasChanges =
          prevLineItems.length !== billJson.line_items.length ||
          prevLineItems.some((item, index) => {
            const newItem = billJson.line_items[index];
            if (!newItem) return true;
            // Check for tax-related changes (primary concern) and other critical fields
            return (
              item.tax_id !== newItem.tax_id ||
              item.tax_name !== newItem.tax_name ||
              item.tax_percentage !== newItem.tax_percentage ||
              item.tds_tax_id !== newItem.tds_tax_id ||
              item.tds_tax_name !== newItem.tds_tax_name ||
              item.tds_tax_percentage !== newItem.tds_tax_percentage ||
              item.rate !== newItem.rate ||
              item.quantity !== newItem.quantity
            );
          });

        // Only update if there are actual changes
        if (hasChanges) {
          // Return new array reference to trigger memo recalculation
          return [...billJson.line_items];
        }

        return prevLineItems;
      });
    }
  }, [billJson?.line_items, billJson?.gst_treatment]);

  // Clear line-item TDS when bill date crosses the FY2026-27 TDS cutover
  useEffect(() => {
    if (
      !effectiveTdsApplicable ||
      tdsRates.length === 0 ||
      isDataLoading ||
      !billJson?.line_items?.length
    ) {
      return;
    }

    const documentDate = billJson.date || billJson.invoice_date || "";
    const normalizedRates = tdsRates.map((rate) => ({
      tds_tax_id: rate.tds_tax_id,
      is_expired: rate.is_expired ?? true,
    }));
    const updatedLineItems = applyDocumentDateTdsToLineItems(
      billJson.line_items,
      normalizedRates,
      documentDate,
    );

    if (!lineItemsTdsChanged(billJson.line_items, updatedLineItems)) {
      return;
    }

    setLineItems([...updatedLineItems]);
    updateLineItems(updatedLineItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateLineItems is stable in behavior
  }, [
    billJson?.date,
    billJson?.invoice_date,
    // billJson?.line_items,
    tdsRates,
    effectiveTdsApplicable,
    isDataLoading,
  ]);

  // ZOUR-19: does GST apply to THIS bill?
  //
  // Was the company-wide flag alone. A bill dated before
  // `gstEnabledFrom` now resolves to false as well, so tax columns, tax rates,
  // the tax breakdown and the ITC picker all disappear together — every one of
  // them already hangs off `isGstEnabled`, which is why this is the only line
  // that had to change.
  //
  // Re-runs when the date changes, not just on mount: an edit that moves the
  // bill across the boundary has to re-draw the form.
  //
  // ZOUR-92: and on the REGISTRATION it is filed under, which can have a later
  // date than the company. Re-runs when the location changes too — moving a
  // bill to a branch registered later has to re-draw the form the same way
  // moving its date does.
  useEffect(() => {
    setIsGstEnabled(
      gstAppliesToDocumentFor(
        billJson?.date || billJson?.invoice_date || "",
        billJson?.location_id ?? null,
      ),
    );
  }, [billJson?.date, billJson?.invoice_date, billJson?.location_id, activeLocations]);

  // ZOUR-19: the same question, as a value the JSX can read, so the page can
  // SAY why the tax fields are gone. The notice is derived from the date in
  // front of the user rather than from validation results, so it is right
  // immediately and right on a document that has never been validated.
  const showPreGstNotice = isPreGstRegistrationFor(
    billJson?.date || billJson?.invoice_date || "",
    billJson?.location_id ?? null,
  );

  // Initialize discount from billJson if it exists
  useEffect(() => {
    if (billJson?.discount !== undefined) {
      setDiscount(billJson.discount);
    }
  }, [billJson?.discount]);

  // Calculate GST value from line items
  const calculatedGstValue = lineItems.reduce((sum, item) => {
    const taxPercentage = item.tax_percentage || 0;
    const taxAmount =
      ((item.rate || 0) * (item.quantity || 0) * taxPercentage) / 100;
    return sum + taxAmount;
  }, 0);

  /** Pending-review strike maps from persisted `review.change_details` (FBAC bill). */
  const billFbacDiffMaps = useMemo(() => {
    const emptyHeader = new Map<string, { oldText: string; newText: string }>();
    const emptyLine = new Map<string, { oldText: string; newText: string }>();
    if (!isReviewPending) {
      return { header: emptyHeader, line: emptyLine };
    }
    return buildBillPendingReviewDisplayMapsFromChangeDetails(
      review?.change_details,
      taxes,
    );
  }, [isReviewPending, review?.change_details, taxes]);

  const fbacHd = (key: string) => billFbacDiffMaps.header.get(key);

  const fbacLineDiff = (lineOneBased: number, fieldKey: string) =>
    billFbacDiffMaps.line.get(`${lineOneBased}-${fieldKey}`);

  const companyCurrencyCode = useMemo(() => {
    const c = getCompany();
    const code = c?.currency;
    return String(code ?? "")
      .trim()
      .toUpperCase();
  }, []);

  const documentCurrencyCode = String(billJson?.currency_code ?? "")
    .trim()
    .toUpperCase();

  const showExchangeRateField =
    !!companyCurrencyCode &&
    !!documentCurrencyCode &&
    companyCurrencyCode !== documentCurrencyCode;

  const accountingBackend = getStoredAccountingBackend();
  const isTallyBackend = accountingBackend === "tally";
  const exchangeRateRequired = isTallyBackend && showExchangeRateField;
  const exchangeRatePostContext = useMemo(
    () => ({
      userSet: !!billJson?.exchange_rate_user_set,
      isPosted: isPostedToZoho,
    }),
    [billJson?.exchange_rate_user_set, isPostedToZoho],
  );

  const { loading: exchangeRateLoading } = useZohoDocumentExchangeRate({
    enabled: !isTallyBackend && showExchangeRateField,
    currencyId: billJson?.currency_id,
    fromDate: billJson?.date || billJson?.invoice_date,
    currentRate: billJson?.exchange_rate,
    onRateFetched: (rate) => {
      setBillJson((prev) => {
        if (!prev) return prev;
        if (!needsZohoExchangeRateFetch(prev.exchange_rate)) return prev;
        return {
          ...prev,
          exchange_rate: rate,
          exchange_rate_user_set: false,
        };
      });
    },
  });

  //  All these info from the json or options json
  const basicInfo = useMemo(
    () => ({
      invoiceType: "BILLS",
      invoiceNumber: getFieldValue(
        billJson?.bill_number || billJson?.invoice_number,
        "Not provided",
      ),
      currency: getFieldValue(billJson?.currency_code, "USD"),
      exchange_rate: formatExchangeRateForBasicInfoField(
        billJson?.exchange_rate,
        {
          showFx: showExchangeRateField,
          backend: accountingBackend,
          userSet: billJson?.exchange_rate_user_set,
        },
      ),
      exchange_rate_user_set: billJson?.exchange_rate_user_set,
      optionsData: {
        invoiceNumber: [],
        currency: [],
      },
    }),
    [
      billJson?.bill_number,
      billJson?.invoice_number,
      billJson?.currency_code,
      billJson?.exchange_rate,
      billJson?.exchange_rate_user_set,
      showExchangeRateField,
      accountingBackend,
    ],
  );

  const datesInfo = {
    documentDate: formatDate(
      billJson?.date || billJson?.invoice_date,
      "Not provided",
    ),
    postingDate: formatDate(
      billJson?.date || billJson?.invoice_date,
      "Not provided",
    ),
    dueDate: formatDate(billJson?.due_date, "Not provided"),
    optionsData: {
      documentDate: [],
      postingDate: [],
      dueDate: [],
    },
  };

  const customerGstinHint = useMemo(() => {
    const company = getCompany();
    const companyRegistered = company?.companyGstEnabled !== "0";
    const companyHasGstin = !!company?.gstNumber?.trim();
    const rawGstin = (billJson?.customer_gstin ?? "").trim().toUpperCase();
    const placeholders = ["NOT AVAILABLE", "N/A", "NA", "NONE", "NULL"];
    const billMissingGstin = !rawGstin || placeholders.includes(rawGstin);
    const vendorIsGst = billJson?.gst_treatment === "business_gst";

    const show =
      isGstEnabled &&
      companyRegistered &&
      companyHasGstin &&
      billMissingGstin &&
      vendorIsGst;

    return {
      show,
      label: "B2B expected",
      tooltip:
        "This vendor is a GST-registered supplier. B2B bills should include your company GSTIN for input tax credit.",
    };
  }, [billJson?.customer_gstin, billJson?.gst_treatment, isGstEnabled]);

  const taxInfo = {
    vendorGstin: billJson?.vendor_gstin || billJson?.gst_no || "",
    customerGstin: billJson?.customer_gstin || "",
    // gstRate: billJson?.tax_total, // This would need to be calculated from line items
    taxBreakdown: `Total Tax: ${getFieldValue(
      billJson?.currency_symbol,
      "₹",
    )}${formatAmount(calculatedGstValue, "0.00")}`,
    optionsData: {
      vendorGstin: [],
      customerGstin: [],
      gstRate: [],
      taxBreakdown: [],
    },
    isGstEnabled: isGstEnabled,
  };

  const documentMetadata = {
    source: "BILLS",
    uploadedBy: "System", // This would come from the bill record
    uploadTimestamp: "Not provided", // This would come from the bill record
    fileName: fileName || "document.pdf",
    optionsData: {
      uploadedBy: [],
      uploadTimestamp: [],
      fileName: [],
    },
  };

  const amountSummary = {
    subtotal: `${getFieldValue(billJson?.currency_symbol, "₹")}${formatAmount(
      billJson?.sub_total,
      "0.00",
    )}`,
    gstValue: `${getFieldValue(billJson?.currency_symbol, "₹")}${formatAmount(
      calculatedGstValue,
      "0.00",
    )}`,
    discountsAdjustments:
      discount !== 0
        ? `${billJson?.currency_symbol || "₹"}${formatAmount(discount, "0.00")}`
        : "No adjustments",
    totalAmount: `${getFieldValue(
      billJson?.currency_symbol,
      "₹",
    )}${formatAmount(billJson?.total, "0.00")}`,
    optionsData: {
      subtotal: [],
      gstValue: [],
      discountsAdjustments: [],
      totalAmount: [],
    },
    isGstEnabled: isGstEnabled,
  };

  // Load GST determination and taxes AFTER permissions and document are loaded
  useEffect(() => {
    // Wait for main data (permissions + document) to finish loading
    if (isDataLoading) return;

    // Wait for bill data to be available
    if (!initialBillJson && !fetchedBillJson) return;

    let mounted = true;

    const loadInitialData = async () => {
      try {
        // Use the initial data or fetched data for GST determination
        const billDataForGST = initialBillJson || fetchedBillJson;
        if (!billDataForGST) return;

        if (isManualSource) {
          // Manual source: compute inter/intra from place_of_supply vs destination_of_supply
          const normalizeState = (state: string | null | undefined): string => {
            if (!state) return "";
            const normalized = (state || "").toString().trim().toUpperCase();
            if (normalized.length === 2) return normalized;
            try {
              return getStateAbbreviation(state) || normalized;
            } catch {
              return normalized;
            }
          };
          const placeOfSupply = normalizeState(billDataForGST.place_of_supply);
          const destinationOfSupply = normalizeState(
            billDataForGST.destination_of_supply,
          );
          const hasSupplyInfo = placeOfSupply && destinationOfSupply;
          const isInterState =
            hasSupplyInfo && placeOfSupply !== destinationOfSupply;
          const specification = isInterState ? "inter" : "intra";
          if (mounted) {
            await loadTaxSpecification(specification);
          }
        } else {
          // Non-manual: use GST determination API
          const companyData = getCompany();
          const billDataForGSTWithDest = {
            ...billDataForGST,
            source_of_supply: billDataForGST.place_of_supply,
            destination_of_supply: companyData?.state,
          };

          const gstResult = await determineGSTType(billDataForGSTWithDest);
          if (mounted && gstResult) {
            // Use tax_type from GST determination to fetch appropriate taxes
            const specification = gstResult.tax_type; // "inter" or "intra"
            await loadTaxSpecification(specification);
          }
        }
      } catch (error) {
        // Fallback to intra state taxes on error
        try {
          if (mounted) await loadTaxSpecification("intra");
        } catch (fallbackError) {}
      }
    };

    // Load inter-state taxes once for RCM/Overseas scenarios
    // Note: Using direct API call here because we need these cached separately
    const loadInterStateTaxes = async () => {
      try {
        const response = await taxRepository.fetchTaxSpecification("inter");
        if (mounted && response.success) {
          const activeTaxes = response.data.taxes.filter(
            (tax: TaxEntity) => tax.is_inactive === "0",
          );
          setInterStateTaxes(activeTaxes);
        }
      } catch (error) {
        console.error("Failed to fetch inter-state taxes:", error);
      }
    };

    loadInitialData();
    loadInterStateTaxes();

    return () => {
      mounted = false;
    };
  }, [
    isDataLoading,
    isManualSource,
    initialBillJson,
    fetchedBillJson,
    loadTaxSpecification,
    determineGSTType,
  ]);

  // Effect for keyboard shortcuts and cleanup
  useEffect(() => {
    // Initialize lastSavedDataRef with initial data
    if (initialBillJson && !lastSavedDataRef.current) {
      lastSavedDataRef.current = JSON.stringify(initialBillJson);
    }

    // Copy ref value for cleanup
    const currentTimeout = debounceTimeoutRef.current;

    return () => {
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
    };
  }, [initialBillJson]);

  // const [numPages, setNumPages] = useState<number>(0);
  // const [pageNumber, setPageNumber] = useState<number>(1);
  // const [scale, setScale] = useState<number>(1.0);

  // const pdfUrl = signedUrl;
  // Handle document URL refresh from DocumentViewer
  // Note: signedUrl and fileName are now managed by the useBillDetails hook
  // and automatically updated when the bill is refetched
  const handlePdfUrlRefresh = useCallback(
    (newUrl: string, newFileName: string) => {
      // These are now read-only from the hook, so we just log
      // The hook will handle updates when data is refetched
    },
    [],
  );

  // Memoize DocumentViewer props to prevent unnecessary re-renders
  const documentViewerProps = useMemo(
    () => ({
      documentId: documentId ? parseInt(documentId) : undefined,
      documentType: "bill" as const,
      pdfUrl: signedUrl || undefined,
      fileName: fileName || "document.pdf",
      className: "w-1/2",
      onError: (error: any) => console.error("PDF Error:", error),
      onUrlRefresh: handlePdfUrlRefresh,
    }),
    [documentId, signedUrl, fileName, handlePdfUrlRefresh],
  );

  // Note: Queries are now automatically fetched via useQueriesBySource hook

  // Validate bill before approval/update
  const validateBill = (): boolean => {
    // Clear all previous validation errors first
    clearErrors();

    const newErrors: Record<string, string> = {};

    // Check if vendor is matched
    if (!partyMatched) {
      newErrors.vendor =
        "Vendor verification required. Please search and match a vendor.";
    }

    // Check if currency is INR
    // if (billJson?.currency_code && billJson.currency_code !== "INR") {
    //   newErrors.currency = `This feature is not built for currencies other than INR yet. Current currency: ${billJson.currency_code}`;
    // }

    // Check line item tax validation
    const lineItemTaxValidation = validateLineItemsTaxInfo();
    if (!lineItemTaxValidation.isValid) {
      newErrors.lineItemTax =
        lineItemTaxValidation.errorMessage ||
        "Line items are missing tax information";
    }

    if (
      exchangeRateRequired &&
      isExchangeRateMissingForTallyPost(billJson?.exchange_rate, {
        userSet: billJson?.exchange_rate_user_set,
        isPosted: isPostedToZoho,
      })
    ) {
      newErrors.exchange_rate =
        "Exchange rate is required for foreign currency documents when posting to Tally.";
    }

    // Set all validation errors at once (after clearing)
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
    }

    return Object.keys(newErrors).length === 0;
  };

  // ── Vendor-change pipeline rerun ────────────────────────────────────────
  // When the accountant manually changes the vendor, re-run the post-match
  // pipeline (GL + supply/tax + TDS) on the backend, poll until done, then
  // reload so the recomputed values show. See docs/vendor-change-rerun-design.md
  const [isRecomputing, setIsRecomputing] = useState(false);

  /**
   * ZOUR-65 polish — the registration the reviewer just moved this bill to,
   * held only for as long as the re-run takes.
   *
   * `isRecomputing` alone cannot say this: the vendor change sets the same flag
   * and would put a location name under the picker for a re-run that has
   * nothing to do with it. The name is what makes the wait legible ("this is
   * running because I picked Bengaluru"), so it is tracked beside the flag and
   * cleared by whoever starts a different re-run.
   */
  const [locationRerunName, setLocationRerunName] = useState<string | null>(null);
  const recomputePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Vendor pending re-run confirmation (drives VendorRerunConfirmModal). Null
  // when no confirm is open.
  const [pendingVendorRerun, setPendingVendorRerun] = useState<{
    vendorId: string;
    vendorName: string;
    oldVendorName?: string | null;
  } | null>(null);
  // Error surfaced inside VendorRerunConfirmModal if the re-run fails.
  const [rerunError, setRerunError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (recomputePollRef.current) {
        clearInterval(recomputePollRef.current);
        recomputePollRef.current = null;
      }
    };
  }, []);

  /**
   * ZOUR-65 — the poll that watches a pipeline re-run to completion.
   *
   * A SIBLING of the identical poll inside `triggerVendorRerun` below, not an
   * extraction of it. That one is left exactly as it was on purpose: it drives a
   * flow that needs a real vendor change and a cron pass to exercise, and this
   * phase is not the place to re-plumb it untested. The duplication is
   * deliberate and worth consolidating once both paths can be tested together.
   */
  const beginRecomputePolling = useCallback(() => {
    if (!documentId) return;

    if (recomputePollRef.current) clearInterval(recomputePollRef.current);
    let elapsed = 0;
    recomputePollRef.current = setInterval(async () => {
      elapsed += 2000;
      try {
        const res = await getRecomputeStatus(documentId);
        const finished =
          res.status === "done" || res.status === "error" || res.status === "idle";
        if (finished || elapsed > 180000) {
          if (recomputePollRef.current) {
            clearInterval(recomputePollRef.current);
            recomputePollRef.current = null;
          }
          setIsRecomputing(false);
          if (res.status === "done") {
            window.history.replaceState(null, "");
            window.location.reload();
          } else if (res.status === "error") {
            setRerunError(res.message || "Re-run failed. Please try again.");
          } else if (elapsed > 180000) {
            setRerunError("Re-run timed out. Please try again.");
          } else {
            setRerunError("Re-run did not complete. Please try again.");
          }
        }
      } catch {
        if (recomputePollRef.current) {
          clearInterval(recomputePollRef.current);
          recomputePollRef.current = null;
        }
        setIsRecomputing(false);
        setRerunError("Lost connection while re-running. Please try again.");
      }
    }, 2000);
  }, [documentId]);

  /**
   * ZOUR-65 — the reviewer moved this bill to another GST registration.
   *
   * The picker has already written `location_id` into the working JSON; this
   * persists it and recomputes the line tax, because inter- vs intra-state has
   * just changed and Zoho refuses a bill whose tax group disagrees with its
   * location.
   */
  const triggerLocationRerun = useCallback(
    async (locationId: string | null, nextBillJson: BillDataEntity) => {
      if (!documentId) return;
      setRerunError(null);
      try {
        setIsRecomputing(true);
        setLocationRerunName(
          activeLocations.find(
            (candidate) => candidate.zoho_location_id === locationId
          )?.name ?? null
        );
        await rerunLocationChange(documentId, locationId);
        beginRecomputePolling();
      } catch (err) {
        setIsRecomputing(false);
        setLocationRerunName(null);

        // "Nothing to re-run" is not a failure. A manually created bill has no
        // processing job behind it, and re-picking the location it is already
        // on changes nothing.
        //
        // But the re-run is also what PERSISTS the choice — it writes the row
        // and the JSON. On these two paths it never ran, so without saving here
        // the reviewer's pick lives only in React state and is gone on reload.
        // That is why this saves through the normal bill-JSON path rather than
        // just swallowing the error.
        const reason =
          err instanceof APIError
            ? ((err.data as { reason?: string } | undefined)?.reason ?? null)
            : null;

        if (reason === "no_job" || reason === "unchanged") {
          try {
            await updateBillJsonHook(documentId, nextBillJson);
          } catch {
            setRerunError(
              "The location was changed but could not be saved. Please try again."
            );
          }

          return;
        }

        setRerunError(
          err instanceof Error
            ? err.message
            : "Could not start the re-run. Please try again."
        );
      }
    },
    [documentId, activeLocations, beginRecomputePolling, updateBillJsonHook]
  );

  const triggerVendorRerun = useCallback(
    async (vendorId: string, vendorName: string) => {
      if (!documentId || !vendorId) return;
      setRerunError(null);
      try {
        setIsRecomputing(true);
        // Not a location re-run: drop any name a previous one left behind so
        // the picker cannot narrate somebody else's wait.
        setLocationRerunName(null);
        await rerunVendorChange(documentId, vendorId, vendorName);

        if (recomputePollRef.current) clearInterval(recomputePollRef.current);
        let elapsed = 0;
        recomputePollRef.current = setInterval(async () => {
          elapsed += 2000;
          try {
            const res = await getRecomputeStatus(documentId);
            const finished =
              res.status === "done" ||
              res.status === "error" ||
              res.status === "idle";
            if (finished || elapsed > 180000) {
              if (recomputePollRef.current) {
                clearInterval(recomputePollRef.current);
                recomputePollRef.current = null;
              }
              setIsRecomputing(false);
              if (res.status === "done") {
                // Reload so recomputed GL / tax / TDS / totals are shown.
                // Clear router history state first: location.state.billDocument
                // holds the pre-rerun document and would otherwise re-hydrate
                // stale. documentId still recovers from sessionStorage, so we
                // stay on this bill and the hook re-fetches the fresh bill_json.
                window.history.replaceState(null, "");
                window.location.reload();
              } else if (res.status === "error") {
                setRerunError(
                  res.message || "Re-run failed. Please try again.",
                );
              } else if (elapsed > 180000) {
                setRerunError("Re-run timed out. Please try again.");
              } else {
                setRerunError("Re-run did not complete. Please try again.");
              }
            }
          } catch {
            if (recomputePollRef.current) {
              clearInterval(recomputePollRef.current);
              recomputePollRef.current = null;
            }
            setIsRecomputing(false);
            setRerunError(
              "Lost connection while re-running. Please try again.",
            );
          }
        }, 2000);
      } catch (err) {
        setIsRecomputing(false);
        setRerunError("Could not start the re-run. Please try again.");
        console.error("Vendor rerun failed to start:", err);
      }
    },
    [documentId],
  );

  // Add handler for party selection
  const handlePartySelect = useCallback(
    (party: any, isManualSelection: boolean = false): boolean => {
      // Use ref to get current billJson to avoid dependency issues
      const currentBillJson = billJsonRef.current;
      // Snapshot the vendor already on the bill BEFORE updateBillJson runs, so
      // the re-run decision below can tell whether the pick actually changed the
      // vendor. Primitives, so a later in-place update can't mutate them.
      const previousVendorId = String(currentBillJson?.vendor_id ?? "").trim();
      const previousVendorName = currentBillJson?.vendor_name ?? null;

      clearFieldError("vendorCurrency");

      const vendorCurrencyId = String(party.currency_id ?? "").trim();
      const billCurrencyId = String(currentBillJson?.currency_id ?? "").trim();
      const vendorCurrencyCode = party.currency_code || party.currency;
      const billCurrencyCode = currentBillJson?.currency_code;

      // Check if vendor currency matches bill currency (silent check, will validate on submit)
      if (
        vendorCurrencyId &&
        billCurrencyId &&
        vendorCurrencyId !== billCurrencyId
      ) {
        setFieldError(
          "vendorCurrency",
          `Currency mismatch: Vendor uses ${vendorCurrencyCode}, but bill is in ${billCurrencyCode}. Please select a vendor with matching currency.`,
        );
        return false; // Don't proceed with selection
      }

      // Use zoho_vendorid if available, otherwise fall back to vendor_id
      const vendorId = party.zoho_vendorid || party.vendor_id;

      // Update vendor data if party has vendor information
      if (
        vendorId &&
        (party.vendor_legal_name ||
          party.type_of_vendor ||
          party.registered_office_address)
      ) {
        setPartyMatched(true);
        clearFieldError("vendor"); // Clear vendor validation error
        const sourceOfSupply = getSupplyLocationCode(
          party.state,
          party.country,
        );

        // If vendor gst_treatment is overseas, clear tax_id from all line items
        // const isOverseas = party.gst_treatment?.toLowerCase() === "overseas";
        // const existingLineItems = currentBillJson?.line_items;

        // Only process line items if they exist and vendor is overseas
        let updatedLineItems: any[] | undefined = undefined;
        // if (isOverseas && existingLineItems && existingLineItems.length > 0) {
        //   updatedLineItems = existingLineItems.map((item) => ({
        //     ...item,
        //     tax_id: "",
        //     tax_name: "",
        //     tax_percentage: 0,
        //     tax_amount: 0,
        //     item_total_inclusive_of_tax:
        //       item.item_total || item.rate * item.quantity,
        //   }));
        // }

        // Build update object - only include line_items if we have updated items
        const updateData: any = {
          vendor_id: vendorId,
          vendor_address: party.address,
          vendor_name: party.vendor_legal_name,
          vendor_gstin: party.gstin,
          gst_no: party.gstin,
          gst_treatment: party.gst_treatment,
          gst_category: party.gst_category,
          payment_terms: party.payment_terms,
          source_of_supply: sourceOfSupply,
        };

        // Only include line_items if we updated them
        if (updatedLineItems) {
          updateData.line_items = updatedLineItems;
        }

        updateBillJson(updateData);

        // If this is a manual selection, mark it in the ref to prevent auto-select from overriding
        if (isManualSelection) {
          const manualSelectionHash = JSON.stringify({
            vendor_id: vendorId,
            vendor_name: party.vendor_legal_name,
            vendor_gstin: party.gstin,
            vendor_address: party.address,
            gst_treatment: party.gst_treatment,
            gst_category: party.gst_category,
            payment_terms: party.payment_terms,
            source_of_supply: sourceOfSupply,
          });

          processedVendorUpdateRef.current = {
            vendorId,
            timestamp: Date.now(), // Current time for manual selection
            vendorDataHash: manualSelectionHash,
            isManualSelection: true, // Mark as manual selection
            // Don't set sessionStorageTimestamp for manual selections
          };

          // Re-run the pipeline (GL + supply/tax + TDS) for the newly chosen
          // vendor — but only when the vendor actually changed. Re-picking the
          // same vendor already on the bill would needlessly wipe and recompute
          // classifications (including manual GL edits) for no real change.
          if (
            previousVendorId &&
            String(vendorId).trim() === previousVendorId
          ) {
            return true; // same vendor — nothing to recompute
          }
          // Gate the re-run behind an explicit confirm modal, since it
          // overwrites the current classifications including any manual edits.
          setPendingVendorRerun({
            vendorId,
            vendorName: party.vendor_legal_name,
            // First-time assignment is keyed off a real matched vendor_id, not the
            // OCR-detected vendor_name (unmatched bills carry an extracted name with
            // no id). No prior match → no "overwrite" warning, "classify" wording.
            oldVendorName: previousVendorId ? previousVendorName : null,
          });
        }

        return true;
      }

      return false;
    },

    // setPendingVendorRerun is a stable setter; the actual re-run is fired from
    // the confirm modal's onConfirm, so triggerVendorRerun is no longer a dep here.
    [updateBillJson, setFieldError, clearFieldError],
  );

  // Ref to track processed vendor updates to prevent infinite loops
  // Also tracks manual selections to prevent auto-select from overriding them
  const processedVendorUpdateRef = useRef<{
    vendorId: string;
    timestamp: number; // Timestamp of the ref update (not sessionStorage timestamp)
    vendorDataHash: string;
    isManualSelection?: boolean; // Flag to indicate manual selection from modal
    sessionStorageTimestamp?: number; // Store sessionStorage timestamp if from auto-select
  } | null>(null);

  // Auto-select vendor after creating/editing
  useEffect(() => {
    const autoSelectData = sessionStorage.getItem("autoSelectVendor");

    if (autoSelectData) {
      try {
        const {
          vendor,
          action,
          timestamp: sessionStorageTimestamp,
        } = JSON.parse(autoSelectData);
        // Use zoho_vendorid if available, otherwise fall back to vendor_id
        const storedVendorId = vendor.zoho_vendorid || vendor.vendor_id;

        // Create a hash of vendor data to detect actual changes
        const vendorDataHash = JSON.stringify({
          vendor_id: storedVendorId,
          vendor_name: vendor.vendor_legal_name,
          vendor_gstin: vendor.gstin,
          vendor_address: vendor.address,
          gst_treatment: vendor.gst_treatment,
          gst_category: vendor.gst_category,
          payment_terms: vendor.payment_terms,
          source_of_supply: getSupplyLocationCode(vendor.state, vendor.country),
        });

        // CRITICAL: Skip auto-select if there's a manual selection for a DIFFERENT vendor
        // Manual selections always take precedence unless it's the same vendor with newer data
        const currentTime = Date.now();
        const hasManualSelection =
          processedVendorUpdateRef.current?.isManualSelection;
        const manuallySelectedVendorId =
          processedVendorUpdateRef.current?.vendorId;

        if (hasManualSelection && processedVendorUpdateRef.current) {
          // Check if the sessionStorage vendor is different from what was manually selected
          const isDifferentVendor =
            manuallySelectedVendorId &&
            storedVendorId &&
            manuallySelectedVendorId !== storedVendorId;

          if (isDifferentVendor) {
            // If it's a different vendor, check how recent the manual selection was
            const manualSelectionAge =
              currentTime - processedVendorUpdateRef.current.timestamp;

            // If manual selection was recent (< 10 seconds), block to prevent race conditions
            // If manual selection was old (> 10 seconds), allow auto-select (user is switching vendors)
            if (manualSelectionAge < 10000) {
              return;
            } else {
              // Manual selection is old, user is switching vendors - allow auto-select
              // Clear the manual selection flag since we're switching to a different vendor

              // Clear the manual selection flag to allow this auto-select
              processedVendorUpdateRef.current.isManualSelection = false;
              // Continue with auto-select below
            }
          } else {
            // Same vendor - use existing logic
            // If it's the same vendor, only allow auto-select if:
            // 1. SessionStorage timestamp is newer than manual selection (recent edit)
            // 2. Manual selection wasn't too recent (within 10 seconds)
            const sessionStorageIsNewer =
              !processedVendorUpdateRef.current.sessionStorageTimestamp ||
              sessionStorageTimestamp >
                processedVendorUpdateRef.current.sessionStorageTimestamp;

            const manualSelectionRecent =
              currentTime - processedVendorUpdateRef.current.timestamp < 10000;

            // Only proceed if sessionStorage is newer AND manual selection wasn't too recent
            if (!sessionStorageIsNewer || manualSelectionRecent) {
              return;
            }
          }
        }

        // Only process if the data is recent (within last 60 seconds)
        if (currentTime - sessionStorageTimestamp < 60000) {
          // Wait for bill data to be loaded before processing (for both created and updated)
          // updateBillJson requires billJson or fetchedBillJson to exist
          if (isDataLoading && !fetchedBillJson) {
            return; // Don't clear sessionStorage, will retry when data loads
          }

          // For "created" actions, check if current billJson already has this vendor
          if (action === "created") {
            // Create hash of CURRENT bill data for comparison
            const currentBillVendorHash = billJson
              ? JSON.stringify({
                  vendor_id: billJson.vendor_id,
                  vendor_name: billJson.vendor_name,
                  vendor_gstin: billJson.vendor_gstin,
                  vendor_address: billJson.vendor_address,
                  gst_treatment: billJson.gst_treatment,
                  gst_category: billJson.gst_category,
                  payment_terms: billJson.payment_terms,
                  source_of_supply: billJson.source_of_supply,
                })
              : null;

            // Only skip if:
            // 1. We've already processed this exact action (same timestamp)
            // 2. AND the current billJson already matches the new vendor data (data already applied)
            const alreadyProcessedAndApplied =
              processedVendorUpdateRef.current &&
              processedVendorUpdateRef.current.vendorId === storedVendorId &&
              processedVendorUpdateRef.current.sessionStorageTimestamp ===
                sessionStorageTimestamp &&
              currentBillVendorHash === vendorDataHash &&
              !processedVendorUpdateRef.current.isManualSelection;

            if (alreadyProcessedAndApplied) {
              return;
            }

            // Mark as processed BEFORE calling handlePartySelect
            processedVendorUpdateRef.current = {
              vendorId: storedVendorId,
              timestamp: currentTime,
              vendorDataHash,
              isManualSelection: false, // Auto-select is not manual
              sessionStorageTimestamp: sessionStorageTimestamp, // Store sessionStorage timestamp
            };

            handlePartySelect(vendor, false); // false = not manual
            return;
          }

          // For "updated" actions, check if already processed
          const currentVendorId = billJson?.vendor_id;

          // Create hash of CURRENT bill data for comparison
          const currentBillVendorHash = billJson
            ? JSON.stringify({
                vendor_id: billJson.vendor_id,
                vendor_name: billJson.vendor_name,
                vendor_gstin: billJson.vendor_gstin,
                vendor_address: billJson.vendor_address,
                gst_treatment: billJson.gst_treatment,
                gst_category: billJson.gst_category,
                payment_terms: billJson.payment_terms,
                source_of_supply: billJson.source_of_supply,
              })
            : null;

          // Check if we've already processed this exact update
          // Only skip if:
          // 1. We have a processed ref
          // 2. The vendor ID matches
          // 3. The sessionStorage timestamp matches (same edit session) - THIS IS KEY: different timestamp = new edit
          // 4. The CURRENT bill data hash matches the new vendor data hash (data already applied)
          // 5. AND it's not a manual selection
          const alreadyProcessed =
            processedVendorUpdateRef.current &&
            processedVendorUpdateRef.current.vendorId === storedVendorId &&
            processedVendorUpdateRef.current.sessionStorageTimestamp ===
              sessionStorageTimestamp && // Different timestamp = proceed
            currentBillVendorHash === vendorDataHash &&
            !processedVendorUpdateRef.current.isManualSelection;

          if (alreadyProcessed) {
            // Already processed this exact update and data is already applied - skip to prevent loop
            return;
          }

          // Check if vendor data actually changed (compare new data with current bill data)
          const vendorDataChanged =
            !billJson?.vendor_id ||
            billJson.vendor_id !== storedVendorId ||
            billJson?.vendor_name !== vendor.vendor_legal_name ||
            billJson?.vendor_gstin !== vendor.gstin ||
            billJson?.vendor_address !== vendor.address ||
            billJson?.gst_treatment !== vendor.gst_treatment ||
            billJson?.gst_category !== vendor.gst_category ||
            billJson?.payment_terms !== vendor.payment_terms ||
            billJson?.source_of_supply !==
              getSupplyLocationCode(vendor.state, vendor.country);

          // Update if:
          // 1. Vendor IDs DON'T match (different vendor) - always update
          // 2. OR there's no current vendor set - always update
          // 3. OR vendor matches AND data actually changed
          const shouldUpdate =
            !currentVendorId ||
            currentVendorId !== storedVendorId ||
            (currentVendorId === storedVendorId && vendorDataChanged);

          if (shouldUpdate) {
            // Mark as processed BEFORE calling handlePartySelect
            processedVendorUpdateRef.current = {
              vendorId: storedVendorId,
              timestamp: currentTime,
              vendorDataHash,
              isManualSelection: false, // Auto-select is not manual
              sessionStorageTimestamp: sessionStorageTimestamp, // Store sessionStorage timestamp
            };

            handlePartySelect(vendor, false); // false = not manual
          } else {
            // Vendor matches and no data changed - mark as processed to prevent future triggers
            processedVendorUpdateRef.current = {
              vendorId: storedVendorId,
              timestamp: currentTime,
              vendorDataHash,
              isManualSelection: false,
              sessionStorageTimestamp: sessionStorageTimestamp,
            };
          }
        } else {
          // Data is stale, clear ref (but keep sessionStorage for reference)
          // Don't clear sessionStorage here - it might be needed for future loads
          processedVendorUpdateRef.current = null;
        }
      } catch (error) {
        console.error("Error parsing autoSelectVendor data:", error);
        processedVendorUpdateRef.current = null;
      }
    }
  }, [
    handlePartySelect,
    billJson?.vendor_id,
    billJson?.vendor_name,
    billJson?.vendor_gstin,
    billJson?.vendor_address,
    billJson?.gst_treatment,
    billJson?.gst_category,
    billJson?.payment_terms,
    billJson?.source_of_supply,
    fetchedBillJson,
    isDataLoading,
    isPostedToZoho,
  ]);

  const handleSearchPartyModalOpen = () => {
    setIsSearchPartyModalOpen(true);
  };

  const handleQueryClick = (queryId: number) => {
    setSelectedQueryId(queryId);
    setIsQueryDetailModalOpen(true);
  };

  // Handle query modal opening
  const handleQueryRaiseModalOpen = () => {
    setIsQueryRaiseModalOpen(true);
  };

  // Validation handlers
  const handleValidationComplete = useCallback(
    async (result: any) => {
      let postValidationLineItems: any[] | null = null;
      try {
        setValidationData(result.data);
        const validationSummary = result.data?.validation_results;
        const passedValidations = validationSummary?.passed_validations || 0;
        const totalValidations = validationSummary?.total_validations || 0;

        // Check if all validations passed
        const allValidationsPassed = passedValidations === totalValidations;
        setValidationPassed(allValidationsPassed);

        // Update line items tax_ids from inter_intra_gst validation response
        // Do a full on check and keep updating the tax_id based on the response and set state !!
        const interIntraGst = validationSummary?.inter_intra_gst;

        if (interIntraGst?.details?.line_items_tax_ids) {
          const lineItemsTaxIds = interIntraGst.details.line_items_tax_ids;
          await loadTaxSpecification(interIntraGst.details.tax_specification);

          // CRITICAL: Create the updated array ONCE and reuse it for both updates
          // This ensures both state updates use the exact same array reference
          const updateLineItemsWithTaxInfo = (items: any[]) => {
            const roundToTwoDecimals = (n: number): number =>
              Math.round((n + Number.EPSILON) * 100) / 100;
            const updatedItems = [...items];

            lineItemsTaxIds.forEach((taxIdInfo: any) => {
              const {
                index,
                tax_id,
                found,
                tax_percentage,
                tax_specification,
              } = taxIdInfo;

              if (
                found &&
                tax_id &&
                index >= 0 &&
                index < updatedItems.length
              ) {
                const currentItem = updatedItems[index];
                if (
                  currentItem.tax_id === "NO_TAX" ||
                  currentItem.tax_id === "no_tax"
                ) {
                  return;
                }

                // Convert tax_specification to tax_name format
                let taxName = "";
                if (tax_specification === "intra") {
                  taxName = `GST${tax_percentage || ""}`;
                } else if (tax_specification === "inter") {
                  taxName = `IGST${tax_percentage || ""}`;
                }

                const nextItem = {
                  ...currentItem,
                  tax_id: tax_id,
                  tax_percentage: tax_percentage,
                  tax_name: taxName,
                };

                const rate = Number(nextItem?.rate ?? 0) || 0;
                const quantity = Number(nextItem?.quantity ?? 0) || 0;
                const taxable = rate * quantity;
                const pct = Number(nextItem?.tax_percentage ?? 0) || 0;
                const taxAmount =
                  String(nextItem?.tax_id ?? "") === "NO_TAX" ||
                  String(nextItem?.tax_id ?? "") === "no_tax" ||
                  String(nextItem?.tax_id ?? "") === "" ||
                  pct === 0
                    ? 0
                    : roundToTwoDecimals((taxable * pct) / 100);

                updatedItems[index] = {
                  ...nextItem,
                  tax_amount: taxAmount,
                  item_total: taxable,
                  item_total_inclusive_of_tax: roundToTwoDecimals(
                    taxable + taxAmount,
                  ),
                };
              }
            });

            return updatedItems;
          };

          // Get current line items from billJson (source of truth)
          const currentLineItems = billJson?.line_items || lineItems;

          // Create updated array ONCE so we can also snapshot a baseline
          const updatedLineItems = updateLineItemsWithTaxInfo(currentLineItems);
          postValidationLineItems = updatedLineItems;

          // Snapshot baseline only on the first validation run; never overwrite after lock.
          if (!validationBaselineLockedRef.current) {
            validationBaselineLineItemsRef.current = updatedLineItems.map(
              (li) => ({
                ...li,
              }),
            );
          }

          // Update billJson FIRST with the shared array reference (match Invoice behavior)
          // Do NOT update originalBillJson here - that would re-trigger the sync effect
          // and overwrite billJson with stale fetchedBillJson (e.g. after vendor change)
          setBillJson((prevBillJson) => {
            if (!prevBillJson) return prevBillJson;

            return {
              ...prevBillJson,
              line_items: updatedLineItems, // Same array reference
            };
          });

          // Update lineItems with the SAME array reference
          // This ensures both states are perfectly in sync
          setLineItems([...updatedLineItems]);
        }

        // Overseas / RCM / non-company currency: force IGST 18% as a system
        // mutation on the *first* validation pass only so the baseline snapshot
        // includes it. Later re-validations (e.g. after the user picks IGST 12%)
        // must not overwrite their tax choice.
        {
          const linesForIgst =
            postValidationLineItems ?? billJson?.line_items ?? lineItems ?? [];
          const gstTreatment =
            fetchedVendor?.gst_treatment ?? billJson?.gst_treatment;
          const gstCategory =
            fetchedVendor?.gst_category ?? billJson?.gst_category;
          const docCurrency = String(billJson?.currency_code ?? "")
            .trim()
            .toUpperCase();

          if (
            !validationBaselineLockedRef.current &&
            linesForIgst.length > 0 &&
            shouldApplyIgst18ForReverseCharge({
              gstTreatment,
              gstCategory,
              companyCurrencyCode,
              documentCurrencyCode: docCurrency,
            })
          ) {
            let interTaxes = interStateTaxes;
            if (!interTaxes.length) {
              try {
                const response =
                  await taxRepository.fetchTaxSpecification("inter");
                if (response.success) {
                  interTaxes = response.data.taxes.filter(
                    (tax: TaxEntity) => tax.is_inactive === "0",
                  );
                  setInterStateTaxes(interTaxes);
                }
              } catch (error) {
                console.error(
                  "Failed to fetch inter-state taxes for IGST 18 apply:",
                  error,
                );
              }
            }

            const igst18 = findIgst18Tax(interTaxes);
            if (igst18) {
              const igstApplied = applyIgst18ToLineItems(linesForIgst, igst18);
              if (igstApplied !== linesForIgst) {
                postValidationLineItems = igstApplied;
                if (!validationBaselineLockedRef.current) {
                  validationBaselineLineItemsRef.current = igstApplied.map(
                    (li) => ({
                      ...li,
                    }),
                  );
                }
                setBillJson((prevBillJson) => {
                  if (!prevBillJson) return prevBillJson;
                  return {
                    ...prevBillJson,
                    line_items: igstApplied,
                  };
                });
                setLineItems([...igstApplied]);
              }
            }
          }
        }

        // Extract failed validation messages
        const failedMessages =
          validationSummary?.validation_details
            ?.filter((validation: any) => !validation.valid)
            ?.map((validation: any) => validation.message) || [];

        setFailedValidationMessages(failedMessages);

        // ── Per-line TDS from LLM classification service ──────────────────────
        const tdsClassificationResult = validationSummary?.tds_classification;
        // Commented out by Arkaan on 2026-03-24 (commit d3b59881 "removed per line tds addition").
        // This block auto-populated TDS values (tds_tax_id, tds_tax_name, tds_tax_percentage)
        // on each line item from the AI classification result. It was disabled because of a bug
        // at line `if (item.tds_tax_id === "") return item` which skips lines where tds_tax_id
        // is empty — but empty is exactly the case where TDS needs to be populated (inverted guard).
        // To re-enable: fix the guard to `if (item.tds_tax_id !== "") return item` (skip lines
        // that ALREADY have TDS set, not lines that need it), then uncomment this block.
        if (
          tdsClassificationResult?.details?.lines?.length > 0 &&
          billJson?.line_items
        ) {
          const classifiedLines: any[] = tdsClassificationResult.details.lines;
          setBillJson((prevBillJson) => {
            if (!prevBillJson?.line_items) return prevBillJson;
            const updatedLineItems = prevBillJson.line_items.map(
              (item, index: number) => {
                if (item.tds_tax_id) return item;
                const lineTds = classifiedLines.find(
                  (l: any) => l.index === index,
                );
                if (!lineTds || !lineTds.applicable) return item;
                return {
                  ...item,
                  tds_tax_id: item.tds_tax_id || lineTds.tds_tax_id,
                  tds_tax_name: item.tds_tax_name || lineTds.tds_tax_name,
                  tds_tax_percentage:
                    item.tds_tax_percentage || lineTds.tds_tax_percentage,
                };
              },
            );
            postValidationLineItems = updatedLineItems;
            if (!validationBaselineLockedRef.current) {
              validationBaselineLineItemsRef.current = updatedLineItems.map(
                (li) => ({
                  ...li,
                }),
              );
            }
            setLineItems([...updatedLineItems]);
            return { ...prevBillJson, line_items: updatedLineItems };
          });
          const anyApplicable = classifiedLines.some((l: any) => l.applicable);
          if (anyApplicable) setIsTDSApplicable(true);
        }
        // Fix 2026-03-24: Vendor-level TDS visibility.
        // The backend sets tds_applicable="yes" when the vendor has tds_applicable=Y
        // in Zoho, regardless of whether individual lines crossed the threshold.
        // This ensures the TDS dropdown is visible so the user can manually apply
        // TDS when needed (e.g. cumulative FY payments about to cross threshold).
        // Per-line auto-population above only fills values when applicable=true;
        // this check only controls column visibility, not values.
        if (tdsClassificationResult?.details?.tds_applicable === "yes") {
          setIsTDSApplicable(true);
        }
        // ── END per-line TDS block ────────────────────────────────────────────

        // Handle TDS validation response (vendor-level fallback)
        // Only applies if per-line tds_classification above didn't populate anything
        // Handle TDS validation response (vendor-level fallback)
        // Only applies if per-line tds_classification above didn't populate anything
        if (!tdsClassificationResult?.details?.lines?.length) {
          const tdsValidation = validationSummary.tds_check ?? undefined;
          if (
            tdsValidation &&
            tdsValidation.details?.tds_applicable === "yes"
          ) {
            setBillJson((prevBillJson) => {
              if (!prevBillJson?.line_items) return prevBillJson;
              const updatedLineItems = prevBillJson.line_items.map((item) => {
                // Populate TDS only when it's missing; keep user/system-set values intact.
                if (item.tds_tax_id) return item;
                return {
                  ...item,
                  tds_tax_id:
                    item.tds_tax_id || tdsValidation.details.tds_tax_id,
                  tds_tax_name:
                    item.tds_tax_name || tdsValidation.details.tds_tax_name,
                  tds_tax_percentage:
                    item.tds_tax_percentage ??
                    parseFloat(tdsValidation.details.tds_tax_percentage),
                };
              });
              postValidationLineItems = updatedLineItems;
              if (!validationBaselineLockedRef.current) {
                validationBaselineLineItemsRef.current = updatedLineItems.map(
                  (li) => ({
                    ...li,
                  }),
                );
              }
              setLineItems([...updatedLineItems]);
              return { ...prevBillJson, line_items: updatedLineItems };
            });
            setIsTDSApplicable(true);
          }
          // Intentionally NOT hiding TDS when tds_applicable === "no".
          // Product requirement: always show the TDS column / edit-line dropdown
          // (default "NO TDS") for every vendor so the accountant can still pick
          // a rate manually — even if the vendor master or bill validation says
          // TDS is not applicable. When validation returns applicable TDS above,
          // we still auto-populate those values onto the line items.
          // else if (tdsValidation?.details?.tds_applicable === "no") {
          //   setIsTDSApplicable(false);
          // }
        }

        // Handle GL Classification
        const glClassification = validationSummary?.gl_classification;
        if (
          glClassification &&
          glClassification.details?.lines &&
          billJson?.line_items
        ) {
          // Capture per-line gl_source_layer so the "Apply GL as default" modal
          // can later gate on "llm"-sourced items only.
          const sourceLayerMap: Record<number, string> = {};
          glClassification.details.lines.forEach((glLine: any) => {
            sourceLayerMap[glLine.index] = glLine.gl_source_layer || "";
          });
          setGlSourceLayerMap(sourceLayerMap);

          const jobid = billDocument?.jobid;
          if (!jobid) return;

          // Check if user has skipped this document
          const skipKey = `gl_modal_skipped_${jobid}`;
          const isSkipped = localStorage.getItem(skipKey) === "true";

          if (isSkipped) {
            return; // Don't show modal if skipped
          }

          // Filter line items that meet the criteria
          const eligibleLineItems: GLClassificationLineItem[] = [];

          glClassification.details.lines.forEach((glLine: any) => {
            const lineItemIndex = glLine.index;
            const currentLineItem = billJson.line_items[lineItemIndex];

            if (!currentLineItem) return;

            // Check conditions: gl_confidence > 0.85 AND gl_source_layer === 'knn' AND (account_name differs OR account_id differs)
            const meetsConfidence = glLine.gl_confidence > 0.85;
            const isKNN =
              glLine.gl_source_layer === "override" ||
              glLine.gl_source_layer === "knn";
            // const accountNameDiffers =
            //   currentLineItem.account_name !== glLine.account_name;
            const accountIdDiffers =
              currentLineItem.account_id !== glLine.account_id;

            if (meetsConfidence && isKNN && accountIdDiffers) {
              eligibleLineItems.push({
                index: lineItemIndex,
                description:
                  currentLineItem.description || currentLineItem.name || "",
                oldAccountId: currentLineItem.account_id || "",
                oldAccountName: currentLineItem.account_name || "",
                newAccountId: glLine.account_id,
                newAccountName: glLine.account_name,
                confidence: glLine.gl_confidence,
              });
            }
          });

          // Show modal if there are eligible line items
          if (eligibleLineItems.length > 0) {
            setGLClassificationLineItems(eligibleLineItems);
            setIsGLClassificationModalOpen(true);
          }
        }
      } finally {
        lineItemTrackingAfterValidationRef.current = true;
        validationBaselineLockedRef.current = true;
        seedTrackedUserLineItemChangesAfterValidation(
          postValidationLineItems ??
            billJsonRef.current?.line_items ??
            lineItems,
        );
      }
    },
    [
      billJson,
      lineItems,
      billDocument,
      loadTaxSpecification,
      seedTrackedUserLineItemChangesAfterValidation,
      fetchedVendor?.gst_treatment,
      fetchedVendor?.gst_category,
      companyCurrencyCode,
      interStateTaxes,
    ],
  );

  // Handle GL Classification Apply
  const handleGLClassificationApply = (
    selectedLineItems: GLClassificationLineItem[],
  ) => {
    if (!billJson) return;

    if (selectedLineItems.length === 0) {
      setIsGLClassificationModalOpen(false);
      setGLClassificationLineItems([]);
      return;
    }

    const updatedLineItems = [...billJson.line_items];
    const confirmedIndexes: number[] = [];

    selectedLineItems.forEach((item) => {
      if (updatedLineItems[item.index]) {
        updatedLineItems[item.index] = {
          ...updatedLineItems[item.index],
          account_id: item.newAccountId,
          account_name: item.newAccountName,
          gl_confidence: item.confidence,
        };
        confirmedIndexes.push(item.index);
      }
    });
    const updated = { ...billJson, line_items: updatedLineItems };
    setBillJson(updated);
    setLineItems(updatedLineItems);
    // Applying the previous GL change counts as the reviewer confirming these
    // lines' GL, so the inline Debit A/c picker stops showing "Select account"
    // for them (mirrors handleInlineGlSelect + handleSaveLineItem).
    setGlConfirmedLines((prev) => {
      const next = { ...prev };
      confirmedIndexes.forEach((idx) => {
        next[idx] = true;
      });
      return next;
    });
    setIsGLClassificationModalOpen(false);
    setGLClassificationLineItems([]);
  };

  // Handle GL Classification Skip
  const handleGLClassificationSkip = () => {
    const jobid = billDocument?.jobid;
    if (jobid) {
      const skipKey = `gl_modal_skipped_${jobid}`;
      localStorage.setItem(skipKey, "true");
    }
    setIsGLClassificationModalOpen(false);
    setGLClassificationLineItems([]);
  };

  // Validation function to check line items have tax information
  const validateLineItemsTaxInfo = (): {
    isValid: boolean;
    errorMessage?: string;
  } => {
    if (!lineItems || lineItems.length === 0) {
      return { isValid: true }; // No line items to validate
    }

    const invalidItems = lineItems.filter((item, index) => {
      // Check if either tax_id or tax_percentage is provided
      const hasTaxId = item.tax_id && item.tax_id !== "";
      const hasTaxPercentage =
        item.tax_percentage !== null && item.tax_percentage !== undefined;

      return !hasTaxId && !hasTaxPercentage;
    });

    if (invalidItems.length > 0) {
      const itemNumbers = invalidItems.map((_, index) => index + 1).join(", ");
      return {
        isValid: false,
        errorMessage: `Line items ${itemNumbers} are missing tax information. Please ensure each line item has either a Tax ID or Tax Percentage before approving.`,
      };
    }

    return { isValid: true };
  };

  /**
   * A line item's account name is considered manually edited when trackedChanges
   * has an entry with exactly the `Line Item N: Account Name` label. Edits to
   * other fields (quantity, rate, etc.) do not disqualify the line.
   */
  const isAccountNameEditedForLineItem = useCallback(
    (idx: number): boolean =>
      trackedChanges.some(
        (c) => c.field === `Line Item ${idx + 1}: Account Name`,
      ),
    [trackedChanges],
  );

  // --- Inline GL picker helpers (2026-06-26) ---
  // Ranked AI candidates for a line, by position (same data the modal uses,
  // billDoc.gl_line_suggestions keyed by line_index).
  const inlineGlSuggestionsForLine = useCallback(
    (idx: number): InlineGlSuggestion[] => {
      const map = (billDoc?.gl_line_suggestions as Record<string, any[]>) || {};
      const list = map[String(idx)];
      return Array.isArray(list) ? (list as InlineGlSuggestion[]) : [];
    },
    [billDoc],
  );

  // The empty inline picker is only shown when GL fields are actually editable
  // (normal review — not the read-only audit/posted states).
  const inlineGlPickerActive =
    !shouldDisableEditableFields && !isPostedToZoho && !isDocumentReadOnly;

  // --- Inline ITC picker helpers (#612) ---
  // itc_classification is computed only on full_validation (cost control). Any
  // later line edit — including picking an ITC token, which mutates billJson —
  // re-runs normal_validation, whose response omits itc_classification; the page
  // then replaces validationData with it, so the live recommendation vanishes and
  // the picker would otherwise fall back to eligible/"AI pick". Snapshot the
  // per-line recommendation the first time we see it (scoped to this jobid) so
  // the picker and the "Source: LLM · 85%" hint keep showing the real pick after
  // an edit.
  const itcRecoSnapshotRef = useRef<{
    jobid: string | null;
    byIndex: Record<number, any>;
  }>({ jobid: null, byIndex: {} });

  useEffect(() => {
    const lines = itcClassification?.details?.lines;
    if (!Array.isArray(lines) || lines.length === 0) return;
    const jobid = billDocument?.jobid ?? null;
    const prev = itcRecoSnapshotRef.current;
    const byIndex = prev.jobid === jobid ? { ...prev.byIndex } : {};
    lines.forEach((l: any) => {
      if (typeof l?.index === "number") byIndex[l.index] = l;
    });
    itcRecoSnapshotRef.current = { jobid, byIndex };
  }, [itcClassification, billDocument?.jobid]);

  // The engine recommendation for a line (from full_validation), by index. Falls
  // back to the jobid-scoped snapshot when a later normal_validation has wiped
  // itc_classification off validationData.
  const itcLineFor = useCallback(
    (idx: number): any => {
      const live = itcClassification?.details?.lines?.find(
        (l: any) => l.index === idx,
      );
      if (live) return live;
      const snap = itcRecoSnapshotRef.current;
      return snap.jobid === (billDocument?.jobid ?? null)
        ? snap.byIndex[idx]
        : undefined;
    },
    [itcClassification, billDocument?.jobid],
  );

  // Effective status = accountant override if present, else the line item's own
  // token (a re-opened bill), else the engine recommendation, else 'eligible'.
  const effectiveItcStatus = useCallback(
    (idx: number, item: any): string =>
      itcSelections[idx] ??
      (item?.itc_eligibility as string) ??
      itcLineFor(idx)?.itc_status ??
      "eligible",
    [itcSelections, itcLineFor],
  );

  // A line still needs review only until the accountant has explicitly picked.
  const itcNeedsReview = useCallback(
    (idx: number): boolean =>
      itcSelections[idx] === undefined && !!itcLineFor(idx)?.needs_review,
    [itcSelections, itcLineFor],
  );

  // Mirrors the Debit A/c "Select account" gate: an LLM-sourced recommendation
  // is shown as an empty "Select" box until the reviewer confirms it. kNN / rule
  // / reconcile sources are trusted enough to pre-select (no pick required), and
  // a re-opened bill that already carries a token counts as picked.
  const itcNeedsPick = useCallback(
    (idx: number, item: any): boolean => {
      if (itcSelections[idx] !== undefined || item?.itc_eligibility)
        return false;
      const rec = itcLineFor(idx);
      if (!rec) return false;
      // Require an explicit pick on any line that is a Sec 17(5) concern — a
      // deterministic catalogue hit, a recommended block, a use-dependent
      // review, or an unconfirmed LLM suggestion. These are exactly the lines
      // that raise the "ITC Check" Issues Found flag, so the picker must read as
      // "decide me" (empty Select box) rather than a committed-looking badge
      // until the accountant clicks. #612
      return (
        rec.source_layer === "llm" ||
        !!rec.section17_category ||
        rec.itc_status === "ineligible_section17" ||
        !!rec.needs_review
      );
    },
    [itcSelections, itcLineFor],
  );

  // Writing a token onto the line item is what feeds the Zoho write-back
  // (line_items[].itc_eligibility) and, on save, the classification capture.
  const handleItcSelect = useCallback(
    (idx: number, status: string) => {
      const updated = lineItems.map((li: any, i: number) =>
        i === idx ? { ...li, itc_eligibility: status } : li,
      );
      setLineItems(updated);
      setBillJson((prev: any) =>
        prev ? { ...prev, line_items: updated } : prev,
      );
      setItcSelections((prev) => ({ ...prev, [idx]: status }));
      setOpenInlineItcIndex(null);
    },
    [lineItems, setBillJson],
  );

  // Persist the accountant's confirmed per-line ITC decisions to the
  // classification store (#612). Fire-and-forget on approve — the kNN engine
  // learns from these confirmed rows. was_manually_corrected = the reviewer
  // chose a token different from the engine recommendation.
  const captureItcDecisions = useCallback(
    async (finalBill: any) => {
      const jobid = billDocument?.jobid;
      const vendorId = finalBill?.vendor_id;
      if (!jobid || !vendorId || !Array.isArray(finalBill?.line_items)) {
        return;
      }
      const lines = finalBill.line_items.map((item: any, idx: number) => {
        const rec = itcLineFor(idx);
        const status = effectiveItcStatus(idx, item);
        return {
          index: idx,
          description: item.description || item.name || "",
          hsn_or_sac: item.hsn_or_sac || "",
          itc_status: status,
          was_manually_corrected:
            rec && rec.itc_status && status !== rec.itc_status ? 1 : 0,
          source_layer: rec?.source_layer ?? null,
          section17_category: rec?.section17_category ?? null,
          reason: rec?.reason ?? null,
          confidence: rec?.confidence ?? null,
          llm_status: rec?.llm_status ?? null,
          knn_status: rec?.knn_status ?? null,
          line_amount: rec?.line_amount ?? null,
          tax_percentage: rec?.tax_percentage ?? null,
          tax_specification: rec?.tax_specification ?? null,
          cgst_amount: rec?.cgst_amount ?? null,
          sgst_amount: rec?.sgst_amount ?? null,
          igst_amount: rec?.igst_amount ?? null,
          cess_amount: rec?.cess_amount ?? null,
          tax_amount: rec?.tax_amount ?? null,
        };
      });
      try {
        await fetch(`${CORE_API_URL}/api_itc_capture.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            jobid: String(jobid),
            vendor_id: String(vendorId),
            vendor_name: finalBill.vendor_name || finalBill.trading_name || "",
            bill_date: finalBill.date || finalBill.bill_date || null,
            document_type: "bill",
            lines,
          }),
        });
      } catch (e) {
        // Non-fatal: capture is a learning side-channel, never block posting.
      }
    },
    [billDocument, itcLineFor, effectiveItcStatus],
  );

  // A line shows the empty picker when it's an LLM line the reviewer hasn't
  // picked yet and there are ranked candidates to choose from. "LLM line" uses
  // the SAME signal as the edit modal — the candidates' own source_layer
  // (gl_line_suggestions[0].source_layer), NOT the validation-API glSourceLayerMap
  // (which isn't repopulated when an already-validated bill is reopened) — so the
  // inline picker and the modal's "AI Suggested" gate never disagree.
  const lineNeedsGlPick = useCallback(
    (idx: number): boolean => {
      const sugg = inlineGlSuggestionsForLine(idx);
      return (
        sugg.length > 0 &&
        sugg[0]?.source_layer === "llm" &&
        !glConfirmedLines[idx]
      );
    },
    [glConfirmedLines, inlineGlSuggestionsForLine],
  );

  // For non-LLM lines that still carry the AI's top pick, expose the source
  // layer + its reason so the row can show "<source> · <reason>" under the GL
  // name (k-NN / override / classifier). Returns null for LLM lines (they use
  // the picker) and for lines the reviewer has overridden to a different GL.
  const inlineGlBadgeMeta = useCallback(
    (
      idx: number,
      lineItem: any,
    ): { label: string; reason: string; confidence: number | null } | null => {
      const top = inlineGlSuggestionsForLine(idx)[0];
      if (!top || top.source_layer === "llm") return null;
      if ((lineItem?.account_id || "") !== (top.account_id || "")) return null;
      const labelMap: Record<string, string> = {
        knn: "kNN",
        override: "Override",
        clf: "Model",
      };
      return {
        label: labelMap[top.source_layer || ""] || top.source_layer || "AI",
        reason: top.reason || "",
        confidence: top.confidence ?? null,
      };
    },
    [inlineGlSuggestionsForLine],
  );

  /**
   * Build the eligible list of line items for the "Apply this GL automatically
   * next time?" modal. A line item is eligible when it has a non-empty GL and
   * the user has not edited the account name.
   */
  const buildApplyGLAsDefaultItems =
    useCallback((): ApplyGLAsDefaultLineItem[] => {
      return lineItems
        .map((li, idx) => ({ li, idx }))
        .filter(
          ({ li, idx }) =>
            !!li.account_id &&
            !!li.account_name &&
            !isAccountNameEditedForLineItem(idx) &&
            (glSourceLayerMap[idx] === "llm" ||
              glSourceLayerMap[idx] === "knn"),
        )
        .map(({ li, idx }) => ({
          lineItemIndex: idx,
          description: li.description || li.name || "",
          accountId: li.account_id || "",
          accountName: li.account_name || "",
          accountCode: (li as any).account_code,
        }));
    }, [lineItems, isAccountNameEditedForLineItem, glSourceLayerMap]);

  const handleApplyGLAsDefaultSkip = () => {
    setIsApplyGLAsDefaultOpen(false);
    setApplyGLAsDefaultItems([]);
    handleDocumentApproval({ skipApplyGLPrompt: true });
  };

  const handleApplyGLAsDefaultSave = async () => {
    const itemsToSave = applyGLAsDefaultItems;
    if (itemsToSave.length === 0) {
      setIsApplyGLAsDefaultOpen(false);
      handleDocumentApproval({ skipApplyGLPrompt: true });
      return;
    }

    setIsApplyingGLDefaults(true);
    try {
      const requests: SemanticOverrideRequest[] = itemsToSave.map((item) => {
        const li = lineItems[item.lineItemIndex];
        return {
          line_item: {
            vendor: billJson?.vendor_name || "Unknown Vendor",
            vendor_id: billJson?.vendor_id || "",
            description: li?.description || "",
            product_description: li?.description || "",
            hsn: li?.hsn_or_sac || "",
            hsn_description: (li as any)?.hsn_description || "",
            customer: "",
            customer_id: "",
          },
          // gl_id: item.accountId,
          gl_id: item.accountName,
          similarity_threshold: 0.85,
          document_type: "bill",
          account_id: item.accountId,
          note: "Save GL as default for similar future bills",
        };
      });

      // Use Promise.all with per-request catch so a single failure does not
      // reject the whole batch - we still want to continue with approval.
      const results = await Promise.all(
        requests.map((r) =>
          createSemanticOverride(r).catch((err: unknown) => {
            console.error("Failed to save GL default:", err);
            return { success: false } as { success: false };
          }),
        ),
      );

      const failures = results.filter((r) => !r?.success).length;
      const total = results.length;
      if (failures === total) {
        showError("Could not save GL defaults. Continuing with approval.");
      } else if (failures > 0) {
        showWarning(
          `Saved ${total - failures} of ${total} GL defaults. Continuing with approval.`,
        );
      }
    } finally {
      setIsApplyingGLDefaults(false);
      setIsApplyGLAsDefaultOpen(false);
      setApplyGLAsDefaultItems([]);
      handleDocumentApproval({ skipApplyGLPrompt: true });
    }
  };

  const handleApproveClick = () => {
    // Block approval while the vendor-change pipeline is re-running
    if (isRecomputing) {
      return;
    }

    if (exchangeRateLoading) {
      showError("Wait for the exchange rate to finish loading.");
      return;
    }

    // Run validation first
    if (!validateBill()) {
      // ErrorBanner will display all validation errors
      return;
    }

    // Block approval until every LLM-classified line has an actively chosen GL.
    // Only enforced when the inline picker is actually shown (editable review);
    // in read-only/audit/posted states the GL is already set from a prior cycle,
    // so there is nothing for the reviewer to pick.
    if (inlineGlPickerActive) {
      const unpicked = lineItems
        .map((_: any, idx: number) => idx)
        .filter((idx: number) => lineNeedsGlPick(idx));
      if (unpicked.length > 0) {
        const nums = unpicked.map((i: number) => i + 1).join(", ");
        setApiErrors(
          new Error(
            `Select a GL (Debit A/c) for line item${
              unpicked.length > 1 ? "s" : ""
            } ${nums} before approving.`,
          ),
        );
        setOpenInlineGlIndex(unpicked[0]);
        return;
      }
    }

    // Block approval until every LLM-classified line has an actively chosen
    // ITC eligibility. Mirrors the Debit A/c (GL) gate above — same
    // unconfirmed-recommendation condition (itcNeedsPick), same "only when
    // the inline picker is actually shown" scoping.
    if (inlineGlPickerActive) {
      const itcUnpicked = lineItems
        .map((_: any, idx: number) => idx)
        .filter((idx: number) => itcNeedsPick(idx, lineItems[idx]));
      if (itcUnpicked.length > 0) {
        const nums = itcUnpicked.map((i: number) => i + 1).join(", ");
        setApiErrors(
          new Error(
            `Select an ITC eligibility for line item${
              itcUnpicked.length > 1 ? "s" : ""
            } ${nums} before approving.`,
          ),
        );
        setOpenInlineItcIndex(itcUnpicked[0]);
        return;
      }
    }

    // Check if there are any failed AI validations
    const hasFailedValidations =
      failedValidationMessages.length > 0 ||
      !validationPassed ||
      validationData?.validation_summary?.failed_validations > 0;

    if (hasFailedValidations && !isManualSource) {
      setIsValidationProgressModalOpen(true);
      return;
    }

    // If no validation failures, proceed with approval
    handleDocumentApproval();
  };

  const handleCloseValidationModal = () => {
    setIsValidationProgressModalOpen(false);
  };

  // New handler for continuing with approval despite validation failures
  const handleContinueApproval = () => {
    setIsValidationProgressModalOpen(false);
    // Proceed with approval despite validation failures
    handleDocumentApproval();
  };

  // Main approval workflow function
  const handleDocumentApproval = async (
    opts: {
      skipApplyGLPrompt?: boolean;
      skipReviewChangesModal?: boolean;
      approvalAudit?: ApprovalAuditPayload;
    } = {},
  ) => {
    if (!documentId || !billJson) {
      return;
    }

    // Offer to memorize untouched GLs as defaults for similar future bills.
    // The modal handlers re-call this function with skipApplyGLPrompt to avoid
    // re-prompting after the user chose "Not now" or "Save as default".
    if (!opts.skipApplyGLPrompt && !isPostedToZoho) {
      const eligible = buildApplyGLAsDefaultItems();
      if (eligible.length > 0) {
        setApplyGLAsDefaultItems(eligible);
        setIsApplyGLAsDefaultOpen(true);
        return;
      }
    }

    // CRITICAL: Don't allow approval if data is still loading
    if (isDataLoading) {
      setApiErrors(new Error("Data is still loading. Please wait."));
      return;
    }

    if (
      exchangeRateRequired &&
      isExchangeRateMissingForTallyPost(billJson.exchange_rate, {
        userSet: billJson.exchange_rate_user_set,
        isPosted: isPostedToZoho,
      })
    ) {
      setFieldErrors({
        exchange_rate:
          "Exchange rate is required for foreign currency documents when posting to Tally.",
      });
      return;
    }

    // Check permissions
    if (isPostedToZoho) {
      if (!canUpdateAfterPush) {
        setApiErrors(
          new Error(
            getUpdateDocumentAfterPushPermissionDeniedMessage(
              "bill",
              accountingBackend,
            ),
          ),
        );
        return;
      }
    } else {
      if (!canPushToZoho) {
        setApiErrors(
          new Error(getPushPermissionDeniedMessage("bills", accountingBackend)),
        );
        return;
      }
    }

    // Closed month: non-Admin/CFO cannot approve/update directly; show confirm modal to submit for review
    if (isMonthClosed && !isAdminOrCFO) {
      setPendingClosedMonthSource("approve");
      setShowClosedMonthConfirmModal(true);
      return;
    }

    // PRIORITY 0: Manual draft not posted + create requires review → create review flow (incl. after rejection resubmission)
    if (
      !isPostedToZoho &&
      isManualSource &&
      canCreate &&
      needsReviewForCreate
    ) {
      const createChanges: ChangeDetails = {
        reason: "Requesting permission to create bill",
        field_changes: trackedChanges.length > 0 ? trackedChanges : undefined,
        raw_json: withVendorGstInReviewRawJson(
          billJsonRef.current || billJson,
          fetchedVendor,
        ),
        original_json: originalBillJson ?? undefined,
        action_types: ["create"],
      };
      console.log("createChanges", createChanges);
      setPendingReviewAction({
        action: () => handleDocumentApproval({ skipApplyGLPrompt: true }),
        changeDetails: createChanges,
        disableSubmit: false,
        actionCode: ACTION_CODES.CREATE,
      });
      setIsReviewModalOpen(true);
      return;
    }

    // PRIORITY 1: Check if there's a pending GL override that needs review
    if (
      pendingGLOverrideReview.length > 0 &&
      needsReviewForOverrideGLPermanent
    ) {
      // Check if there's a pending review (block if exists)
      if (review?.status === "pending") {
        setApiErrors(
          new Error(
            "A review request is already pending for this document. Please wait for it to be reviewed.",
          ),
        );
        return;
      }

      // Collect all GL overrides that need review
      const glOverridesNeedingReview = pendingGLOverrideReview.filter(
        (gl) => gl.changeScope === "always",
      );

      if (glOverridesNeedingReview.length > 0) {
        // Build field_changes for all GL overrides
        const glFieldChanges = glOverridesNeedingReview.map((gl) => ({
          field: `Line Item ${gl.lineItemIndex + 1}: Account Name`,
          old: getAccountNameLeaf(gl.glData.old_account_name),
          new: getAccountNameLeaf(gl.glData.new_account_name),
        }));

        // Store all GL override data in array format
        const lineItemGLDataArray = glOverridesNeedingReview.map((gl) => ({
          line_item_index: gl.lineItemIndex,
          old_account_id: gl.glData.old_account_id,
          old_account_name: gl.glData.old_account_name,
          new_account_id: gl.glData.new_account_id,
          new_account_name: gl.glData.new_account_name,
          change_scope: gl.changeScope,
          reason: gl.reason,
          override_data: gl.overrideData,
        }));

        // Send review as override_gl_permanent
        const glChanges: ChangeDetails = {
          reason: glOverridesNeedingReview[0].reason, // Use first reason as primary
          old_value: glOverridesNeedingReview[0].glData.old_account_name,
          new_value: glOverridesNeedingReview[0].glData.new_account_name,
          // Include GL details in field_changes for display
          field_changes: glFieldChanges,
          // Store line item index and GL data for applying changes
          line_item_index: glOverridesNeedingReview[0].lineItemIndex, // Keep for backward compatibility
          line_item_gl_data: lineItemGLDataArray as any, // Store as array
          change_scope: glOverridesNeedingReview[0].changeScope, // Store whether it's "always" or "once"
          // Include raw JSON with ALL changes (GL + other changes)
          raw_json: withVendorGstInReviewRawJson(
            billJsonRef.current || billJson,
            fetchedVendor,
          ),
          original_json: originalBillJson ?? undefined,
          validation_snapshot: buildValidationSnapshotForReview(
            validationBaselineLineItemsRef.current,
            billJsonRef.current || billJson,
          ),
          // Set action types - always include override_gl_permanent
          action_types: ["override_gl_permanent"],
          override_data: glOverridesNeedingReview[0].overrideData, // Use first one for backward compatibility
        };

        // Check if push_to_zoho/update_after_push also needs review
        const needsOtherReview = isPostedToZoho
          ? needsReviewForUpdateAfterPush
          : needsReviewForPushToZoho;

        // Check if edit also needs review (only for non-posted documents)
        // BUT only if push_to_zoho does NOT need review (push_to_zoho has priority)
        const needsEditReview =
          !isPostedToZoho && needsReviewForEdit && !needsReviewForPushToZoho;

        // Build action_types array
        const actionTypes: string[] = ["override_gl_permanent"];

        if (needsOtherReview) {
          if (isPostedToZoho) {
            actionTypes.push("update_after_push");
          } else {
            actionTypes.push("push_to_zoho");
          }
        } else if (needsEditReview) {
          // Only add edit if push_to_zoho doesn't need review
          actionTypes.push("edit");
        }

        glChanges.action_types = actionTypes;

        // If other action also needs review OR edit needs review, merge tracked changes
        if (needsOtherReview || needsEditReview) {
          // Merge other tracked changes into field_changes if they exist
          // This ensures the reviewer sees both GL overrides and other changes
          // BUT filter out GL override changes (Line Item X: Account Name) since we already added them explicitly
          if (trackedChanges.length > 0) {
            // Get all line item numbers that have GL overrides
            const glOverrideLineItemNumbers = glOverridesNeedingReview.map(
              (gl) => gl.lineItemIndex + 1,
            );
            const glOverrideFieldPatterns = glOverrideLineItemNumbers.map(
              (num) => `Line Item ${num}: Account Name`,
            );

            // Filter out all GL override changes from trackedChanges to prevent duplicates
            const otherTrackedChanges = trackedChanges.filter(
              (change) => !glOverrideFieldPatterns.includes(change.field),
            );

            glChanges.field_changes = [
              ...(glChanges.field_changes || []),
              ...otherTrackedChanges,
            ];
          }
        }

        setPendingReviewAction({
          action: () => {
            // After review is approved, proceed with approval
            handleDocumentApproval({ skipApplyGLPrompt: true });
          },
          changeDetails: glChanges,
          disableSubmit: false,
        });
        setIsReviewModalOpen(true);
        return;
      }
    }

    // PRIORITY 2: Check if push_to_zoho/update_after_push needs review
    const needsReviewForPush = isPostedToZoho
      ? needsReviewForUpdateAfterPush
      : needsReviewForPushToZoho;

    if (needsReviewForPush) {
      // Check if there are tracked changes
      if (trackedChanges.length === 0 && isPostedToZoho) {
        // No changes - show modal but disable submit
        const allChanges: ChangeDetails = {
          reason:
            "No changes detected. Please make changes to the document before requesting a review.",
          field_changes: [],
          raw_json: withVendorGstInReviewRawJson(
            billJsonRef.current || billJson,
            fetchedVendor,
          ),
          action_types: [isPostedToZoho ? "update_after_push" : "push_to_zoho"],
        };

        setPendingReviewAction({
          action: () => {
            // Do nothing - submit will be disabled
          },
          changeDetails: allChanges,
          disableSubmit: true,
        });
        setIsReviewModalOpen(true);
        return;
      }

      // There are changes - show review modal with changes
      const companyDataForReview = getCompany();
      const currentBillWithDestination = applyExchangeRateForAccountingPayload(
        {
          ...billJson,
          destination_of_supply: companyDataForReview?.state
            ? getStateAbbreviation(companyDataForReview.state)
            : (billJson?.destination_of_supply ?? ""),
        },
        accountingBackend,
        exchangeRatePostContext,
      );
      let rawJsonForReview: any = { documentId, ...currentBillWithDestination };
      rawJsonForReview = withVendorGstInReviewRawJson(
        rawJsonForReview,
        fetchedVendor,
      );
      const allChanges: ChangeDetails = {
        reason: isPostedToZoho
          ? getReviewRequestUpdateAfterPostReason("bill", accountingBackend)
          : getReviewRequestPushReason("bill", accountingBackend),
        field_changes: trackedChanges.length > 0 ? trackedChanges : undefined,
        raw_json: rawJsonForReview,
        original_json: isPostedToZoho
          ? (originalBillJson ?? undefined)
          : undefined,
        validation_snapshot: buildValidationSnapshotForReview(
          validationBaselineLineItemsRef.current,
          billJsonRef.current || billJson,
        ),
        action_types: [isPostedToZoho ? "update_after_push" : "push_to_zoho"],
      };

      setPendingReviewAction({
        action: () => {
          // After review is approved, proceed with approval
          handleDocumentApproval({ skipApplyGLPrompt: true });
        },
        changeDetails: allChanges,
        disableSubmit: false,
      });
      setIsReviewModalOpen(true);
      return;
    }

    // PRIORITY 3: Check if edit needs review (only if NOT posted AND user made changes)
    // Only check if push_to_zoho doesn't require review AND no GL override needs review
    if (
      !isPostedToZoho &&
      trackedChanges.length > 0 &&
      needsReviewForEdit &&
      !needsReviewForPushToZoho && // Only if push_to_zoho doesn't need review
      !(pendingGLOverrideReview.length > 0 && needsReviewForOverrideGLPermanent) // Only if GL override doesn't need review
    ) {
      // Create review request with "edit" action (no GL override, already handled in PRIORITY 1)
      // Include original_json so review modal and approval panels show "Document (before changes)" + "Changes Requested" like update_after_push
      const editChanges: ChangeDetails = {
        reason: "Requesting permission to edit bill",
        field_changes: trackedChanges,
        raw_json: withVendorGstInReviewRawJson(
          billJsonRef.current || billJson,
          fetchedVendor,
        ),
        original_json: originalBillJson ?? undefined,
        validation_snapshot: buildValidationSnapshotForReview(
          validationBaselineLineItemsRef.current,
          billJsonRef.current || billJson,
        ),
        action_types: ["edit"],
      };

      setPendingReviewAction({
        action: () => handleDocumentApproval({ skipApplyGLPrompt: true }), // Retry after approval
        changeDetails: editChanges,
        disableSubmit: false,
      });
      setIsReviewModalOpen(true);
      return;
    }

    if (!opts.skipReviewChangesModal) {
      const hasDirectApproveChanges =
        trackedChanges.length > 0 || pendingGLOverrideReview.length > 0;

      if (hasDirectApproveChanges) {
        const directApproveChanges: ChangeDetails = {
          field_changes: trackedChanges.length > 0 ? trackedChanges : undefined,
          raw_json: withVendorGstInReviewRawJson(
            billJsonRef.current || billJson,
            fetchedVendor,
          ),
          original_json: originalBillJson ?? undefined,
          validation_snapshot: buildValidationSnapshotForReview(
            validationBaselineLineItemsRef.current,
            billJsonRef.current || billJson,
          ),
          action_types: [isPostedToZoho ? "update_after_push" : "push_to_zoho"],
        };

        setPendingReviewAction({
          requiresReview: false,
          action: () =>
            handleDocumentApproval({
              skipApplyGLPrompt: true,
              skipReviewChangesModal: true,
            }),
          changeDetails: directApproveChanges,
          disableSubmit: false,
          actionCode: isPostedToZoho
            ? ACTION_CODES.UPDATE_AFTER_PUSH
            : ACTION_CODES.PUSH_TO_ZOHO,
        });
        setIsReviewModalOpen(true);
        return;
      }
    }

    try {
      setIsApprovalLoading(true);

      // Get company data to set destination_of_supply
      const companyData = getCompany();
      const companyGstEnabled = Number(companyData?.companyGstEnabled ?? 0);
      const billDataWithDestination = applyExchangeRateForAccountingPayload(
        {
          ...billJson,
          destination_of_supply: companyData?.state
            ? getStateAbbreviation(companyData.state)
            : "",
        } as BillDataEntity,
        accountingBackend,
        exchangeRatePostContext,
      );

      // #612: stamp each line with its confirmed ITC token (accountant override
      // if any, else the engine recommendation) so it reaches Zoho
      // (line_items[].itc_eligibility), then capture the confirmed decisions for
      // the kNN store. Computed here — not written to bill state — so a load
      // never silently mutates the bill.
      if (isGstEnabled && Array.isArray(billDataWithDestination.line_items)) {
        billDataWithDestination.line_items =
          billDataWithDestination.line_items.map((item: any, idx: number) => ({
            ...item,
            itc_eligibility: effectiveItcStatus(idx, item),
          }));
        captureItcDecisions(billDataWithDestination);
      }

      // Manual draft not yet pushed: use push-draft endpoint (no PDF required)
      if (!isPostedToZoho && isManualSource && documentId) {
        if (!canPushToZoho) {
          throw new Error(
            getPushPermissionDeniedMessage("bills", accountingBackend),
          );
        }
        const built = buildManualBillJson(
          billDataWithDestination,
          billDataWithDestination.line_items ?? [],
          {
            currencies: [],
            companyGstEnabled,
            exchangeRateInput: showExchangeRateField
              ? String(billDataWithDestination.exchange_rate ?? "")
              : "1",
          },
        );
        if (!built.success) {
          throw new Error(Object.values(built.errors).join(" "));
        }
        const canonicalBillJson = built.data;
        await pushManualBillDraft(
          parseInt(documentId),
          companyGstEnabled,
          canonicalBillJson,
          opts.approvalAudit,
        );
        await processBillJson(canonicalBillJson);
        showSuccess("Bill created successfully");
        sessionStorage.removeItem("autoSelectVendor");
        navigate("/dashboard/bills");
        return;
      }

      // Fetch fresh signed URL for PDF attachment (upload flow only)
      let pdfUrl = signedUrl;
      if (documentId && !isPostedToZoho && !isManualSource) {
        try {
          const signedUrlResponse = await getSignedUrlForDocument({
            documentId: parseInt(documentId),
            documentType: "bill",
            expiration: 3600,
          });
          if (signedUrlResponse.success && signedUrlResponse.data.signedUrl) {
            pdfUrl = signedUrlResponse.data.signedUrl;
          }
        } catch (error) {}
      }

      if (!isPostedToZoho && !pdfUrl) {
        throw new Error("PDF URL is required for bill creation");
      }

      // Clear tax_id from line items if vendor has business_none or unregistered
      const isBusinessNone =
        billDataWithDestination.gst_treatment?.toLowerCase() ===
        "business_none";
      const isUnregistered =
        billDataWithDestination.gst_category?.toLowerCase() === "unregistered";

      if (
        (isBusinessNone || isUnregistered) &&
        billDataWithDestination.line_items?.length
      ) {
        billDataWithDestination.line_items =
          billDataWithDestination.line_items.map((item) => ({
            ...item,
            tax_id: "",
            tax_percentage: 0,
            tax_amount: 0,
          }));

        // Recalculate totals after clearing tax
        const newSubTotal = billDataWithDestination.line_items.reduce(
          (sum, item) => sum + (item.rate || 0) * (item.quantity || 0),
          0,
        );
        const newTaxTotal = 0; // No tax for business_none or unregistered
        const newTotal =
          newSubTotal + (billDataWithDestination.discount || 0) + newTaxTotal;

        billDataWithDestination.sub_total = newSubTotal;
        billDataWithDestination.tax_total = newTaxTotal;
        billDataWithDestination.total = newTotal;
      }

      // Step 1: Create or Update Zoho bill based on edit mode
      if (isPostedToZoho) {
        // Send full bill state to backend (Option 1: no delta); backend uses it as source of truth
        await updateZohoBill(
          documentId,
          billDataWithDestination,
          opts.approvalAudit,
        );
      } else {
        // Check permission before creating in Zoho
        if (!canPushToZoho) {
          throw new Error(
            getPushPermissionDeniedMessage("bills", accountingBackend),
          );
        }
        // Error will be thrown automatically if it fails
        await createZohoBill(
          documentId,
          billDataWithDestination,
          pdfUrl!,
          opts.approvalAudit,
        );
      }

      // Step 2: Push to Applied Table
      await processBillJson(billDataWithDestination);

      const successMessage = isPostedToZoho
        ? "Bill updated successfully"
        : "Bill created successfully";
      showSuccess(successMessage);

      // Clear sessionStorage before navigating away
      sessionStorage.removeItem("autoSelectVendor");

      // Step 4: Navigate back to bills
      navigate("/dashboard/bills");
    } catch (error) {
      console.error("Error during document approval:", error);
      setApiErrors(error);
    } finally {
      setIsApprovalLoading(false);
    }
  };

  // Handle reject bill (direct or open review modal if review required)
  const handleRejectBill = async () => {
    if (!documentId) {
      console.error("Missing documentId for rejection");
      return;
    }

    // If user has permission but action requires review, open ReviewRequestModal
    if (canReject && needsReviewForReject) {
      setPendingReviewAction({
        action: () => {},
        actionCode: ACTION_CODES.REJECT,
        changeDetails: {
          action_types: ["reject"],
          raw_json:
            withVendorGstInReviewRawJson(
              billJson ?? undefined,
              fetchedVendor,
            ) ?? undefined,
        },
        disableSubmit: false,
      });
      setIsReviewModalOpen(true);
      return;
    }

    try {
      setIsApprovalLoading(true);

      const result = await rejectBill(parseInt(documentId));
      if (!result) {
        throw new Error("Failed to reject bill");
      }

      // Show success message
      showSuccess("Bill rejected successfully");

      // Clear sessionStorage before navigating away
      sessionStorage.removeItem("autoSelectVendor");

      // Navigate back to bills
      navigate("/dashboard/bills");
    } catch (error) {
      console.error("Error rejecting bill:", error);
      setApiErrors(error);
    } finally {
      setIsApprovalLoading(false);
    }
  };

  // Handle mark as duplicate (direct or open review modal if review required)
  const handleMarkAsDuplicate = async () => {
    if (!documentId) {
      console.error("Missing documentId for marking as duplicate");
      return;
    }

    // If user has permission but action requires review, open ReviewRequestModal
    if (canMarkAsDuplicate && needsReviewForDuplicate) {
      setPendingReviewAction({
        action: () => {},
        actionCode: ACTION_CODES.DUPLICATE,
        changeDetails: {
          action_types: ["duplicate"],
          raw_json:
            withVendorGstInReviewRawJson(
              billJson ?? undefined,
              fetchedVendor,
            ) ?? undefined,
        },
        disableSubmit: false,
      });
      setIsReviewModalOpen(true);
      return;
    }

    try {
      setIsApprovalLoading(true);

      const result = await markAsDuplicate(parseInt(documentId));
      if (!result) {
        throw new Error("Failed to mark bill as duplicate");
      }

      // Show success message
      showSuccess("Bill marked as duplicate successfully");

      // Clear sessionStorage before navigating away
      sessionStorage.removeItem("autoSelectVendor");

      // Navigate back to bills
      navigate("/dashboard/bills");
    } catch (error) {
      console.error("Error marking bill as duplicate:", error);
      setApiErrors(error);
    } finally {
      setIsApprovalLoading(false);
    }
  };

  // Handle delete (posted bill only): open review modal if required, else call API
  const handleDeleteBill = async () => {
    if (!documentId) return;
    if (
      needsReviewForDelete &&
      !hasApprovedReview(ACTION_CODES.DELETE_AFTER_PUSH_TO_ZOHO)
    ) {
      setPendingReviewAction({
        action: () => {},
        actionCode: ACTION_CODES.DELETE_AFTER_PUSH_TO_ZOHO,
        changeDetails: {
          action_types: ["delete_after_push_to_zoho"],
          raw_json:
            withVendorGstInReviewRawJson(
              billJson ?? undefined,
              fetchedVendor,
            ) ?? undefined,
        },
        disableSubmit: false,
      });
      setIsReviewModalOpen(true);
      return;
    }
    try {
      setIsApprovalLoading(true);
      const ok = await deletePostedBill(parseInt(documentId));
      if (!ok) throw new Error("Failed to delete bill");
      showSuccess(
        getDeletedFromBackendSuccessMessage("Bill", accountingBackend),
      );
      sessionStorage.removeItem("autoSelectVendor");
      navigate("/dashboard/bills");
    } catch (error) {
      console.error("Error deleting bill:", error);
      setApiErrors(error);
    } finally {
      setIsApprovalLoading(false);
    }
  };

  // Note: Query creation is now handled by QueryRaiseModal with clean architecture hooks
  // onOpenChange callback will trigger refetchQueries automatically

  //   const handleSearchAddParty = () => {
  //     // This would typically open a modal or navigate to party search
  //     setPartyMatched(true);
  //   };

  // Line item handlers
  const handleEditLineItem = (index: number) => {
    if (aiDetectionLoading) {
      showWarning("Line items cannot be edited while validation is running.");
      return;
    }
    setEditingLineItemIndex(index);
    const lineItemData = { ...lineItems[index] };

    // Store the ORIGINAL account name and id BEFORE any modifications
    setOriginalAccountName(lineItemData.account_name || "");
    setOriginalAccountId(lineItemData.account_id || "");

    // Ensure tax_percentage is set with a default value if not present
    if (
      lineItemData.tax_percentage === undefined ||
      lineItemData.tax_percentage === null
    ) {
      lineItemData.tax_percentage = 0; // Default to 0%
    }

    // If tax_id is not set (empty only) and tax_percentage is available and > 0, try to find matching tax
    if (
      (!lineItemData.tax_id || lineItemData.tax_id === "") &&
      lineItemData.tax_percentage !== undefined &&
      lineItemData.tax_percentage !== null &&
      lineItemData.tax_percentage > 0
    ) {
      const matchingTax = taxes.find(
        (tax) => parseFloat(tax.tax_percentage) === lineItemData.tax_percentage,
      );
      if (matchingTax) {
        lineItemData.tax_id = matchingTax.tax_id;
      }
    }

    // Ensure TDS fields are preserved (handle null/undefined)
    if (
      lineItemData.tds_tax_id === null ||
      lineItemData.tds_tax_id === undefined
    ) {
      lineItemData.tds_tax_id = "";
    }
    if (
      lineItemData.tds_tax_name === null ||
      lineItemData.tds_tax_name === undefined
    ) {
      lineItemData.tds_tax_name = "";
    }
    if (
      lineItemData.tds_tax_percentage === null ||
      lineItemData.tds_tax_percentage === undefined
    ) {
      lineItemData.tds_tax_percentage = undefined;
    }

    // Note: Account verification is now handled in EditLineItemForBillsComponent

    setEditingLineItemData(lineItemData);
    setIsEditLineItemModalOpen(true);
  };

  const handleDeleteLineItem = (index: number) => {
    if (aiDetectionLoading) {
      showWarning("Line items cannot be deleted while validation is running.");
      return;
    }
    // Check delete permission

    const updatedLineItems = lineItems.filter((_, i) => i !== index);
    setLineItems(updatedLineItems);
    // Update billJson with the new line items and recalculated totals
    updateLineItems(updatedLineItems);
  };

  const handleAddLineItem = () => {
    if (aiDetectionLoading) {
      showWarning("Line items cannot be added while validation is running.");
      return;
    }
    // Create empty line item template with all required fields
    const newLineItem: LineItemEntity = {
      name: "",
      description: "",
      quantity: 0,
      rate: 0,
      unit: "" as any,
      hsn_or_sac: "",
      hsn_description: "",
      sku: "",
      tax_id: "",
      tax_name: "",
      tax_percentage: 0,
      tax_amount: 0,
      account_id: "",
      account_name: "",
      item_total: 0,
      item_total_inclusive_of_tax: 0,
      gl_confidence: 0,
      item_order: lineItems.length + 1,
      is_billable: true,
      tds_tax_id: "",
      tds_tax_name: "",
      tds_tax_percentage: undefined,
    };

    setEditingLineItemIndex(-1); // Use -1 to indicate new item
    setEditingLineItemData(newLineItem);
    setOriginalAccountName(""); // No original since it's new
    setOriginalAccountId(""); // No original since it's new
    setIsEditLineItemModalOpen(true);
  };

  const handleSaveLineItem = () => {
    if (aiDetectionLoading) {
      showWarning("Line items cannot be saved while validation is running.");
      return;
    }
    if (editingLineItemData) {
      // Check if account_name was changed from original
      const accountNameChanged =
        editingLineItemIndex !== -1 &&
        editingLineItemIndex !== null &&
        originalAccountId !== "" &&
        editingLineItemData.account_id !== originalAccountId;

      // If account_name was changed, set gl_confidence to 0
      const lineItemToSave = accountNameChanged
        ? { ...editingLineItemData, gl_confidence: 0 }
        : editingLineItemData;

      if (editingLineItemIndex === -1) {
        // Adding new item
        const updatedLineItems = [...lineItems, lineItemToSave];
        setLineItems(updatedLineItems);
        updateLineItems(updatedLineItems);
        showSuccess("Line item added successfully");
      } else if (editingLineItemIndex !== null) {
        // Editing existing item
        const updatedLineItems = [...lineItems];
        updatedLineItems[editingLineItemIndex] = lineItemToSave;
        setLineItems(updatedLineItems);
        updateLineItems(updatedLineItems);
        // A modal save also counts as the reviewer confirming this line's GL,
        // so the inline empty picker (if LLM) becomes a normal badge.
        setGlConfirmedLines((p) => ({ ...p, [editingLineItemIndex]: true }));
        showSuccess("Line item updated successfully");
      }

      // Close modal and reset
      setIsEditLineItemModalOpen(false);
      setEditingLineItemIndex(null);
      setEditingLineItemData(null);

      // Show Update GL Dialog if:
      // 1. Account name was changed
      // 2. There are more than 1 line item (check after update)
      // 3. This was an existing item (not a new one)
      // const finalLineItemsCount =
      //   editingLineItemIndex === -1 ? lineItems.length + 1 : lineItems.length;
      // if (
      //   accountNameChanged &&
      //   finalLineItemsCount > 1 &&
      //   editingLineItemIndex !== -1 &&
      //   editingLineItemIndex !== null
      // ) {
      //   setPendingGLUpdate({
      //     changedLineItemIndex: editingLineItemIndex,
      //     oldAccountName: originalAccountName,
      //     newAccountName: editingLineItemData.account_name || "",
      //     newAccountId: editingLineItemData.account_id || "",
      //   });
      //   setIsUpdateGLDialogOpen(true);
      // }
    }
  };

  // Inline GL pick from the ranked AI list. Writes account_id + account_name via
  // the SAME updateLineItems path the edit modal uses (so change-tracking,
  // "Apply GL as default", capture and posting all see it identically), then
  // marks the line confirmed so the empty box becomes a normal badge.
  const handleInlineGlSelect = (
    index: number,
    suggestion: InlineGlSuggestion,
  ) => {
    if (aiDetectionLoading) {
      showWarning("Line items cannot be edited while validation is running.");
      return;
    }
    const updatedLineItems = [...lineItems];
    const prev = updatedLineItems[index];
    if (!prev) return;
    const accountChanged =
      (prev.account_id || "") !== (suggestion.account_id || "");
    updatedLineItems[index] = {
      ...prev,
      account_id: suggestion.account_id,
      account_name: suggestion.gl_name,
      gl_confidence: accountChanged ? 0 : prev.gl_confidence,
    };
    setLineItems(updatedLineItems);
    updateLineItems(updatedLineItems);
    setGlConfirmedLines((p) => ({ ...p, [index]: true }));
    setOpenInlineGlIndex(null);
  };

  const handleCancelEdit = () => {
    setIsEditLineItemModalOpen(false);
    setEditingLineItemIndex(null);
    setEditingLineItemData(null);
  };

  // Handle applying GL to selected line items
  const handleApplyGLToSelected = (selectedIndices: number[]) => {
    if (!pendingGLUpdate) return;

    const updatedLineItems = [...lineItems];
    selectedIndices.forEach((index) => {
      if (updatedLineItems[index]) {
        updatedLineItems[index] = {
          ...updatedLineItems[index],
          account_id: pendingGLUpdate.newAccountId,
          account_name: pendingGLUpdate.newAccountName,
          gl_confidence: 0, // Mark as manually edited
        };
      }
    });

    setLineItems(updatedLineItems);
    updateLineItems(updatedLineItems);
    showSuccess(
      `GL updated for ${selectedIndices.length} line item${
        selectedIndices.length > 1 ? "s" : ""
      }`,
    );

    setIsUpdateGLDialogOpen(false);
    setPendingGLUpdate(null);
  };

  // Handle skipping GL update
  const handleSkipGLUpdate = () => {
    setIsUpdateGLDialogOpen(false);
    setPendingGLUpdate(null);
  };

  // Handle line item data changes from the component
  const handleLineItemDataChange = (newData: any) => {
    setEditingLineItemData(newData);
  };

  // Handler functions for all sections
  const handleBasicInfoSave = (newData: any) => {
    // Update billJson with basic info changes
    if (newData.invoiceNumber !== undefined) {
      updateBillJson({ bill_number: newData.invoiceNumber });
    }
    if (newData.currency) {
      const updates: any = {
        currency_code: newData.currency,
      };
      // Also update currency_id and currency_symbol if provided
      if (newData.currency_id) {
        updates.currency_id = newData.currency_id;
      }
      if (newData.currency_symbol) {
        updates.currency_symbol = newData.currency_symbol;
      }
      updateBillJson(updates);
    }
    if (showExchangeRateField) {
      const trimmed = String(newData.exchange_rate ?? "").trim();
      if (trimmed === "") {
        updateBillJson({
          exchange_rate: null,
          exchange_rate_user_set: false,
        } as Partial<BillDataEntity> & {
          exchange_rate: null;
          exchange_rate_user_set: false;
        });
      } else {
        const n = parseFloat(trimmed);
        if (Number.isFinite(n) && n > 0) {
          updateBillJson({
            exchange_rate: n,
            exchange_rate_user_set: true,
          });
        }
      }
    }
  };

  const handleDatesInfoSave = (newData: any) => {
    // Update billJson with date changes
    if (newData.documentDate !== undefined) {
      updateBillJson({
        date: newData.documentDate,
        invoice_date: newData.documentDate,
      });
    }
    if (newData.dueDate !== undefined) {
      updateBillJson({ due_date: newData.dueDate });
    }
  };

  const handleTaxInfoSave = (newData: any) => {
    // Update billJson with tax info changes
    if (newData.vendorGstin !== undefined) {
      updateBillJson({
        vendor_gstin: newData.vendorGstin,
        gst_no: newData.vendorGstin,
      });
    }
    if (newData.customerGstin !== undefined) {
      updateBillJson({ customer_gstin: newData.customerGstin });
    }
  };

  const handleDocumentMetadataSave = (newData: any) => {
    // Update billJson with document metadata changes
    // This would depend on what fields are available in the metadata
  };

  const handleAmountSummarySave = (newData: any) => {
    // Update billJson with amount changes
    // Note: Discount is already handled via onDiscountChange callback
    const parseAmountField = (value: string | undefined) => {
      const trimmed = String(value ?? "").trim();
      if (!trimmed) return 0;
      const parsed = parseFloat(trimmed.replace(/[^0-9.-]/g, ""));
      return Number.isFinite(parsed) ? parsed : 0;
    };
    if (newData.subtotal !== undefined) {
      updateBillJson({ sub_total: parseAmountField(newData.subtotal) });
    }
    if (newData.gstValue !== undefined) {
      updateBillJson({ tax_total: parseAmountField(newData.gstValue) });
    }
    if (newData.totalAmount !== undefined) {
      updateBillJson({ total: parseAmountField(newData.totalAmount) });
    }
  };

  // Show loader while fetching all required data (permissions + document + review statuses)
  if (isDataLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 py-8 ">
          <div className="flex w-full items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
              <span className="text-heading-2 font-heading-2 text-default-font">
                Loading Bill Data...
              </span>
              <span className="text-body font-body text-subtext-color">
                Please wait while we fetch permissions, document details, and
                review statuses
              </span>
            </div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  // Show error if data loading failed
  if (reviewDataError) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 py-8 ">
          <div className="flex w-full items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <IconWithBackground
                variant="error"
                icon={<FeatherAlertTriangle />}
              />
              <span className="text-heading-2 font-heading-2 text-default-font">
                Failed to Load Data
              </span>
              <span className="text-body font-body text-subtext-color text-center max-w-md">
                {reviewDataError.message ||
                  "Failed to load bill data. Please try again."}
              </span>
              <Button onClick={() => navigate(-1)}>Go Back</Button>
            </div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  // Check if billJson data is available
  if (!billJson) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 py-8 ">
          <div className="flex w-full items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <IconWithBackground
                variant="error"
                icon={<FeatherAlertTriangle />}
              />
              <span className="text-heading-2 font-heading-2 text-default-font">
                No Document Data Available
              </span>
              <span className="text-body font-body text-subtext-color text-center max-w-md">
                The document data could not be loaded. Please go back and try
                again.
              </span>
              <Button onClick={() => navigate(-1)}>Go Back</Button>
            </div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start bg-default-background">
        <div className="flex w-full flex-wrap items-start justify-between border-b border-solid border-neutral-border px-6 py-6">
          <div className="flex items-center gap-2">
            <Breadcrumbs>
              <Breadcrumbs.Item onClick={handleGoBack}>BILLS</Breadcrumbs.Item>
              <Breadcrumbs.Divider />
              <Breadcrumbs.Item>
                {getFieldValue(
                  billJson?.bill_number || billJson?.invoice_number,
                  "Document",
                )}
              </Breadcrumbs.Item>
              <Breadcrumbs.Divider />
              <Breadcrumbs.Item active={true}>Review Items</Breadcrumbs.Item>
            </Breadcrumbs>
          </div>
          <div className="flex items-center gap-3">
            {/* Auto-save status indicator */}
            <div className="flex items-center gap-2 text-sm">
              {isAutoSaving && (
                <div className="flex items-center gap-1 text-blue-600">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                  <span>Saving...</span>
                </div>
              )}
              {!isAutoSaving && lastSaved && !saveError && (
                <div className="flex items-center gap-1 text-green-600">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span>Saved {lastSaved.toLocaleTimeString()}</span>
                </div>
              )}
              {saveError && (
                <div className="flex items-center gap-2 text-red-600">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  <span>Save failed</span>
                  <button
                    onClick={() => billJson && debouncedSave(billJson, true)}
                    className="text-xs underline hover:no-underline"
                    disabled={isAutoSaving}
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
            <Button variant="neutral-secondary" onClick={handleGoBack}>
              Cancel
            </Button>
            {!isPostedToZoho && (
              <Button
                variant="brand-primary"
                onClick={saveAndGoBack}
                disabled={
                  isDataLoading ||
                  !canSaveAndGoBack ||
                  isCheckingReview ||
                  isReviewPending ||
                  isRecomputing
                }
              >
                {isRecomputing ? "Re-processing…" : "Save & Go Back"}
              </Button>
            )}
          </div>
        </div>
        <div className="flex w-full flex-1 flex-col items-start min-h-0">
          {/* Display validation errors with ErrorBanner component */}
          {Object.keys(errors).length > 0 && (
            <div className="w-full px-6 pt-6 pb-4">
              <ErrorBanner
                errors={errors}
                title="Please fix the following errors:"
                autoScrollOnMount={true}
                context="bill"
              />
            </div>
          )}

          {/* Closed month banner - show when document is in a closed month */}
          {isMonthClosed === true && (
            <div className="w-full px-6 pt-4 pb-2">
              <ClosedMonthBanner entityLabel="Bill" />
            </div>
          )}

          {/* Review Status Banner - Show at the top, before AI Detection Overview */}
          {review &&
            isReviewStatusBannerVisibleForUser(
              review,
              reviewBannerCurrentUserId,
            ) &&
            (review.status !== "approved" || showApprovedBanner) && (
              <div className="w-full px-6 pt-4 pb-4">
                <ReviewStatusBanner
                  review={review}
                  moduleCode={MODULE_CODES.BILL}
                  currentUserId={reviewBannerCurrentUserId}
                  onResubmit={() => {
                    if (review.status === "rejected") {
                      setIsReviewModalOpen(true);
                    }
                  }}
                />
              </div>
            )}

          {/* AI Validation Overview - Only show if not read-only, data is available, party is matched, review is not pending, and not manual (no AI for manual) */}
          {!isDocumentReadOnly &&
            !isReviewPending &&
            !isManualSource &&
            !isRecomputing &&
            !pendingVendorRerun &&
            billJson &&
            billDocument &&
            partyMatched && (
              <AIDetectionOverviewBills
                billData={billJson}
                billDocument={billDocument}
                onValidationComplete={handleValidationComplete}
                onLoadingChange={setAiDetectionLoading}
              />
            )}

          {/* Party Verification Section - hidden while a review is pending */}
          {!partyMatched && !isDocumentReadOnly && !isReviewPending && (
            <div className="flex w-full items-start justify-between border-b border-solid border-error-200 px-6 py-6 bg-gradient-to-r from-error-50 to-error-100">
              <div className="flex items-start gap-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-error-500">
                    <span className="text-body-bold font-body-bold text-white">
                      !
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      Party Verification Required
                    </span>
                    <Badge variant="error" icon={<FeatherAlertTriangle />}>
                      Party Details Not Matched
                    </Badge>
                  </div>
                </div>
                <div className="flex flex-col items-start gap-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-1.5 w-1.5 flex-none items-start rounded-full bg-error-500" />
                    <span className="text-body font-body text-default-font">
                      Party details must be verified before AI processing
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex h-1.5 w-1.5 flex-none items-start rounded-full bg-error-500" />
                    <span className="text-body font-body text-default-font">
                      Please search existing parties or add a new one
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex min-w-0 w-full items-start gap-6 px-6 py-6 pb-0">
            <div className="flex min-w-0 grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex min-w-0 w-full items-start gap-6">
                {/* PDF Viewer - manual bills: show attachments or full-width details; tally: no viewer */}
                {isManualSource ? (
                  attachmentUrls.length > 0 ? (
                    <div className="flex w-1/2 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 p-6">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Attachments
                      </span>
                      <div className="flex w-full flex-col gap-2">
                        {attachmentUrls.map((att, index) => (
                          <div
                            key={index}
                            className="flex w-full items-center gap-3 rounded-md bg-default-background px-3 py-3 border border-solid border-neutral-200"
                          >
                            <FeatherFile className="h-5 w-5 flex-none text-neutral-600" />
                            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <span className="text-body-bold font-body-bold text-default-font truncate">
                                {att.filename}
                              </span>
                              {att.file_size != null && (
                                <span className="text-caption font-caption text-subtext-color">
                                  {(att.file_size / 1024).toFixed(2)} KB
                                </span>
                              )}
                            </div>
                            <IconButton
                              variant="neutral-tertiary"
                              size="small"
                              icon={<FeatherDownload />}
                              onClick={() =>
                                window.open(att.signed_url, "_blank")
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : /* No attachments: full-width for details, no empty left column */
                  null
                ) : isTallySource /* Tally source: no document viewer, full-width details */ ? null : documentId ? (
                  <DocumentViewer {...documentViewerProps} />
                ) : (
                  <div className="flex w-1/2 items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 p-8">
                    <span className="text-body font-body text-subtext-color">
                      Loading PDF...
                    </span>
                  </div>
                )}

                {/* Invoice Details - full width when manual with no attachments or tally (no viewer). min-w-0 allows shrinking so line items table fits viewport. */}
                <div
                  className={`flex min-w-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm ${
                    (isManualSource && attachmentUrls.length === 0) ||
                    isTallySource
                      ? "w-full"
                      : "w-1/2"
                  }`}
                >
                  <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border px-1 pt-1 pb-2.5">
                    <span className="text-heading-2 font-heading-2 text-default-font">
                      Invoice Details
                    </span>
                    {isManualSource && <Badge variant="neutral">Manual</Badge>}
                    {isTallySource && <Badge variant="neutral">Tally</Badge>}
                    {/* {isDocumentReadOnly ? (
                        <Badge variant="error">{documentStatusLabel}</Badge>
                      ) : (
                        <Badge variant="warning">Pending Review</Badge>
                      )} */}
                  </div>
                  <div className="flex w-full flex-col items-start gap-6">
                    {/* Party & Service Details */}
                    <div
                      className={`flex w-full flex-col items-start gap-3 ${
                        partyMatched || isReviewPending
                          ? "border-b border-solid border-neutral-100 pb-4"
                          : "rounded-md border border-solid border-error-300 bg-error-100 px-4 py-4 bg-opacity-10"
                      }`}
                    >
                      <div className="flex w-full items-center justify-between">
                        <span className="text-body-bold font-body-bold text-subtext-color">
                          Party &amp; Service Details
                        </span>
                        <div className="flex items-center gap-2">
                          {partyMatched && (
                            <Badge variant="success" icon={<FeatherCheck />}>
                              Matched Customer
                            </Badge>
                          )}
                          <Button
                            size="large"
                            icon={<FeatherUserPen />}
                            onClick={handleSearchPartyModalOpen}
                            disabled={
                              isDocumentReadOnly || shouldDisableEditableFields
                            }
                          >
                            {partyMatched ? "Change Party" : "Search"}
                          </Button>
                        </div>
                      </div>
                      <div className="w-full items-start gap-4 grid grid-cols-3">
                        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                          <span className="text-body font-body text-subtext-color">
                            Party Name
                          </span>
                          <ReviewPendingInlineValue
                            pendingReviewDiffActive={isReviewPending}
                            isEditing={isSearchPartyModalOpen}
                            oldText={fbacHd("vendor_name")?.oldText ?? ""}
                            newText={shortenTrackedPartyChangeNewValue(
                              fbacHd("vendor_name")?.newText ?? "",
                            )}
                          >
                            <span
                              className={`text-body-bold font-body-bold ${
                                partyMatched || isReviewPending
                                  ? "text-default-font"
                                  : "text-error-700"
                              }`}
                            >
                              {billJson?.vendor_id !== ""
                                ? billJson?.vendor_name
                                : "No Matched Party"}
                            </span>
                          </ReviewPendingInlineValue>
                          {billDocument?.vendor_match_method &&
                            billDocument.vendor_match_details
                              ?.zoho_vendor_id === billJson?.vendor_id && (
                              <SubframeCore.Tooltip.Provider>
                                <SubframeCore.Tooltip.Root>
                                  <SubframeCore.Tooltip.Trigger asChild={true}>
                                    <span className="cursor-pointer mt-0.5">
                                      <FeatherInfo className="h-3 w-3 text-subtext-color" />
                                    </span>
                                  </SubframeCore.Tooltip.Trigger>
                                  <SubframeCore.Tooltip.Portal>
                                    <SubframeCore.Tooltip.Content
                                      side="top"
                                      align="start"
                                      sideOffset={4}
                                      asChild={true}
                                    >
                                      <Tooltip>
                                        {billDocument.vendor_match_method ===
                                        "gstin_exact" ? (
                                          <span>Matched by GSTIN (exact)</span>
                                        ) : (
                                          <div className="flex flex-col gap-0.5">
                                            <span>
                                              Matched by multi-field (score:{" "}
                                              {Math.round(
                                                (billDocument
                                                  .vendor_match_details
                                                  ?.consolidated_score ?? 0) *
                                                  100,
                                              )}
                                              %)
                                            </span>
                                            {billDocument.vendor_match_details
                                              ?.field_scores && (
                                              <div className="flex flex-col gap-0.5 mt-1 border-t border-white/20 pt-1">
                                                {billDocument
                                                  .vendor_match_details
                                                  .field_scores.name !=
                                                  null && (
                                                  <span>
                                                    Name:{" "}
                                                    {Math.round(
                                                      billDocument
                                                        .vendor_match_details
                                                        .field_scores.name *
                                                        100,
                                                    )}
                                                    %
                                                  </span>
                                                )}
                                                {billDocument
                                                  .vendor_match_details
                                                  .field_scores.gstin !=
                                                  null && (
                                                  <span>
                                                    GSTIN:{" "}
                                                    {Math.round(
                                                      billDocument
                                                        .vendor_match_details
                                                        .field_scores.gstin *
                                                        100,
                                                    )}
                                                    %
                                                  </span>
                                                )}
                                                {billDocument
                                                  .vendor_match_details
                                                  .field_scores.pan != null && (
                                                  <span>
                                                    PAN:{" "}
                                                    {Math.round(
                                                      billDocument
                                                        .vendor_match_details
                                                        .field_scores.pan * 100,
                                                    )}
                                                    %
                                                  </span>
                                                )}
                                                {billDocument
                                                  .vendor_match_details
                                                  .field_scores.address !=
                                                  null && (
                                                  <span>
                                                    Address:{" "}
                                                    {Math.round(
                                                      billDocument
                                                        .vendor_match_details
                                                        .field_scores.address *
                                                        100,
                                                    )}
                                                    %
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </Tooltip>
                                    </SubframeCore.Tooltip.Content>
                                  </SubframeCore.Tooltip.Portal>
                                </SubframeCore.Tooltip.Root>
                              </SubframeCore.Tooltip.Provider>
                            )}
                        </div>
                        {/* <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                              <span className="text-body font-body text-subtext-color">
                                Nature of Services/Goods
                              </span>
                              <span
                                className={`text-body-bold font-body-bold ${
                                  partyMatched
                                    ? "text-default-font"
                                    : "text-error-700"
                                }`}
                              >
                                {vendorData?.type_of_vendor ||
                                  "Consulting Services"}
                              </span>
                            </div> */}
                        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                          <span className="text-body font-body text-subtext-color">
                            Vendor Address
                          </span>
                          <span
                            className={`text-body-bold font-body-bold ${
                              partyMatched || isReviewPending
                                ? "text-default-font"
                                : "text-error-700"
                            }`}
                          >
                            {billJson?.vendor_address || "No Vendor Address"}
                          </span>
                        </div>
                      </div>
                      {!partyMatched && !isReviewPending && (
                        <div className="flex w-full items-start gap-4 rounded-md bg-error-600 px-4 py-4">
                          <IconWithBackground
                            variant="error"
                            size="medium"
                            icon={<FeatherAlertOctagon />}
                          />
                          <div className="flex flex-col items-start gap-1 grow">
                            <span className="text-heading-3 font-heading-3 text-white">
                              Critical: Unmatched Party Details
                            </span>
                            <span className="text-caption font-caption text-neutral-100">
                              We detected{" "}
                              {billJson?.vendor_name?.trim() ||
                                "Unknown vendor"}{" "}
                              but couldn&#39;t find a match in your records.
                              Please search above or add as a new party.
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Basic Information */}
                    <BasicInfoEditableField
                      initialData={basicInfo}
                      optionsData={basicInfo.optionsData}
                      onSave={handleBasicInfoSave}
                      disabled={
                        isDocumentReadOnly || shouldDisableEditableFields
                      }
                      showExchangeRate={showExchangeRateField}
                      exchangeRateRequired={exchangeRateRequired}
                      exchangeRateLoading={exchangeRateLoading}
                      accountingBackend={accountingBackend}
                      exchangeRateError={errors.exchange_rate}
                      fieldDiffs={{
                        ...(fbacHd("bill_number")
                          ? { invoice_number: fbacHd("bill_number")! }
                          : {}),
                        ...(fbacHd("currency_symbol")
                          ? { currency_symbol: fbacHd("currency_symbol")! }
                          : {}),
                        ...(fbacHd("exchange_rate")
                          ? { exchange_rate: fbacHd("exchange_rate")! }
                          : {}),
                      }}
                      pendingReviewDiffActive={isReviewPending}
                    />

                    {/* Dates */}
                    <DatesEditableField
                      initialData={datesInfo}
                      onSave={handleDatesInfoSave}
                      disabled={
                        isDocumentReadOnly || shouldDisableEditableFields
                      }
                      fieldDiffs={{
                        ...(fbacHd("date") ? { date: fbacHd("date")! } : {}),
                        ...(fbacHd("due_date")
                          ? { due_date: fbacHd("due_date")! }
                          : {}),
                      }}
                      pendingReviewDiffActive={isReviewPending}
                    />

                    {showPreGstNotice && (
                      <GstPreRegistrationNotice
                        documentDate={
                          billJson?.date || billJson?.invoice_date || ""
                        }
                        documentLabel="bill"
                      />
                    )}

                    {/* ZOUR-65: which GST registration receives this bill.
                        Beside the tax block because it decides the GSTIN on the
                        document and whether the tax is IGST or CGST+SGST.
                        Changing it is tracked like any other edit. */}
                    {/* No border box and no padding of its own: the user asked
                        for the picker to sit in the column like the fields
                        around it rather than in a panel of its own
                        (2026-09-15). The parent column already supplies the
                        gap between blocks, so px-4/py-4 only pushed this one
                        out of line with its neighbours. */}
                    {hasMultipleLocations && (
                      <div className="flex w-full flex-col gap-4">
                        <LocationPicker
                          locations={activeLocations}
                          value={billJson?.location_id ?? null}
                          disabled={!canEdit && !canUpdateAfterPush}
                          /*
                           * MANUAL + POSTED = READ-ONLY, and the distinction from
                           * an uploaded bill is the whole point — do not
                           * "simplify" this to isPostedToZoho alone.
                           *
                           * Moving the registration has to recompute the line tax
                           * family, because IGST and CGST+SGST are decided by it.
                           * On an uploaded bill the pipeline re-run does exactly
                           * that. A manual bill has NO processing job, so
                           * BillRerunService answers 422 no_job and the fallback
                           * below persists location_id and destination_of_supply
                           * and nothing else: the lines keep the old tax and the
                           * document silently disagrees with itself (local UAT,
                           * bill 48 — TN GST5 lines sitting under a KA
                           * registration). Once it is in Zoho there is nothing
                           * safe left to do here, so the answer is Zoho's own:
                           * void it and raise it again.
                           */
                          locked={
                            isManualSource &&
                            (isPostedToZoho || !!billDoc?.zoho_billid)
                          }
                          busyLabel={
                            isRecomputing && locationRerunName
                              ? `Re-running checks for ${locationRerunName}…`
                              : null
                          }
                          onChange={(locationId) => {
                            // ZOUR-65 R17: moving the picker moves OUR side of
                            // the supply. A bill is where WE receive, so the
                            // destination is the new registration's state; left
                            // alone the JSON keeps the old one (N24) and every
                            // reader that trusts the field — the supply checks,
                            // anything downstream of this page — is looking at
                            // the location the reviewer just moved away from.
                            const picked = activeLocations.find(
                              (candidate) =>
                                candidate.zoho_location_id === locationId
                            );
                            const patch = {
                              location_id: locationId,
                              ...(picked?.state_code
                                ? { destination_of_supply: picked.state_code }
                                : {}),
                            };

                            updateBillJson(patch);
                            // The next JSON is built here rather than read back
                            // from state: setBillJson is async, so the save
                            // below would otherwise persist the PREVIOUS
                            // location.
                            void triggerLocationRerun(locationId, {
                              ...((billJson ?? {}) as BillDataEntity),
                              ...patch,
                            });
                          }}
                        />
                      </div>
                    )}

                    {/* Tax Information */}
                    <TaxInfoEditableField
                      initialData={taxInfo}
                      isGstEnabled={isGstEnabled}
                      optionsData={taxInfo.optionsData}
                      onSave={handleTaxInfoSave}
                      lineItems={lineItems}
                      currencySymbol={billJson?.currency_symbol || "₹"}
                      disabled={
                        isDocumentReadOnly || shouldDisableEditableFields
                      }
                      disableVendorGstin={true}
                      showGstinFormatWarning={true}
                      fieldDiffs={{
                        ...(fbacHd("vendor_gstin")
                          ? { vendor_gstin: fbacHd("vendor_gstin")! }
                          : {}),
                      }}
                      pendingReviewDiffActive={isReviewPending}
                      customerGstinHint={customerGstinHint}
                    />

                    {/* Document Metadata - hide for manual source */}
                    {!isManualSource && (
                      <DocumentMetadataEditableField
                        initialData={documentMetadata}
                        optionsData={documentMetadata.optionsData}
                        onSave={handleDocumentMetadataSave}
                        disabled={
                          isDocumentReadOnly || shouldDisableEditableFields
                        }
                      />
                    )}

                    {/* Amount Summary */}
                    <AmountSummaryEditableField
                      initialData={amountSummary}
                      isGstEnabled={isGstEnabled}
                      optionsData={amountSummary.optionsData}
                      onSave={handleAmountSummarySave}
                      lineItems={lineItems}
                      currencySymbol={billJson?.currency_symbol || "₹"}
                      currentDiscount={discount}
                      onDiscountChange={handleDiscountChange}
                      disabled={
                        isDocumentReadOnly || shouldDisableEditableFields
                      }
                      tcsTaxId={billJson?.tcs_tax_id ?? null}
                      tcsTaxName={billJson?.tcs_tax_name ?? null}
                      tcsTaxPercentage={billJson?.tcs_tax_percentage}
                      onTcsChange={(value) => {
                        setUserHasChangedTcs(true);
                        if (value === null) {
                          updateBillJson({
                            tcs_tax_id: "",
                            tcs_tax_name: "",
                            tcs_tax_percentage: undefined,
                            tcs_nature_of_collection: "",
                          });
                        } else {
                          updateBillJson({
                            tcs_tax_id: value.tcs_tax_id,
                            tcs_tax_name: value.tcs_tax_name,
                            tcs_tax_percentage: value.tcs_tax_percentage,
                            tcs_nature_of_collection:
                              value.tcs_nature_of_collection ?? "",
                          });
                        }
                      }}
                      tcsDisabled={hasAnyTds}
                      tcsDisabledMessage="TDS is applied. TCS cannot be selected when TDS is applied on a line item."
                      tcsRecommendation={tcsRecommendation}
                      userHasChangedTcs={userHasChangedTcs}
                      amountFieldDiffs={{
                        ...(fbacHd("sub_total")
                          ? { sub_total: fbacHd("sub_total")! }
                          : {}),
                        ...(fbacHd("tax_total")
                          ? { tax_total: fbacHd("tax_total")! }
                          : {}),
                        ...(fbacHd("total") ? { total: fbacHd("total")! } : {}),
                        ...(fbacHd("tcs_tax_name")
                          ? { tcs_tax_name: fbacHd("tcs_tax_name")! }
                          : {}),
                        ...(fbacHd("tcs_tax_percentage")
                          ? {
                              tcs_tax_percentage: fbacHd("tcs_tax_percentage")!,
                            }
                          : {}),
                      }}
                      pendingReviewDiffActive={isReviewPending}
                    />
                  </div>
                </div>
              </div>

              {/* Line Items Section */}
              <div className="flex w-full flex-col items-start gap-6">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-heading-2 font-heading-2 text-default-font">
                      Detected Line Items
                    </span>
                    <Badge variant="success" icon={<FeatherCheck />}>
                      {lineItems.length} Items Detected
                    </Badge>
                  </div>
                  <Button
                    variant="neutral-primary"
                    size="small"
                    icon={<FeatherPlus />}
                    onClick={handleAddLineItem}
                    disabled={
                      aiDetectionLoading ||
                      isDocumentReadOnly ||
                      shouldDisableEditableFields
                    }
                  >
                    Add line item
                  </Button>
                </div>

                {/* Line Items Table - equal columns, truncation + hover tooltips. Non-manual: no min-width + shrink so table fits viewport. */}
                <div className="flex w-full min-w-0 flex-col items-start rounded-md border border-solid border-neutral-border overflow-hidden">
                  {/*
                    OLD HEADER — kept for rollback (column reorder, 2026-06-26).
                    Original order: Description | AI Suggestion | HSN | Qty | Rate |
                    GST | Amount | TDS | Debit A/c | Credit A/c | Confidence | Actions.
                    Superseded by the NEW HEADER below, which matches the new design
                    (AI Suggestion + Confidence columns dropped; Debit/Credit moved up
                    right after Description; Amount moved to the end before Actions).
                    Uncomment to restore the old header layout.
                  */}
                  {/*
                  <div className="flex min-w-0 items-center gap-2 sm:gap-4 border-b border-solid border-neutral-border bg-default-background px-2 sm:px-4 py-3 w-full text-caption-bold font-caption-bold text-subtext-color">
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Description"
                      >
                        Description
                      </span>
                    </div>
                    {!isManualSource && (
                      <div
                        className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                      >
                        <span
                          className="text-body font-body text-default-font truncate block w-full"
                          title="AI Suggestion"
                        >
                          AI Suggestion
                        </span>
                      </div>
                    )}
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="HSN Code"
                      >
                        HSN Code
                      </span>
                    </div>
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Qty"
                      >
                        Qty
                      </span>
                    </div>
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Rate"
                      >
                        Rate
                      </span>
                    </div>
                    {isGstEnabled && (
                      <div
                        className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                      >
                        <span
                          className="text-body font-body text-default-font truncate block w-full"
                          title="GST"
                        >
                          GST
                        </span>
                      </div>
                    )}
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Amount"
                      >
                        Amount
                      </span>
                    </div>
                    {effectiveTdsApplicable && (
                      <div
                        className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                      >
                        <span
                          className="text-body font-body text-default-font truncate block w-full"
                          title="TDS"
                        >
                          TDS
                        </span>
                      </div>
                    )}
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Debit A/c"
                      >
                        Debit A/c
                      </span>
                    </div>
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Credit A/c"
                      >
                        Credit A/c
                      </span>
                    </div>
                    {!isManualSource && (
                      <div
                        className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                      >
                        <span
                          className="text-body font-body text-default-font truncate block w-full"
                          title="Confidence"
                        >
                          Confidence
                        </span>
                      </div>
                    )}
                    <div className="flex w-[90px] shrink-0 items-start">
                      <span className="text-body font-body text-default-font truncate">
                        Actions
                      </span>
                    </div>
                  </div>
                  */}

                  {/* NEW HEADER (2026-06-26) — Description | Debit A/c | Credit A/c | HSN | Qty | Rate | GST | TDS | Amount | Actions
                      Width tuning (2026-06-26): Description/Debit/Credit/HSN flex
                      to fill; Qty/Rate/GST/TDS/Amount are fixed-width (sized to
                      content; Rate/Amount fit ₹10000000.00). Keep header + row
                      widths in lock-step or columns misalign. */}
                  <div className="flex min-w-0 items-center gap-2 sm:gap-4 border-b border-solid border-neutral-border bg-default-background px-2 sm:px-4 py-3 w-full text-caption-bold font-caption-bold text-subtext-color">
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Description"
                      >
                        Description
                      </span>
                    </div>
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Debit A/c"
                      >
                        Debit A/c
                      </span>
                    </div>
                    <div
                      className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                    >
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Credit A/c"
                      >
                        Credit A/c
                      </span>
                    </div>
                    <div className="flex w-24 shrink-0 items-start overflow-hidden">
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="HSN Code"
                      >
                        HSN Code
                      </span>
                    </div>
                    <div className="flex w-14 shrink-0 items-start overflow-hidden">
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Qty"
                      >
                        Qty
                      </span>
                    </div>
                    <div className="flex w-28 shrink-0 items-start overflow-hidden">
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Rate"
                      >
                        Rate
                      </span>
                    </div>
                    {isGstEnabled && (
                      <div className="flex w-20 shrink-0 items-start overflow-hidden">
                        <span
                          className="text-body font-body text-default-font truncate block w-full"
                          title="GST"
                        >
                          GST
                        </span>
                      </div>
                    )}
                    {isGstEnabled && (
                      <div className="flex w-36 shrink-0 items-start overflow-hidden">
                        <span
                          className="text-body font-body text-default-font truncate block w-full"
                          title="Input Tax Credit eligibility"
                        >
                          ITC
                        </span>
                      </div>
                    )}
                    {effectiveTdsApplicable && (
                      <div className="flex w-24 shrink-0 items-start overflow-hidden">
                        <span
                          className="text-body font-body text-default-font truncate block w-full"
                          title="TDS"
                        >
                          TDS
                        </span>
                      </div>
                    )}
                    <div className="flex w-28 shrink-0 items-start overflow-hidden">
                      <span
                        className="text-body font-body text-default-font truncate block w-full"
                        title="Amount"
                      >
                        Amount
                      </span>
                    </div>
                    <div className="flex w-[90px] shrink-0 items-start">
                      <span className="text-body font-body text-default-font truncate">
                        Actions
                      </span>
                    </div>
                  </div>

                  {/*
                    OLD ROWS — kept for rollback (column reorder, 2026-06-26).
                    The old row map can't be turned into a literal JSX comment
                    because it contains nested JSX comments, so it is disabled
                    with the SHOW_OLD_LINE_ITEM_LAYOUT flag instead (code left
                    fully intact below). The NEW map further down renders the
                    reordered columns. To restore the old layout: uncomment the
                    OLD HEADER above, set SHOW_OLD_LINE_ITEM_LAYOUT = true, and
                    remove the NEW HEADER + NEW map.
                  */}
                  {SHOW_OLD_LINE_ITEM_LAYOUT &&
                    lineItems.map((item, index) => {
                      const tdsLabel =
                        item.tds_tax_name && (item.tds_tax_percentage ?? 0) > 0
                          ? `${item.tds_tax_name} (${item.tds_tax_percentage}%)`
                          : "NO TDS";
                      const gstLineLabel =
                        formatDetectedLineItemGstDisplay(item);
                      const lineNo = index + 1;
                      const ld = (fieldKey: string) =>
                        fbacLineDiff(lineNo, fieldKey);
                      const lineAmountDiff =
                        ld("item_total_inclusive_of_tax") || ld("item_total");
                      const tdsDiff =
                        ld("tds_tax_name") || ld("tds_tax_percentage");
                      const accountNameDiff = ld("account_name");
                      return (
                        <div
                          key={`${item.name}-${index}`}
                          className="flex min-w-0 items-start gap-2 sm:gap-4 border-b border-solid border-neutral-100 bg-white px-2 sm:px-4 py-3 w-full"
                        >
                          <div
                            className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                          >
                            <ReviewPendingInlineValue
                              pendingReviewDiffActive={isReviewPending}
                              isEditing={isEditLineItemModalOpen}
                              oldText={ld("description")?.oldText ?? ""}
                              newText={ld("description")?.newText ?? ""}
                            >
                              <span
                                className="text-body-bold font-body-bold text-default-font truncate block w-full"
                                title={item.description || "—"}
                              >
                                {item.description}
                              </span>
                            </ReviewPendingInlineValue>
                          </div>
                          {!isManualSource && (
                            <div
                              className={`flex min-w-0 flex-1 items-start overflow-hidden gap-1 ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                            >
                              {isReviewPending ? (
                                <ReviewPendingInlineValue
                                  pendingReviewDiffActive={isReviewPending}
                                  isEditing={isEditLineItemModalOpen}
                                  oldText={getAccountNameLeaf(
                                    accountNameDiff?.oldText ?? "",
                                  )}
                                  newText={getAccountNameLeaf(
                                    accountNameDiff?.newText ?? "",
                                  )}
                                  layout="stacked"
                                  oldValueClassName="line-through opacity-70 text-caption font-caption text-default-font truncate whitespace-nowrap block w-full"
                                  newValueClassName="text-caption font-caption text-default-font truncate whitespace-nowrap block w-full"
                                >
                                  <span
                                    className="text-caption font-caption text-default-font truncate whitespace-nowrap block w-full"
                                    title={
                                      getAccountNameLeaf(item.account_name) ||
                                      "—"
                                    }
                                  >
                                    {getAccountNameLeaf(item.account_name)}
                                  </span>
                                </ReviewPendingInlineValue>
                              ) : (
                                <>
                                  <div className="inline-flex min-w-0 max-w-full items-center rounded-md border border-solid border-success-100 bg-success-100 px-2 py-1 min-h-6 overflow-hidden">
                                    <ReviewPendingInlineValue
                                      pendingReviewDiffActive={isReviewPending}
                                      isEditing={isEditLineItemModalOpen}
                                      oldText={accountNameDiff?.oldText ?? ""}
                                      newText={accountNameDiff?.newText ?? ""}
                                      oldValueClassName="line-through opacity-70 text-caption font-caption text-success-800 truncate block max-w-full"
                                      newValueClassName="text-caption font-caption text-success-800 truncate block max-w-full"
                                    >
                                      <span
                                        className="text-caption font-caption text-success-800 truncate block max-w-full"
                                        title={
                                          getAccountNameLeaf(
                                            item.account_name,
                                          ) || "—"
                                        }
                                      >
                                        {getAccountNameLeaf(item.account_name)}
                                      </span>
                                    </ReviewPendingInlineValue>
                                  </div>
                                  {(() => {
                                    // Match by array index - backend returns decisions ordered by line_id
                                    // which should match the frontend line items order
                                    const proposedDecision =
                                      proposedDecisions[index];

                                    // Check if line item was manually edited
                                    // If gl_confidence is 0, it means it was manually edited
                                    // Or if account_name doesn't match the proposed decision's account_name
                                    const isManuallyEdited =
                                      item.gl_confidence === 0 ||
                                      (proposedDecision &&
                                        getAccountNameLeaf(
                                          item.account_name,
                                        ) !==
                                          getAccountNameLeaf(
                                            proposedDecision.account_name,
                                          ));

                                    // Only show tooltip if:
                                    // 1. There's a proposed decision for this line item
                                    // 2. The line item hasn't been manually edited
                                    // 3. There's a reason to show
                                    if (
                                      proposedDecision &&
                                      !isManuallyEdited &&
                                      proposedDecision.reason
                                    ) {
                                      return (
                                        <SubframeCore.Tooltip.Provider>
                                          <SubframeCore.Tooltip.Root>
                                            <SubframeCore.Tooltip.Trigger
                                              asChild={true}
                                            >
                                              <IconButton
                                                size="small"
                                                icon={<FeatherInfo />}
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              />
                                            </SubframeCore.Tooltip.Trigger>
                                            <SubframeCore.Tooltip.Portal>
                                              <SubframeCore.Tooltip.Content
                                                side="top"
                                                align="center"
                                                sideOffset={4}
                                                asChild={true}
                                              >
                                                <Tooltip>
                                                  {proposedDecision.reason}
                                                </Tooltip>
                                              </SubframeCore.Tooltip.Content>
                                            </SubframeCore.Tooltip.Portal>
                                          </SubframeCore.Tooltip.Root>
                                        </SubframeCore.Tooltip.Provider>
                                      );
                                    }
                                    return null;
                                  })()}
                                </>
                              )}
                            </div>
                          )}
                          <div
                            className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                          >
                            <ReviewPendingInlineValue
                              pendingReviewDiffActive={isReviewPending}
                              isEditing={isEditLineItemModalOpen}
                              oldText={ld("hsn_or_sac")?.oldText ?? ""}
                              newText={ld("hsn_or_sac")?.newText ?? ""}
                            >
                              <span
                                className="text-body font-body text-default-font truncate block w-full"
                                title={item.hsn_or_sac || "N/A"}
                              >
                                {item.hsn_or_sac || "N/A"}
                              </span>
                            </ReviewPendingInlineValue>
                          </div>
                          <div
                            className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                          >
                            <ReviewPendingInlineValue
                              pendingReviewDiffActive={isReviewPending}
                              isEditing={isEditLineItemModalOpen}
                              oldText={ld("quantity")?.oldText ?? ""}
                              newText={ld("quantity")?.newText ?? ""}
                            >
                              <span
                                className="text-body font-body text-default-font truncate block w-full"
                                title={String(item.quantity)}
                              >
                                {item.quantity}
                              </span>
                            </ReviewPendingInlineValue>
                          </div>
                          <div
                            className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                          >
                            <ReviewPendingInlineValue
                              pendingReviewDiffActive={isReviewPending}
                              isEditing={isEditLineItemModalOpen}
                              oldText={ld("rate")?.oldText ?? ""}
                              newText={ld("rate")?.newText ?? ""}
                            >
                              <span
                                className="text-body font-body text-default-font truncate block w-full"
                                title={`${billJson?.currency_symbol || "₹"}${item.rate.toFixed(2)}`}
                              >
                                {billJson?.currency_symbol || "₹"}
                                {item.rate.toFixed(2)}
                              </span>
                            </ReviewPendingInlineValue>
                          </div>
                          {isGstEnabled && (
                            <div
                              className={`flex min-w-0 flex-1 items-center gap-1 overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                            >
                              {aiDetectionLoading ? (
                                <>
                                  <FeatherLoader className="h-3.5 w-3.5 animate-spin text-subtext-color" />
                                  <span className="text-caption font-caption text-subtext-color truncate">
                                    Loading…
                                  </span>
                                </>
                              ) : (
                                <ReviewPendingInlineValue
                                  pendingReviewDiffActive={isReviewPending}
                                  isEditing={isEditLineItemModalOpen}
                                  oldText={ld("tax_name")?.oldText ?? ""}
                                  newText={ld("tax_name")?.newText ?? ""}
                                >
                                  <span
                                    className="text-body font-body text-default-font truncate block w-full"
                                    title={gstLineLabel}
                                  >
                                    {gstLineLabel}
                                  </span>
                                </ReviewPendingInlineValue>
                              )}
                            </div>
                          )}
                          <div
                            className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                          >
                            <ReviewPendingInlineValue
                              pendingReviewDiffActive={isReviewPending}
                              isEditing={isEditLineItemModalOpen}
                              oldText={lineAmountDiff?.oldText ?? ""}
                              newText={lineAmountDiff?.newText ?? ""}
                            >
                              <span
                                className="text-body-bold font-body-bold text-default-font truncate block w-full"
                                title={`${billJson?.currency_symbol || "₹"}${
                                  isGstEnabled
                                    ? (
                                        item.rate * item.quantity +
                                        (item.rate *
                                          item.quantity *
                                          (item.tax_percentage || 0)) /
                                          100
                                      ).toFixed(2)
                                    : (item.rate * item.quantity).toFixed(2)
                                }`}
                              >
                                {billJson?.currency_symbol || "₹"}
                                {isGstEnabled
                                  ? (
                                      item.rate * item.quantity +
                                      (item.rate *
                                        item.quantity *
                                        (item.tax_percentage || 0)) /
                                        100
                                    ).toFixed(2)
                                  : (item.rate * item.quantity).toFixed(2)}
                              </span>
                            </ReviewPendingInlineValue>
                          </div>
                          {effectiveTdsApplicable &&
                            (() => {
                              // While bill validation is running, show a loader so the
                              // accountant knows TDS may still be auto-populated.
                              if (aiDetectionLoading) {
                                return (
                                  <div
                                    className={`flex min-w-0 flex-1 items-center gap-1 overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                                  >
                                    <FeatherLoader className="h-3.5 w-3.5 animate-spin text-subtext-color" />
                                    <span className="text-caption font-caption text-subtext-color truncate">
                                      Loading…
                                    </span>
                                  </div>
                                );
                              }

                              const lineTds =
                                tdsClassification?.details?.lines?.find(
                                  (l: any) => l.index === index,
                                );
                              const reasoning = lineTds?.reasoning;
                              const tooltipText = [
                                lineTds?.tds_tax_percentage != null &&
                                  `TDS: ${lineTds.tds_tax_percentage}%`,
                                lineTds?.section &&
                                  `Section: ${lineTds.section}${lineTds.sub_section ? ` (${lineTds.sub_section})` : ""}`,
                                reasoning,
                                lineTds?.confidence &&
                                  `Confidence: ${Math.round(lineTds.confidence * 100)}%`,
                                lineTds?.source_layer &&
                                  `Source: ${lineTds.source_layer}`,
                              ]
                                .filter(Boolean)
                                .join("\n");

                              // Same flow as AI Suggestion (Debit A/c): show info icon only when
                              // the line's TDS matches the AI suggestion; hide when user has changed it.
                              const suggestedTdsId = lineTds
                                ? lineTds.applicable
                                  ? String(lineTds.tds_tax_id ?? "")
                                  : ""
                                : undefined;
                              const currentTdsId = String(
                                item.tds_tax_id ?? "",
                              );
                              const isManuallyEditedTds =
                                lineTds &&
                                suggestedTdsId !== undefined &&
                                currentTdsId !== suggestedTdsId;

                              return (
                                <div
                                  className={`flex min-w-0 flex-1 items-center gap-1 overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                                >
                                  <ReviewPendingInlineValue
                                    pendingReviewDiffActive={isReviewPending}
                                    isEditing={isEditLineItemModalOpen}
                                    oldText={tdsDiff?.oldText ?? ""}
                                    newText={tdsDiff?.newText ?? ""}
                                  >
                                    <span
                                      className="text-caption font-caption text-default-font truncate block w-full"
                                      title={tdsLabel}
                                    >
                                      {tdsLabel}
                                    </span>
                                  </ReviewPendingInlineValue>

                                  {/* ⓘ reasoning tooltip — only when not manually edited (same as AI Suggestion column) */}
                                  {tooltipText && !isManuallyEditedTds && (
                                    <SubframeCore.Tooltip.Provider>
                                      <SubframeCore.Tooltip.Root>
                                        <SubframeCore.Tooltip.Trigger
                                          asChild={true}
                                        >
                                          <span className="shrink-0 cursor-default text-subtext-color">
                                            <FeatherInfo className="h-3 w-3" />
                                          </span>
                                        </SubframeCore.Tooltip.Trigger>
                                        <SubframeCore.Tooltip.Portal>
                                          <SubframeCore.Tooltip.Content
                                            side="top"
                                            align="start"
                                            sideOffset={4}
                                            asChild={true}
                                          >
                                            <Tooltip className="max-w-[200px] whitespace-pre-wrap">
                                              {tooltipText}
                                            </Tooltip>
                                          </SubframeCore.Tooltip.Content>
                                        </SubframeCore.Tooltip.Portal>
                                      </SubframeCore.Tooltip.Root>
                                    </SubframeCore.Tooltip.Provider>
                                  )}

                                  {/* ⚠ low-confidence badge — hide when user has overridden TDS */}
                                  {!isManuallyEditedTds &&
                                    lineTds?.needs_manual_review && (
                                      <span
                                        title="AI confidence low — please verify TDS"
                                        className="shrink-0 text-xs text-warning-500"
                                      >
                                        ⚠
                                      </span>
                                    )}
                                </div>
                              );
                            })()}
                          <div
                            className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                          >
                            <ReviewPendingInlineValue
                              pendingReviewDiffActive={isReviewPending}
                              isEditing={isEditLineItemModalOpen}
                              oldText={getAccountNameLeaf(
                                accountNameDiff?.oldText ?? "",
                              )}
                              newText={getAccountNameLeaf(
                                accountNameDiff?.newText ?? "",
                              )}
                              layout="stacked"
                              oldValueClassName="line-through opacity-70 text-caption font-caption text-default-font truncate whitespace-nowrap block w-full"
                              newValueClassName="text-caption font-caption text-default-font truncate whitespace-nowrap block w-full"
                            >
                              <span
                                className="text-caption font-caption text-default-font truncate whitespace-nowrap block w-full"
                                title={
                                  getAccountNameLeaf(item.account_name) || "—"
                                }
                              >
                                {getAccountNameLeaf(item.account_name)}
                              </span>
                            </ReviewPendingInlineValue>
                          </div>
                          <div
                            className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                          >
                            <span
                              className="text-caption font-caption text-default-font truncate block w-full"
                              title="Account Payable"
                            >
                              Account Payable
                            </span>
                          </div>
                          {!isManualSource && (
                            <div
                              className={`flex min-w-0 flex-1 items-center overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                            >
                              {item.gl_confidence ? (
                                <Badge
                                  variant="success"
                                  title={`${Math.round(item.gl_confidence * 100)}%`}
                                >
                                  {Math.round(item.gl_confidence * 100)}%
                                </Badge>
                              ) : null}
                            </div>
                          )}
                          <div className="flex w-[90px] shrink-0 items-center gap-1">
                            <IconButton
                              size="small"
                              icon={<FeatherX />}
                              onClick={(
                                event: React.MouseEvent<HTMLButtonElement>,
                              ) => {
                                event.stopPropagation();
                                handleDeleteLineItem(index);
                              }}
                              disabled={
                                isDataLoading ||
                                aiDetectionLoading ||
                                isDocumentReadOnly ||
                                shouldDisableEditableFields
                              }
                            />
                            <IconButton
                              size="small"
                              icon={<FeatherEdit2 />}
                              onClick={(
                                event: React.MouseEvent<HTMLButtonElement>,
                              ) => {
                                event.stopPropagation();
                                handleEditLineItem(index);
                              }}
                              disabled={
                                isDataLoading ||
                                aiDetectionLoading ||
                                !hasEditPermission ||
                                isDocumentReadOnly ||
                                shouldDisableEditableFields
                              }
                            />
                          </div>
                        </div>
                      );
                    })}

                  {/* NEW ROWS (2026-06-26) — reordered columns to match the new
                      design; AI Suggestion + Confidence columns dropped.
                      Order: Description | Debit A/c | Credit A/c | HSN | Qty |
                      Rate | GST | TDS | Amount | Actions. */}
                  {lineItems.map((item, index) => {
                    const tdsLabel =
                      item.tds_tax_name && (item.tds_tax_percentage ?? 0) > 0
                        ? `${item.tds_tax_name} (${item.tds_tax_percentage}%)`
                        : "NO TDS";
                    const gstLineLabel = formatDetectedLineItemGstDisplay(item);
                    const lineNo = index + 1;
                    const ld = (fieldKey: string) =>
                      fbacLineDiff(lineNo, fieldKey);
                    const lineAmountDiff =
                      ld("item_total_inclusive_of_tax") || ld("item_total");
                    const tdsDiff =
                      ld("tds_tax_name") || ld("tds_tax_percentage");
                    const accountNameDiff = ld("account_name");
                    return (
                      <div
                        key={`${item.name}-${index}`}
                        className="flex min-w-0 items-start gap-2 sm:gap-4 border-b border-solid border-neutral-100 bg-white px-2 sm:px-4 py-3 w-full"
                      >
                        {/* Description */}
                        <div
                          className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                        >
                          <ReviewPendingInlineValue
                            pendingReviewDiffActive={isReviewPending}
                            isEditing={isEditLineItemModalOpen}
                            oldText={ld("description")?.oldText ?? ""}
                            newText={ld("description")?.newText ?? ""}
                          >
                            <span
                              className="text-body-bold font-body-bold text-default-font truncate block w-full"
                              title={item.description || "—"}
                            >
                              {item.description}
                            </span>
                          </ReviewPendingInlineValue>
                        </div>
                        {/* Debit A/c (GL account leaf) */}
                        <div
                          className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                        >
                          {inlineGlPickerActive && lineNeedsGlPick(index) ? (
                            // LLM line, not yet picked: empty AI-suggestion box.
                            <InlineGlAccountPicker
                              open={openInlineGlIndex === index}
                              onOpenChange={(o) =>
                                setOpenInlineGlIndex(o ? index : null)
                              }
                              suggestions={inlineGlSuggestionsForLine(index)}
                              onSelect={(s) => handleInlineGlSelect(index, s)}
                            />
                          ) : (
                            <ReviewPendingInlineValue
                              pendingReviewDiffActive={isReviewPending}
                              isEditing={isEditLineItemModalOpen}
                              oldText={getAccountNameLeaf(
                                accountNameDiff?.oldText ?? "",
                              )}
                              newText={getAccountNameLeaf(
                                accountNameDiff?.newText ?? "",
                              )}
                              layout="stacked"
                              oldValueClassName="line-through opacity-70 text-caption font-caption text-default-font truncate whitespace-nowrap block w-full"
                              newValueClassName="text-caption font-caption text-default-font truncate whitespace-nowrap block w-full"
                            >
                              <div className="flex w-full flex-col">
                                <span
                                  className="text-caption font-caption text-default-font truncate whitespace-nowrap block w-full"
                                  title={
                                    getAccountNameLeaf(item.account_name) || "—"
                                  }
                                >
                                  {getAccountNameLeaf(item.account_name)}
                                </span>
                                {/* Source layer (compact) for non-LLM lines:
                                    "Source: kNN · 69%" small/grey/bold + an info
                                    icon whose hover tooltip carries the reason.
                                    The % is the top candidate's confidence (same
                                    figure the picker shows); hidden when absent. */}
                                {(() => {
                                  const meta = inlineGlBadgeMeta(index, item);
                                  if (!meta) return null;
                                  const pct =
                                    meta.confidence != null
                                      ? `${Math.round(meta.confidence * 100)}%`
                                      : null;
                                  // Info icon (hover = reason). Kept in the same
                                  // nowrap unit as the value so it never wraps to
                                  // a line by itself in the narrow A/c cell.
                                  const infoIcon = meta.reason ? (
                                    <SubframeCore.Tooltip.Provider>
                                      <SubframeCore.Tooltip.Root>
                                        <SubframeCore.Tooltip.Trigger
                                          asChild={true}
                                        >
                                          <span className="ml-1 inline-flex cursor-pointer align-middle leading-none">
                                            <FeatherInfo className="h-3 w-3 text-subtext-color" />
                                          </span>
                                        </SubframeCore.Tooltip.Trigger>
                                        <SubframeCore.Tooltip.Portal>
                                          <SubframeCore.Tooltip.Content
                                            side="top"
                                            align="start"
                                            sideOffset={4}
                                            asChild={true}
                                          >
                                            <Tooltip>{meta.reason}</Tooltip>
                                          </SubframeCore.Tooltip.Content>
                                        </SubframeCore.Tooltip.Portal>
                                      </SubframeCore.Tooltip.Root>
                                    </SubframeCore.Tooltip.Provider>
                                  ) : null;
                                  return (
                                    <span className="mt-0.5 block leading-snug text-caption font-caption-bold text-subtext-color">
                                      Source: {meta.label}
                                      {pct != null ? (
                                        <>
                                          {" "}
                                          <span className="whitespace-nowrap">
                                            · {pct}
                                            {infoIcon}
                                          </span>
                                        </>
                                      ) : (
                                        infoIcon
                                      )}
                                    </span>
                                  );
                                })()}
                              </div>
                            </ReviewPendingInlineValue>
                          )}
                        </div>
                        {/* Credit A/c */}
                        <div
                          className={`flex min-w-0 flex-1 items-start overflow-hidden ${isManualSource ? "shrink-0 min-w-[64px]" : "shrink"}`}
                        >
                          <span
                            className="text-caption font-caption text-default-font truncate block w-full"
                            title="Account Payable"
                          >
                            Account Payable
                          </span>
                        </div>
                        {/* HSN Code */}
                        <div className="flex w-24 shrink-0 items-start overflow-hidden">
                          <ReviewPendingInlineValue
                            pendingReviewDiffActive={isReviewPending}
                            isEditing={isEditLineItemModalOpen}
                            oldText={ld("hsn_or_sac")?.oldText ?? ""}
                            newText={ld("hsn_or_sac")?.newText ?? ""}
                          >
                            <span
                              className="text-body font-body text-default-font truncate block w-full"
                              title={item.hsn_or_sac || "N/A"}
                            >
                              {item.hsn_or_sac || "N/A"}
                            </span>
                          </ReviewPendingInlineValue>
                        </div>
                        {/* Qty */}
                        <div className="flex w-14 shrink-0 items-start overflow-hidden">
                          <ReviewPendingInlineValue
                            pendingReviewDiffActive={isReviewPending}
                            isEditing={isEditLineItemModalOpen}
                            oldText={ld("quantity")?.oldText ?? ""}
                            newText={ld("quantity")?.newText ?? ""}
                          >
                            <span
                              className="text-body font-body text-default-font truncate block w-full"
                              title={String(item.quantity)}
                            >
                              {item.quantity}
                            </span>
                          </ReviewPendingInlineValue>
                        </div>
                        {/* Rate */}
                        <div className="flex w-28 shrink-0 items-start overflow-hidden">
                          <ReviewPendingInlineValue
                            pendingReviewDiffActive={isReviewPending}
                            isEditing={isEditLineItemModalOpen}
                            oldText={ld("rate")?.oldText ?? ""}
                            newText={ld("rate")?.newText ?? ""}
                          >
                            <span
                              className="text-body font-body text-default-font truncate block w-full"
                              title={`${billJson?.currency_symbol || "₹"}${item.rate.toFixed(2)}`}
                            >
                              {billJson?.currency_symbol || "₹"}
                              {item.rate.toFixed(2)}
                            </span>
                          </ReviewPendingInlineValue>
                        </div>
                        {/* GST */}
                        {isGstEnabled && (
                          <div className="flex w-20 shrink-0 flex-col items-start gap-0.5 overflow-hidden">
                            {aiDetectionLoading ? (
                              <div className="flex items-center gap-1">
                                <FeatherLoader className="h-3.5 w-3.5 animate-spin text-subtext-color" />
                                <span className="text-caption font-caption text-subtext-color truncate">
                                  Loading…
                                </span>
                              </div>
                            ) : (
                              <>
                                <ReviewPendingInlineValue
                                  pendingReviewDiffActive={isReviewPending}
                                  isEditing={isEditLineItemModalOpen}
                                  oldText={ld("tax_name")?.oldText ?? ""}
                                  newText={ld("tax_name")?.newText ?? ""}
                                >
                                  <span
                                    className="text-body font-body text-default-font truncate block w-full"
                                    title={gstLineLabel}
                                  >
                                    {gstLineLabel}
                                  </span>
                                </ReviewPendingInlineValue>

                                {/* GST recommendation hint. Read-only: surfaces ONLY the learned
                                    (kNN) suggestion inline — document-sourced rates are already shown
                                    in the tax field, so they add no signal. The review flag (⚠) still
                                    shows for any source. */}
                                {(() => {
                                  const lineGst =
                                    gstClassification?.details?.lines?.find(
                                      (l: any) => l.index === index,
                                    );
                                  if (!lineGst) return null;
                                  const isKnn = lineGst.source_layer === "knn";
                                  // Nothing worth showing for plain document-sourced lines.
                                  if (!isKnn && !lineGst.needs_manual_review)
                                    return null;
                                  const tooltipText = [
                                    lineGst.recommended_gst_rate != null &&
                                      `GST: ${lineGst.recommended_gst_rate}%${lineGst.recommended_gst_class ? ` (${lineGst.recommended_gst_class})` : ""}`,
                                    isKnn &&
                                      lineGst.confidence != null &&
                                      `Confidence: ${Math.round(lineGst.confidence * 100)}%`,
                                    lineGst.reasoning,
                                  ]
                                    .filter(Boolean)
                                    .join("\n");
                                  return (
                                    <div className="flex items-center gap-1">
                                      {isKnn &&
                                        lineGst.recommended_gst_rate !=
                                          null && (
                                          <span className="shrink-0 text-[10px] leading-none font-caption text-subtext-color">
                                            kNN {lineGst.recommended_gst_rate}%
                                          </span>
                                        )}
                                      {(isKnn || lineGst.needs_manual_review) &&
                                        tooltipText && (
                                          <SubframeCore.Tooltip.Provider>
                                            <SubframeCore.Tooltip.Root>
                                              <SubframeCore.Tooltip.Trigger
                                                asChild={true}
                                              >
                                                <span className="shrink-0 cursor-default text-subtext-color">
                                                  <FeatherInfo className="h-2.5 w-2.5" />
                                                </span>
                                              </SubframeCore.Tooltip.Trigger>
                                              <SubframeCore.Tooltip.Portal>
                                                <SubframeCore.Tooltip.Content
                                                  side="top"
                                                  align="start"
                                                  sideOffset={4}
                                                  asChild={true}
                                                >
                                                  <Tooltip className="max-w-[200px] whitespace-pre-wrap">
                                                    {tooltipText}
                                                  </Tooltip>
                                                </SubframeCore.Tooltip.Content>
                                              </SubframeCore.Tooltip.Portal>
                                            </SubframeCore.Tooltip.Root>
                                          </SubframeCore.Tooltip.Provider>
                                        )}
                                      {lineGst.needs_manual_review && (
                                        <span
                                          title="GST engine suggests reviewing this rate"
                                          className="shrink-0 text-[10px] leading-none text-warning-500"
                                        >
                                          ⚠
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                        )}
                        {/* ITC eligibility (#612) — badge + inline picker + source hint */}
                        {isGstEnabled && (
                          <div className="flex w-36 shrink-0 flex-col items-start gap-0.5 overflow-hidden">
                            {(() => {
                              const lineItc = itcLineFor(index);
                              const status = effectiveItcStatus(index, item);
                              const needsReview = itcNeedsReview(index);
                              if (inlineGlPickerActive) {
                                return (
                                  <InlineItcPicker
                                    open={openInlineItcIndex === index}
                                    onOpenChange={(o) =>
                                      setOpenInlineItcIndex(o ? index : null)
                                    }
                                    current={status}
                                    recommended={
                                      lineItc?.itc_status ?? "eligible"
                                    }
                                    source={lineItc?.source_layer}
                                    needsReview={needsReview}
                                    needsPick={itcNeedsPick(index, item)}
                                    reason={lineItc?.reason ?? ""}
                                    onSelect={(s) => handleItcSelect(index, s)}
                                  />
                                );
                              }
                              return (
                                <ItcStatusBadge
                                  status={status}
                                  needsReview={needsReview}
                                />
                              );
                            })()}
                            {(() => {
                              const lineItc = itcLineFor(index);
                              if (!lineItc) return null;
                              // Once the accountant picks a status, drop the
                              // source hint entirely — the choice is theirs, so
                              // the engine's provenance is no longer relevant.
                              if (itcSelections[index] !== undefined)
                                return null;
                              // An unpicked LLM line shows the blank "Select" box
                              // (no committed value), so mirror Debit A/c and show
                              // no source line under it — the AI's pick + % live
                              // inside the picker until the reviewer confirms.
                              if (itcNeedsPick(index, item)) return null;
                              // Friendly source labels (parity with the Debit A/c
                              // "Source: kNN · 69%" hint), shared with the picker's
                              // recommendation pill via ITC_SOURCE_LABELS.
                              const srcLabel =
                                ITC_SOURCE_LABELS[lineItc.source_layer] ||
                                lineItc.source_layer ||
                                "";
                              const pct =
                                lineItc.confidence != null
                                  ? ` · ${Math.round(lineItc.confidence * 100)}%`
                                  : "";
                              if (!srcLabel) return null;
                              return (
                                <div className="flex items-center gap-1">
                                  <span className="shrink-0 leading-snug text-caption font-caption-bold text-subtext-color">
                                    Source: {srcLabel}
                                    {pct}
                                  </span>
                                  {lineItc.reason && (
                                    <SubframeCore.Tooltip.Provider>
                                      <SubframeCore.Tooltip.Root>
                                        <SubframeCore.Tooltip.Trigger
                                          asChild={true}
                                        >
                                          <span className="shrink-0 cursor-default text-subtext-color">
                                            <FeatherInfo className="h-2.5 w-2.5" />
                                          </span>
                                        </SubframeCore.Tooltip.Trigger>
                                        <SubframeCore.Tooltip.Portal>
                                          <SubframeCore.Tooltip.Content
                                            side="top"
                                            align="start"
                                            sideOffset={4}
                                            asChild={true}
                                          >
                                            <Tooltip className="max-w-[220px] whitespace-pre-wrap">
                                              {lineItc.reason}
                                            </Tooltip>
                                          </SubframeCore.Tooltip.Content>
                                        </SubframeCore.Tooltip.Portal>
                                      </SubframeCore.Tooltip.Root>
                                    </SubframeCore.Tooltip.Provider>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        {/* TDS */}
                        {effectiveTdsApplicable &&
                          (() => {
                            // While bill validation is running, show a loader so the
                            // accountant knows TDS may still be auto-populated.
                            if (aiDetectionLoading) {
                              return (
                                <div className="flex w-24 shrink-0 items-center gap-1 overflow-hidden">
                                  <FeatherLoader className="h-3.5 w-3.5 animate-spin text-subtext-color" />
                                  <span className="text-caption font-caption text-subtext-color truncate">
                                    Loading…
                                  </span>
                                </div>
                              );
                            }

                            const lineTds =
                              tdsClassification?.details?.lines?.find(
                                (l: any) => l.index === index,
                              );
                            const reasoning = lineTds?.reasoning;
                            const tooltipText = [
                              lineTds?.tds_tax_percentage != null &&
                                `TDS: ${lineTds.tds_tax_percentage}%`,
                              lineTds?.section &&
                                `Section: ${lineTds.section}${lineTds.sub_section ? ` (${lineTds.sub_section})` : ""}`,
                              reasoning,
                              lineTds?.confidence &&
                                `Confidence: ${Math.round(lineTds.confidence * 100)}%`,
                              lineTds?.source_layer &&
                                `Source: ${lineTds.source_layer}`,
                            ]
                              .filter(Boolean)
                              .join("\n");

                            // Show info icon only when the line's TDS matches the
                            // AI suggestion; hide when the user has changed it.
                            const suggestedTdsId = lineTds
                              ? lineTds.applicable
                                ? String(lineTds.tds_tax_id ?? "")
                                : ""
                              : undefined;
                            const currentTdsId = String(item.tds_tax_id ?? "");
                            const isManuallyEditedTds =
                              lineTds &&
                              suggestedTdsId !== undefined &&
                              currentTdsId !== suggestedTdsId;

                            return (
                              <div className="flex w-24 shrink-0 items-center gap-1 overflow-hidden">
                                <ReviewPendingInlineValue
                                  pendingReviewDiffActive={isReviewPending}
                                  isEditing={isEditLineItemModalOpen}
                                  oldText={tdsDiff?.oldText ?? ""}
                                  newText={tdsDiff?.newText ?? ""}
                                >
                                  <span
                                    className="text-caption font-caption text-default-font truncate block w-full"
                                    title={tdsLabel}
                                  >
                                    {tdsLabel}
                                  </span>
                                </ReviewPendingInlineValue>

                                {/* reasoning tooltip — only when not manually edited */}
                                {tooltipText && !isManuallyEditedTds && (
                                  <SubframeCore.Tooltip.Provider>
                                    <SubframeCore.Tooltip.Root>
                                      <SubframeCore.Tooltip.Trigger
                                        asChild={true}
                                      >
                                        <span className="shrink-0 cursor-default text-subtext-color">
                                          <FeatherInfo className="h-3 w-3" />
                                        </span>
                                      </SubframeCore.Tooltip.Trigger>
                                      <SubframeCore.Tooltip.Portal>
                                        <SubframeCore.Tooltip.Content
                                          side="top"
                                          align="start"
                                          sideOffset={4}
                                          asChild={true}
                                        >
                                          <Tooltip className="max-w-[200px] whitespace-pre-wrap">
                                            {tooltipText}
                                          </Tooltip>
                                        </SubframeCore.Tooltip.Content>
                                      </SubframeCore.Tooltip.Portal>
                                    </SubframeCore.Tooltip.Root>
                                  </SubframeCore.Tooltip.Provider>
                                )}

                                {/* low-confidence badge — hide when user has overridden TDS */}
                                {!isManuallyEditedTds &&
                                  lineTds?.needs_manual_review && (
                                    <span
                                      title="AI confidence low — please verify TDS"
                                      className="shrink-0 text-xs text-warning-500"
                                    >
                                      ⚠
                                    </span>
                                  )}
                              </div>
                            );
                          })()}
                        {/* Amount */}
                        <div className="flex w-28 shrink-0 items-start overflow-hidden">
                          <ReviewPendingInlineValue
                            pendingReviewDiffActive={isReviewPending}
                            isEditing={isEditLineItemModalOpen}
                            oldText={lineAmountDiff?.oldText ?? ""}
                            newText={lineAmountDiff?.newText ?? ""}
                          >
                            <span
                              className="text-body-bold font-body-bold text-default-font truncate block w-full"
                              title={`${billJson?.currency_symbol || "₹"}${
                                isGstEnabled
                                  ? (
                                      item.rate * item.quantity +
                                      (item.rate *
                                        item.quantity *
                                        (item.tax_percentage || 0)) /
                                        100
                                    ).toFixed(2)
                                  : (item.rate * item.quantity).toFixed(2)
                              }`}
                            >
                              {billJson?.currency_symbol || "₹"}
                              {isGstEnabled
                                ? (
                                    item.rate * item.quantity +
                                    (item.rate *
                                      item.quantity *
                                      (item.tax_percentage || 0)) /
                                      100
                                  ).toFixed(2)
                                : (item.rate * item.quantity).toFixed(2)}
                            </span>
                          </ReviewPendingInlineValue>
                        </div>
                        {/* Actions */}
                        <div className="flex w-[90px] shrink-0 items-center gap-1">
                          <IconButton
                            size="small"
                            icon={<FeatherX />}
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>,
                            ) => {
                              event.stopPropagation();
                              handleDeleteLineItem(index);
                            }}
                            disabled={
                              isDataLoading ||
                              aiDetectionLoading ||
                              isDocumentReadOnly ||
                              shouldDisableEditableFields
                            }
                          />
                          <IconButton
                            size="small"
                            icon={<FeatherEdit2 />}
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>,
                            ) => {
                              event.stopPropagation();
                              handleEditLineItem(index);
                            }}
                            disabled={
                              isDataLoading ||
                              aiDetectionLoading ||
                              !hasEditPermission ||
                              isDocumentReadOnly ||
                              shouldDisableEditableFields
                            }
                          />
                        </div>
                      </div>
                    );
                  })}

                  {/* Totals */}
                  <div className="flex flex-col items-end gap-3 border-t border-solid border-neutral-200 bg-default-background px-4 py-4 min-w-full">
                    {(() => {
                      const subtotal = lineItems.reduce(
                        (sum, item) => sum + item.rate * item.quantity,
                        0,
                      );
                      const totalTDS = lineItems.reduce((sum, item) => {
                        if (item.tds_tax_percentage) {
                          const itemAmount = item.rate * item.quantity;
                          return (
                            sum + (itemAmount * item.tds_tax_percentage) / 100
                          );
                        }
                        return sum;
                      }, 0);
                      const totalTax = isGstEnabled ? taxBreakdown.totalTax : 0;
                      const tcsPercent =
                        isGstEnabled &&
                        billJson?.tcs_tax_id &&
                        billJson.tcs_tax_id !== "" &&
                        billJson?.tcs_tax_percentage != null
                          ? billJson.tcs_tax_percentage
                          : 0;
                      const tcsAmount =
                        tcsPercent > 0
                          ? (subtotal + totalTax) * (tcsPercent / 100)
                          : 0;

                      return (
                        <>
                          <div className="flex w-full max-w-[384px] items-center justify-between">
                            <span className="text-body font-body text-subtext-color">
                              Subtotal
                            </span>
                            <span className="text-body-bold font-body-bold text-default-font">
                              {billJson?.currency_symbol || "₹"}
                              {subtotal.toFixed(2)}
                            </span>
                          </div>
                          {/* Show discount if exists */}
                          {discount !== 0 && (
                            <div className="flex w-full max-w-[384px] items-center justify-between">
                              <span className="text-body font-body text-subtext-color">
                                Discount/Adjustments
                              </span>
                              <span
                                className={`text-body-bold font-body-bold ${
                                  discount < 0
                                    ? "text-success-600"
                                    : "text-error-600"
                                }`}
                              >
                                {billJson?.currency_symbol || "₹"}
                                {formatAmount(discount, "0.00")}
                              </span>
                            </div>
                          )}
                          {/* Show tax rows only if NOT reverse charge */}
                          {isGstEnabled &&
                            !showReverseChargeMessage &&
                            taxBreakdown.rows.map((row) => {
                              const rowAmount = row.amount;
                              return (
                                <div
                                  key={row.label}
                                  className="flex w-full max-w-[384px] items-center justify-between"
                                >
                                  <span className="text-body font-body text-subtext-color">
                                    {row.label}
                                  </span>
                                  <span className="text-body-bold font-body-bold text-default-font">
                                    {billJson?.currency_symbol || "₹"}
                                    {rowAmount.toFixed(2)}
                                  </span>
                                </div>
                              );
                            })}

                          {/* Show TDS if exists */}
                          {totalTDS > 0 && (
                            <div className="flex w-full max-w-[384px] items-center justify-between">
                              <span className="text-body font-body text-subtext-color">
                                TDS Deducted
                              </span>
                              <span className="text-body-bold font-body-bold text-error-600">
                                (-) {billJson?.currency_symbol || "₹"}
                                {totalTDS.toFixed(2)}
                              </span>
                            </div>
                          )}
                          {/* Show TCS if applicable */}
                          {tcsAmount > 0 && (
                            <div className="flex w-full max-w-[384px] items-center justify-between">
                              <span className="text-body font-body text-subtext-color">
                                TCS
                                {billJson?.tcs_tax_name
                                  ? ` (${billJson.tcs_tax_name})`
                                  : ""}
                              </span>
                              <span className="text-body-bold font-body-bold text-default-font">
                                {billJson?.currency_symbol || "₹"}
                                {tcsAmount.toFixed(2)}
                              </span>
                            </div>
                          )}

                          <div className="flex w-full max-w-[384px] items-center justify-between border-t border-solid border-neutral-border pt-3">
                            <span className="text-body-bold font-body-bold text-default-font">
                              Total Amount
                            </span>
                            <span className="text-heading-3 font-heading-3 text-default-font">
                              {billJson?.currency_symbol || "₹"}
                              {showReverseChargeMessage
                                ? (
                                    subtotal +
                                    discount -
                                    totalTDS +
                                    tcsAmount
                                  ).toFixed(2)
                                : (
                                    subtotal +
                                    discount +
                                    totalTax -
                                    totalTDS +
                                    tcsAmount
                                  ).toFixed(2)}
                            </span>
                          </div>
                          {/* Show tax breakdown below total for reverse charge */}
                          {showReverseChargeMessage && isGstEnabled && (
                            <>
                              {taxBreakdown.rows.map((row) => {
                                const rowAmount = row.amount;
                                return (
                                  <div
                                    key={row.label}
                                    className="flex w-full max-w-[384px] items-center justify-between"
                                  >
                                    <span className="text-body font-body text-subtext-color">
                                      {row.label}
                                    </span>
                                    <span className="text-body-bold font-body-bold text-default-font">
                                      {billJson?.currency_symbol || "₹"}
                                      {rowAmount.toFixed(2)}
                                    </span>
                                  </div>
                                );
                              })}
                            </>
                          )}
                          {showReverseChargeMessage && (
                            <div className="flex w-full max-w-[384px] items-start gap-2 rounded-md border border-solid border-info-200 bg-info-50 px-3 py-2">
                              <FeatherInfo className="h-4 w-4 text-info-700" />
                              <span className="text-body font-body text-info-700">
                                Reverse charges of{" "}
                                {formatReverseChargeAmountWithDocument(
                                  resolveReverseChargeAmount({
                                    taxTotal: isGstEnabled ? totalTax : 0,
                                    documentCurrencyCode:
                                      billJson?.currency_code,
                                    documentCurrencySymbol:
                                      billJson?.currency_symbol,
                                    exchangeRate: billJson?.exchange_rate,
                                  }),
                                )}{" "}
                                will be applied
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex w-full flex-col items-start gap-3 pt-2">
                  {/* Warning messages */}
                  <div className="flex flex-col gap-2">
                    {isDocumentReadOnly && (
                      <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-neutral-100 border border-solid border-neutral-200">
                        <FeatherAlertTriangle className="h-4 w-4 text-error-600" />
                        <span className="text-body font-body text-subtext-color">
                          This document is {documentStatusLabel?.toLowerCase()}{" "}
                          and cannot be modified
                        </span>
                      </div>
                    )}
                    {hasUnresolvedQueries && !isDocumentReadOnly && (
                      <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-warning-50 border border-solid border-warning-200">
                        <FeatherAlertTriangle className="h-4 w-4 text-warning-600" />
                        <span className="text-body font-body text-warning-700">
                          Please resolve {unresolvedCount} open{" "}
                          {unresolvedCount === 1 ? "query" : "queries"} before{" "}
                          {isPostedToZoho ? "updating" : "approving"} this
                          document
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Button
                        icon={<FeatherCheck />}
                        onClick={handleApproveClick}
                        disabled={
                          isDataLoading ||
                          isApprovalLoading ||
                          isDocumentReadOnly ||
                          hasUnresolvedQueries ||
                          !canApprove ||
                          isReviewPending ||
                          aiDetectionLoading ||
                          exchangeRateLoading
                        }
                      >
                        {isApprovalLoading
                          ? isPostedToZoho
                            ? "Updating..."
                            : "Approving..."
                          : isPostedToZoho
                            ? "Update Bill"
                            : "Approve"}
                      </Button>
                      {!canApprove && !isDataLoading && (
                        <Tooltip content="You do not have permission to perform this action">
                          <FeatherLock className="h-4 w-4 text-neutral-400" />
                        </Tooltip>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="brand-secondary"
                        icon={<FeatherMessageCircle />}
                        onClick={handleQueryRaiseModalOpen}
                        disabled={
                          isDataLoading ||
                          isApprovalLoading ||
                          isDocumentReadOnly ||
                          (!canEdit && !canPushToZoho) ||
                          isReviewPending
                        }
                      >
                        Raise a Query
                      </Button>
                      {!canEdit && !canPushToZoho && !isDataLoading && (
                        <Tooltip content="You do not have permission to raise queries for this document">
                          <FeatherLock className="h-4 w-4 text-neutral-400" />
                        </Tooltip>
                      )}
                    </div>
                    {!isPostedToZoho && canReject && (
                      <Button
                        variant="destructive-secondary"
                        icon={<FeatherX />}
                        onClick={handleRejectBill}
                        disabled={
                          isDataLoading ||
                          isApprovalLoading ||
                          isDocumentReadOnly ||
                          isReviewPending ||
                          aiDetectionLoading
                        }
                      >
                        Reject
                      </Button>
                    )}
                    {!isPostedToZoho && canMarkAsDuplicate && (
                      <Button
                        variant="neutral-secondary"
                        icon={<FeatherCopy />}
                        onClick={handleMarkAsDuplicate}
                        disabled={
                          isDataLoading ||
                          isApprovalLoading ||
                          isDocumentReadOnly ||
                          isReviewPending ||
                          aiDetectionLoading
                        }
                      >
                        Mark as Duplicate
                      </Button>
                    )}
                    {isPostedToZoho && canDelete && (
                      <Button
                        variant="destructive-secondary"
                        icon={<FeatherX />}
                        onClick={handleDeleteBill}
                        disabled={
                          isDataLoading ||
                          isApprovalLoading ||
                          isDocumentReadOnly ||
                          isReviewPending ||
                          aiDetectionLoading
                        }
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                  {aiDetectionLoading && (
                    <div className="flex w-full items-center gap-2 px-4 py-2 rounded-md bg-warning-50 border border-solid border-warning-200">
                      <span className="text-body font-body text-warning-700">
                        AI detection is running. You cannot perform these
                        actions until it completes.
                      </span>
                    </div>
                  )}
                </div>

                {/* Activity Log and Queries */}
                <div className="flex w-full items-start gap-6">
                  <QueriesListForDocument
                    queries={queriesEntities}
                    loading={queriesLoading}
                    loadingMore={queriesLoadingMore}
                    hasMore={queriesHasMore}
                    onLoadMore={loadMoreQueries}
                    onQueryClick={handleQueryClick}
                  />

                  {/* Activity Log */}
                  {documentId && (
                    <div className="flex flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 w-1/2">
                      <ActivityLogComponent
                        key={activityLogRefreshKey}
                        feature="bill"
                        featureId={documentId}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SearchPartyModal
        open={isSearchPartyModalOpen}
        onOpenChange={setIsSearchPartyModalOpen}
        onPartySelect={(vendor) => handlePartySelect(vendor, true)} // true = manual selection
        id={documentId!}
        initialSearchTerm={billJson?.vendor_name}
        initialSelectedVendorId={billJson?.vendor_id}
      />
      <QueryRaiseModal
        open={isQueryRaiseModalOpen}
        onOpenChange={setIsQueryRaiseModalOpen}
        sourceDoc={{
          type: "bill",
          id: parseInt(documentId || "0"),
          filename: fileName || "document.pdf",
          amount: billJson?.currency_symbol + billJson?.total,
          viewUrl: signedUrl || "",
        }}
        onQueryRaised={refetchQueries}
      />
      <QueryDetailModal
        open={isQueryDetailModalOpen}
        onOpenChange={setIsQueryDetailModalOpen}
        queryId={selectedQueryId}
        onQueryResolved={refetchQueries}
        hideViewSourceButton={true}
      />

      {/* GL Classification Modal */}
      <GLClassificationModal
        open={isGLClassificationModalOpen}
        onOpenChange={setIsGLClassificationModalOpen}
        lineItems={glClassificationLineItems}
        onApply={handleGLClassificationApply}
        onSkip={handleGLClassificationSkip}
      />

      {/* Apply GL As Default Modal - shown on Approve when one or more line
          items still have the AI-suggested GL (account name not edited).
          Backdrop / escape dismissal is treated as "no further action": the
          user must click Approve again to continue. The explicit "Not now"
          and "Save as default suggestion" buttons drive the approval flow. */}
      <ApplyGLAsDefaultModal
        open={isApplyGLAsDefaultOpen}
        onOpenChange={(open) => {
          if (isApplyingGLDefaults) return; // lock during in-flight overrides
          setIsApplyGLAsDefaultOpen(open);
          if (!open) {
            setApplyGLAsDefaultItems([]);
          }
        }}
        documentType="bill"
        principalName={billJson?.vendor_name}
        lineItems={applyGLAsDefaultItems}
        onNotNow={handleApplyGLAsDefaultSkip}
        onSaveAsDefault={handleApplyGLAsDefaultSave}
        isLoading={isApplyingGLDefaults}
      />

      {/* Edit Line Item Modal */}
      <EditLineItemForBillsComponent
        open={isEditLineItemModalOpen}
        onOpenChange={setIsEditLineItemModalOpen}
        editingLineItemData={editingLineItemData}
        editingLineItemIndex={editingLineItemIndex}
        originalAccountName={originalAccountName}
        originalAccountId={originalAccountId}
        taxes={taxes}
        interStateTaxes={interStateTaxes}
        vendorName={billJson?.vendor_name}
        vendorId={billJson?.vendor_id || ""}
        vendorGstTreatment={resolvedGstTreatment}
        vendorGstCategory={resolvedGstCategory}
        currencyCode={billJson?.currency_code}
        exchangeRate={billJson?.exchange_rate}
        isNewItem={editingLineItemIndex === -1}
        isGstEnabled={isGstEnabled}
        currencySymbol={billJson?.currency_symbol || "₹"}
        isTdsApplicable={effectiveTdsApplicable}
        isTcsApplied={!!(billJson?.tcs_tax_id && billJson.tcs_tax_id !== "")}
        documentDate={billJson?.date || billJson?.invoice_date || ""}
        glSuggestions={
          ((billDoc?.gl_line_suggestions as Record<string, any[]>) || {})[
            String(editingLineItemIndex ?? "")
          ] || []
        }
        onSave={handleSaveLineItem}
        onCancel={handleCancelEdit}
        onLineItemDataChange={handleLineItemDataChange}
        billId={documentId}
        onGLOverrideNeedsReview={(glOverrideData) => {
          setPendingGLOverrideReview((prev) => {
            // Check if a GL override already exists for this line item
            const existingIndex = prev.findIndex(
              (gl) => gl.lineItemIndex === glOverrideData.lineItemIndex,
            );
            if (existingIndex >= 0) {
              // Update existing GL override
              const updated = [...prev];
              updated[existingIndex] = glOverrideData;
              return updated;
            } else {
              // Add new GL override
              return [...prev, glOverrideData];
            }
          });
        }}
      />

      {/* Update GL Dialog */}
      {/* {pendingGLUpdate && (
        <UpdateGLDialog
          open={isUpdateGLDialogOpen}
          onOpenChange={setIsUpdateGLDialogOpen}
          changedLineItemIndex={pendingGLUpdate.changedLineItemIndex}
          oldAccountName={pendingGLUpdate.oldAccountName}
          newAccountName={pendingGLUpdate.newAccountName}
          newAccountId={pendingGLUpdate.newAccountId}
          allLineItems={lineItems}
          currencySymbol={billJson?.currency_symbol || "₹"}
          onApply={handleApplyGLToSelected}
          onSkip={handleSkipGLUpdate}
        />
      )} */}

      {/* Validation Progress Modal */}
      <Dialog
        open={isValidationProgressModalOpen}
        onOpenChange={setIsValidationProgressModalOpen}
      >
        <Dialog.Content className="min-w-[600px] max-w-[700px]">
          {/* Header */}
          <div className="flex w-full items-center justify-between gap-4 p-6 border-b border-solid border-neutral-border">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-warning-500">
                <span className="text-body-bold font-body-bold text-white">
                  AI
                </span>
              </div>
              <div className="flex flex-col items-start gap-1">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Validation Issues Detected
                </span>
                <span className="text-body font-body text-subtext-color">
                  Some validations failed. Do you want to continue?
                </span>
              </div>
            </div>
            <IconButton
              size="small"
              icon={<FeatherX />}
              onClick={handleCloseValidationModal}
            />
          </div>

          {/* Content */}
          <div className="flex w-full flex-col gap-6 p-6">
            {isApprovalLoading ? (
              <div className="flex flex-col items-center gap-4 py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
                <div className="flex flex-col items-center gap-2">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    Approving Document...
                  </span>
                  <span className="text-body font-body text-subtext-color text-center">
                    Please wait while we process your approval. This may take a
                    few moments.
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-warning-100">
                    <FeatherAlertTriangle className="h-4 w-4 text-warning-600" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Validation Warnings
                    </span>
                    <span className="text-body font-body text-subtext-color">
                      The following validations failed:
                    </span>
                  </div>
                </div>

                {validationData && (
                  <div className="flex flex-col gap-3 rounded-md border border-solid border-neutral-200 bg-neutral-50 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-body font-body text-default-font">
                        Current Progress:
                      </span>
                      <span className="text-body-bold font-body-bold text-warning-700">
                        {validationData.validation_summary
                          ?.passed_validations || 0}
                        /
                        {validationData.validation_summary?.total_validations ||
                          8}{" "}
                        Checks Passed
                      </span>
                    </div>

                    {failedValidationMessages.length > 0 && (
                      <div className="flex flex-col gap-2 mt-3">
                        <span className="text-body-bold font-body-bold text-error-700">
                          Failed Validations:
                        </span>
                        {failedValidationMessages.map((message, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <div className="flex h-1.5 w-1.5 flex-none items-start rounded-full bg-error-500" />
                            <span className="text-caption font-caption text-error-700">
                              {message}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex w-full items-center justify-end gap-3">
                  <Button
                    variant="neutral-secondary"
                    onClick={handleCloseValidationModal}
                    disabled={isApprovalLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="brand-secondary"
                    onClick={handleContinueApproval}
                    disabled={isApprovalLoading}
                  >
                    {isApprovalLoading ? "Approving..." : "Continue Anyway"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog>

      {/* Vendor-change re-run confirm + in-modal progress (replaces window.confirm) */}
      <VendorRerunConfirmModal
        open={!!pendingVendorRerun}
        loading={isRecomputing}
        error={rerunError}
        oldVendorName={pendingVendorRerun?.oldVendorName ?? null}
        newVendorName={pendingVendorRerun?.vendorName ?? null}
        onOpenChange={(open) => {
          // Block dismissal while the re-run is in flight; on success the page
          // reloads, which tears the modal down on its own.
          if (!open && !isRecomputing) {
            setPendingVendorRerun(null);
            setRerunError(null);
          }
        }}
        onConfirm={() => {
          if (!pendingVendorRerun || isRecomputing) return;
          // Keep the modal open — it switches to the in-modal progress view and
          // closes via the page reload when the re-run finishes.
          void triggerVendorRerun(
            pendingVendorRerun.vendorId,
            pendingVendorRerun.vendorName,
          );
        }}
      />

      {/* Closed month confirm modal - when non-Admin/CFO tries to save or approve in closed month */}
      <ClosedMonthConfirmModal
        open={showClosedMonthConfirmModal}
        onOpenChange={(open) => {
          setShowClosedMonthConfirmModal(open);
          if (!open) setPendingClosedMonthSource(null);
        }}
        entityLabel="Bill"
        onConfirm={() => {
          setShowClosedMonthConfirmModal(false);
          if (pendingClosedMonthSource === "approve") {
            setPendingReviewAction({
              action: () => {},
              actionCode: isPostedToZoho
                ? ACTION_CODES.UPDATE_AFTER_PUSH
                : ACTION_CODES.PUSH_TO_ZOHO,
              changeDetails: {
                closed_month: true,
                action_types: [
                  isPostedToZoho ? "update_after_push" : "push_to_zoho",
                  ACTION_TYPE_MONTH_CLOSE,
                ],
                field_changes: trackedChanges,
                raw_json:
                  withVendorGstInReviewRawJson(
                    billJson ?? undefined,
                    fetchedVendor,
                  ) ?? undefined,
                original_json: originalBillJson ?? undefined,
                validation_snapshot: buildValidationSnapshotForReview(
                  validationBaselineLineItemsRef.current,
                  billJsonRef.current || billJson,
                ),
              },
              disableSubmit: false,
            });
          } else {
            setPendingReviewAction({
              action: () => {},
              actionCode: isPostedToZoho
                ? ACTION_CODES.UPDATE_AFTER_PUSH
                : ACTION_CODES.UPDATE,
              changeDetails: {
                closed_month: true,
                action_types: [
                  isPostedToZoho ? "update_after_push" : "edit",
                  ACTION_TYPE_MONTH_CLOSE,
                ],
                field_changes: trackedChanges,
                raw_json:
                  withVendorGstInReviewRawJson(
                    billJson ?? undefined,
                    fetchedVendor,
                  ) ?? undefined,
                original_json: originalBillJson ?? undefined,
                validation_snapshot: buildValidationSnapshotForReview(
                  validationBaselineLineItemsRef.current,
                  billJsonRef.current || billJson,
                ),
              },
              disableSubmit: false,
            });
          }
          setPendingClosedMonthSource(null);
          setIsReviewModalOpen(true);
        }}
      />

      {/* Review Changes Modal */}
      {documentId && (
        <InvoiceBillReviewChangesModal
          open={isReviewModalOpen}
          onOpenChange={setIsReviewModalOpen}
          moduleCode={MODULE_CODES.BILL}
          actionCode={
            pendingReviewAction?.actionCode ??
            (pendingGLOverrideReview.length > 0 &&
            needsReviewForOverrideGLPermanent
              ? ACTION_CODES.OVERRIDE_GL_PERMANENT
              : isPostedToZoho
                ? ACTION_CODES.UPDATE_AFTER_PUSH
                : needsReviewForPushToZoho
                  ? ACTION_CODES.PUSH_TO_ZOHO
                  : ACTION_CODES.UPDATE)
          }
          entityType="bill"
          entityId={parseInt(documentId)}
          documentNumber={billJson?.bill_number || billJson?.invoice_number}
          changeDetails={pendingReviewAction?.changeDetails}
          disableSubmit={pendingReviewAction?.disableSubmit}
          requiresReview={pendingReviewAction?.requiresReview ?? true}
          skipNavigation={true}
          onSuccess={async (comments) => {
            if (pendingReviewAction?.requiresReview === false) {
              const changeDetails = pendingReviewAction.changeDetails;
              setPendingReviewAction(null);
              setIsReviewModalOpen(false);
              if (!changeDetails || !comments?.trim()) {
                return;
              }
              await handleDocumentApproval({
                skipApplyGLPrompt: true,
                skipReviewChangesModal: true,
                approvalAudit: {
                  reason: comments.trim(),
                  change_details: changeDetails,
                },
              });
              return;
            }

            // Create review: stay on page (review requested); do not go back
            if (
              pendingReviewAction?.changeDetails?.action_types?.includes(
                "create",
              )
            ) {
              setPendingReviewAction(null);
              setIsReviewModalOpen(false);
              handleGoBack();
              return;
            }
            // Success toast shown by modal; avoid duplicate
            setTrackedChanges([]);
            setPendingGLOverrideReview([]);
            handleGoBack();
          }}
          onError={(error) => {
            console.error("Failed to create review request:", error);
            setApiErrors(error);
          }}
        />
      )}
    </DefaultPageLayout>
  );
};

export default BillsReviewPage;
